import {
  AbstractQuery,
  BTreeRead,
  Chunk,
  ChunkNotFoundError,
  ClientStateNotFoundError,
  DD31,
  DEFAULT_HEAD_NAME,
  DiffsMap,
  ENTITIES_KEY_PREFIX,
  ExpressionBuilder,
  GOT_QUERIES_KEY_PREFIX,
  Latest,
  V6,
  V7,
  addDiffsForIndexes,
  assertClientV6,
  assertCookie,
  assertHasClientState,
  assertHash,
  assertJSONObject,
  assertJSONValue,
  assertLocalMetaDD31,
  assertOrderingIncludesPK,
  assertRefs,
  assertSnapshotCommitDD31,
  assertSnapshotMetaDD31,
  astSchema,
  asyncIterableToArray,
  baseSnapshotFromCommit,
  baseSnapshotFromHash,
  baseSnapshotFromHead,
  binarySearch,
  clientGroupHasPendingMutations,
  clientGroupIDSchema,
  clientIDSchema,
  commitFromHash,
  commitFromHead,
  commitIsLocalDD31,
  compareCookies,
  compareCookiesForSnapshots,
  createChunk,
  createPredicate,
  decodeIndexKey,
  deepEqual,
  deepFreeze,
  deepFreezeAllowUndefined,
  defaultFormat,
  desiredQueriesPrefixForClient,
  diff,
  diff2,
  diffCommits,
  disableClientGroup,
  emptyHash,
  encodeIndexScanKey,
  filterPush,
  getClientGroup,
  getClientGroupForClient,
  getClientGroupIDForClient,
  getClientGroups,
  getClients,
  getNonCryptoRandomValues,
  getSizeOfValue,
  h64,
  hasClientState,
  hashOfAST,
  initClientV6,
  inspectDownMessageSchema,
  isLocalMetaDD31,
  isProd,
  joinIterables,
  jsonObjectSchema,
  jsonSchema,
  localMutations,
  localMutationsDD31,
  localMutationsGreaterThan,
  makeClientID,
  mapAST,
  mapCondition,
  mustGetChunk,
  mustGetClient,
  mustGetHeadHash,
  nanoid,
  newQuery,
  newRandomHash,
  newWriteLocal,
  newWriteSnapshotDD31,
  normalizeAST,
  parse,
  primaryKeySchema,
  primaryKeyValueRecordSchema,
  readFromDefaultHead,
  readFromHash,
  readonlyArray,
  readonlyObject,
  rowSchema,
  setClient,
  setClientGroup,
  setClientGroups,
  setClients,
  snapshotMetaParts,
  sourceNameFromKey,
  staticParam,
  stringCompare,
  test,
  toDesiredQueriesKey,
  toGotQueriesKey,
  toPrimaryKeyString,
  toStaticParam,
  transformFilters,
  using,
  valita_exports,
  withRead,
  withWrite,
  withWriteNoImplicitCommit,
  wrapIterable
} from "./chunk-S6IF5D2U.js";
import {
  compareTTL,
  hasOwn,
  parseTTL
} from "./chunk-MB3DEFRC.js";
import {
  assert,
  assertArray,
  assertNotNull,
  assertNumber,
  assertObject,
  assertString,
  compareValues,
  makeComparator,
  must,
  unreachable,
  valuesEqual
} from "./chunk-2NCGIK6G.js";

// ../replicache/src/call-default-fetch.ts
async function callDefaultFetch(url, auth, requestID, requestBody) {
  const init = {
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "Content-type": "application/json",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "Authorization": auth,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "X-Replicache-RequestID": requestID
    },
    body: JSON.stringify(requestBody),
    method: "POST"
  };
  const request = new Request(url, init);
  const response = await fetch(request);
  const httpStatusCode = response.status;
  if (httpStatusCode !== 200) {
    return [
      void 0,
      {
        httpStatusCode,
        errorMessage: await response.text()
      }
    ];
  }
  return [
    response,
    {
      httpStatusCode,
      errorMessage: ""
    }
  ];
}

// ../replicache/src/error-responses.ts
function isError(obj, type) {
  return typeof obj === "object" && obj !== null && obj.error === type;
}
function isErrorResponse(obj) {
  return typeof obj.error === "string";
}
function isClientStateNotFoundResponse(v) {
  return isError(v, "ClientStateNotFound");
}
function isVersionNotSupportedResponse(v) {
  if (!isError(v, "VersionNotSupported")) {
    return false;
  }
  const { versionType } = v;
  switch (versionType) {
    case void 0:
    case "pull":
    case "push":
    case "schema":
      return true;
  }
  return false;
}
function assertVersionNotSupportedResponse(v) {
  assert(isVersionNotSupportedResponse(v));
}

// ../replicache/src/http-request-info.ts
function assertHTTPRequestInfo(v) {
  assertObject(v);
  assertNumber(v.httpStatusCode);
  assertString(v.errorMessage);
}

// ../replicache/src/patch-operation.ts
function assertPatchOperations(p) {
  assertArray(p);
  for (const item of p) {
    assertPatchOperation(item);
  }
}
function assertPatchOperation(p) {
  assertObject(p);
  switch (p.op) {
    case "put":
      assertString(p.key);
      assertJSONValue(p.value);
      break;
    case "update":
      assertString(p.key);
      if (p.merge !== void 0) {
        assertJSONObject(p.merge);
      }
      if (p.constrain !== void 0) {
        assertArray(p.constrain);
        for (const key of p.constrain) {
          assertString(key);
        }
      }
      break;
    case "del":
      assertString(p.key);
      break;
    case "clear":
      break;
    default:
      throw new Error(
        `unknown patch op \`${p.op}\`, expected one of \`put\`, \`del\`, \`clear\``
      );
  }
}

// ../replicache/src/get-default-puller.ts
function getDefaultPuller(rep) {
  async function puller(requestBody, requestID) {
    const [response, httpRequestInfo] = await callDefaultFetch(
      rep.pullURL,
      rep.auth,
      requestID,
      requestBody
    );
    if (!response) {
      return { httpRequestInfo };
    }
    return {
      response: await response.json(),
      httpRequestInfo
    };
  }
  defaultPullers.add(puller);
  return puller;
}
var defaultPullers = /* @__PURE__ */ new WeakSet();
function isDefaultPuller(puller) {
  return defaultPullers.has(puller);
}
function assertPullResponseV1(v) {
  assertObject(v);
  if (isClientStateNotFoundResponse(v) || isVersionNotSupportedResponse(v)) {
    return;
  }
  const v2 = v;
  if (v2.cookie !== void 0) {
    assertCookie(v2.cookie);
  }
  assertLastMutationIDChanges(v2.lastMutationIDChanges);
  assertPatchOperations(v2.patch);
}
function assertLastMutationIDChanges(lastMutationIDChanges) {
  assertObject(lastMutationIDChanges);
  for (const [key, value] of Object.entries(lastMutationIDChanges)) {
    assertString(key);
    assertNumber(value);
  }
}
function assertPullerResultV1(v) {
  assertObject(v);
  assertHTTPRequestInfo(v.httpRequestInfo);
  if (v.response !== void 0) {
    assertPullResponseV1(v.response);
  }
}

// ../replicache/src/kv/idb-store.ts
import { resolver } from "@rocicorp/resolver";

// ../shared/src/browser-env.ts
var overrides = /* @__PURE__ */ new Map();
function getBrowserGlobal(name) {
  if (overrides.has(name)) {
    return overrides.get(name);
  }
  return globalThis[name];
}
function getBrowserGlobalMethod(name) {
  return getBrowserGlobal(name)?.bind(globalThis);
}
function mustGetBrowserGlobal(name) {
  const r = getBrowserGlobal(name);
  if (r === void 0) {
    throw new Error(
      `Unsupported JavaScript environment: Could not find ${name}.`
    );
  }
  return r;
}

// ../shared/src/resolved-promises.ts
var promiseTrue = Promise.resolve(true);
var promiseFalse = Promise.resolve(false);
var promiseUndefined = Promise.resolve(void 0);
var promiseVoid = Promise.resolve();
var promiseNever = new Promise(() => {
});

// ../replicache/src/kv/write-impl-base.ts
var deleteSentinel = Symbol();
var WriteImplBase = class {
  _pending = /* @__PURE__ */ new Map();
  #read;
  constructor(read) {
    this.#read = read;
  }
  has(key) {
    switch (this._pending.get(key)) {
      case void 0:
        return this.#read.has(key);
      case deleteSentinel:
        return promiseFalse;
      default:
        return promiseTrue;
    }
  }
  async get(key) {
    const v = this._pending.get(key);
    switch (v) {
      case deleteSentinel:
        return void 0;
      case void 0: {
        const v2 = await this.#read.get(key);
        return deepFreezeAllowUndefined(v2);
      }
      default:
        return v;
    }
  }
  put(key, value) {
    this._pending.set(key, deepFreeze(value));
    return promiseVoid;
  }
  del(key) {
    this._pending.set(key, deleteSentinel);
    return promiseVoid;
  }
  release() {
    this.#read.release();
  }
  get closed() {
    return this.#read.closed;
  }
};

// ../replicache/src/kv/idb-store.ts
var RELAXED = { durability: "relaxed" };
var OBJECT_STORE = "chunks";
var IDBStore = class {
  #db;
  #closed = false;
  #idbDeleted = false;
  constructor(name) {
    this.#db = openDatabase(name);
  }
  read() {
    return this.#withReopen(readImpl);
  }
  write() {
    return this.#withReopen(writeImpl);
  }
  async close() {
    if (!this.#idbDeleted) {
      const db = await this.#db;
      db.close();
    }
    this.#closed = true;
  }
  get closed() {
    return this.#closed;
  }
  async #withReopen(fn) {
    const reopenExistingDB = async (name) => {
      const { promise, resolve, reject } = resolver();
      const req = indexedDB.open(name);
      req.onupgradeneeded = () => {
        const tx = req.transaction;
        assertNotNull(tx);
        tx.abort();
        this.#idbDeleted = true;
        reject(new IDBNotFoundError(`Replicache IndexedDB not found: ${name}`));
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      const db2 = await promise;
      db2.onversionchange = () => db2.close();
      return db2;
    };
    const db = await this.#db;
    try {
      return fn(db);
    } catch (e) {
      if (!this.#closed && e instanceof DOMException) {
        if (e.name === "InvalidStateError") {
          this.#db = reopenExistingDB(db.name);
          const reopened = await this.#db;
          return fn(reopened);
        } else if (e.name === "NotFoundError") {
          this.#idbDeleted = true;
          mustGetBrowserGlobal("indexedDB").deleteDatabase(db.name);
          throw new IDBNotFoundError(
            `Replicache IndexedDB ${db.name} missing object store. Deleting db.`
          );
        }
      }
      throw e;
    }
  }
};
var ReadImpl = class {
  #tx;
  #closed = false;
  constructor(tx) {
    this.#tx = tx;
  }
  has(key) {
    return new Promise((resolve, reject) => {
      const req = objectStore(this.#tx).count(key);
      req.onsuccess = () => resolve(req.result > 0);
      req.onerror = () => reject(req.error);
    });
  }
  get(key) {
    return new Promise((resolve, reject) => {
      const req = objectStore(this.#tx).get(key);
      req.onsuccess = () => resolve(deepFreezeAllowUndefined(req.result));
      req.onerror = () => reject(req.error);
    });
  }
  release() {
    this.#closed = true;
  }
  get closed() {
    return this.#closed;
  }
};
var WriteImpl = class extends WriteImplBase {
  #tx;
  #closed = false;
  constructor(tx) {
    super(new ReadImpl(tx));
    this.#tx = tx;
  }
  commit() {
    if (this._pending.size === 0) {
      return promiseVoid;
    }
    return new Promise((resolve, reject) => {
      const tx = this.#tx;
      const store = objectStore(tx);
      for (const [key, val] of this._pending) {
        if (val === deleteSentinel) {
          store.delete(key);
        } else {
          store.put(val, key);
        }
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
  release() {
    this.#closed = true;
  }
  get closed() {
    return this.#closed;
  }
};
function writeImpl(db) {
  const tx = db.transaction(OBJECT_STORE, "readwrite", RELAXED);
  return new WriteImpl(tx);
}
function readImpl(db) {
  const tx = db.transaction(OBJECT_STORE, "readonly");
  return new ReadImpl(tx);
}
function objectStore(tx) {
  return tx.objectStore(OBJECT_STORE);
}
function openDatabase(name) {
  const idb = mustGetBrowserGlobal("indexedDB");
  return new Promise((resolve, reject) => {
    const req = idb.open(name);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(OBJECT_STORE);
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = () => reject(req.error);
  });
}
var IDBNotFoundError = class extends Error {
  name = "IDBNotFoundError";
};

// ../shared/src/abort-error.ts
var AbortError = class extends Error {
  name = "AbortError";
};

// ../shared/src/sleep.ts
import { resolver as resolver2 } from "@rocicorp/resolver";
var promiseVoid2 = Promise.resolve();
var promiseNever2 = new Promise(() => void 0);
function sleep(ms, signal) {
  const newAbortError = () => new AbortError("Aborted");
  if (signal?.aborted) {
    return Promise.reject(newAbortError());
  }
  if (ms === 0) {
    return promiseVoid2;
  }
  return new Promise((resolve, reject) => {
    let handleAbort;
    if (signal) {
      handleAbort = () => {
        clearTimeout(id);
        reject(newAbortError());
      };
      signal.addEventListener("abort", handleAbort, { once: true });
    }
    const id = setTimeout(() => {
      resolve();
      signal?.removeEventListener("abort", handleAbort);
    }, ms);
  });
}
function sleepWithAbort(ms, signal) {
  if (ms === 0) {
    return [promiseVoid2, promiseNever2];
  }
  const { promise: abortedPromise, resolve: abortedResolve } = resolver2();
  const sleepPromise = new Promise((resolve) => {
    const handleAbort = () => {
      clearTimeout(id);
      abortedResolve();
    };
    const id = setTimeout(() => {
      resolve();
      signal.removeEventListener("abort", handleAbort);
    }, ms);
    signal.addEventListener("abort", handleAbort, { once: true });
  });
  return [sleepPromise, abortedPromise];
}

// ../replicache/src/bg-interval.ts
function initBgIntervalProcess(processName, process2, delayMs, lc, signal) {
  void runBgIntervalProcess(processName, process2, delayMs, lc, signal);
}
async function runBgIntervalProcess(processName, process2, delayMs, lc, signal) {
  if (signal.aborted) {
    return;
  }
  lc = lc.withContext("bgIntervalProcess", processName);
  lc.debug?.("Starting");
  while (!signal.aborted) {
    try {
      await sleep(delayMs(), signal);
    } catch (e) {
      if (!(e instanceof AbortError)) {
        throw e;
      }
    }
    if (!signal.aborted) {
      lc.debug?.("Running");
      try {
        await process2();
      } catch (e) {
        if (signal.aborted) {
          lc.debug?.("Error running most likely due to close.", e);
        } else {
          lc.error?.("Error running.", e);
        }
      }
    }
  }
  lc.debug?.("Stopping");
}

// ../replicache/src/dag/gc.ts
function computeRefCountUpdates(headChanges, putChunks, delegate) {
  return new RefCountUpdates(headChanges, putChunks, delegate).compute();
}
var RefCountUpdates = class {
  #newHeads;
  #oldHeads;
  #putChunks;
  #delegate;
  #refsCounted;
  #refCountUpdates;
  #loadedRefCountPromises;
  #isLazyDelegate;
  constructor(headChanges, putChunks, delegate) {
    const newHeads = [];
    const oldHeads = [];
    for (const changedHead of headChanges) {
      if (changedHead.old !== changedHead.new) {
        changedHead.old && oldHeads.push(changedHead.old);
        changedHead.new && newHeads.push(changedHead.new);
      }
    }
    this.#newHeads = newHeads;
    this.#oldHeads = oldHeads;
    this.#putChunks = putChunks;
    this.#delegate = delegate;
    this.#refCountUpdates = /* @__PURE__ */ new Map();
    this.#loadedRefCountPromises = /* @__PURE__ */ new Map();
    this.#isLazyDelegate = delegate.areRefsCounted !== void 0;
    this.#refsCounted = this.#isLazyDelegate ? /* @__PURE__ */ new Set() : null;
  }
  async compute() {
    for (const n of this.#newHeads) {
      await this.#changeRefCount(n, 1);
    }
    await Promise.all(
      Array.from(
        this.#putChunks.values(),
        (hash) => this.#ensureRefCountLoaded(hash)
      )
    );
    if (this.#isLazyDelegate) {
      assert(this.#delegate.areRefsCounted);
      assert(this.#refsCounted);
      let refCountsUpdated;
      do {
        refCountsUpdated = false;
        for (const hash of this.#putChunks.values()) {
          if (!this.#delegate.areRefsCounted(hash) && !this.#refsCounted.has(hash) && this.#refCountUpdates.get(hash) !== 0) {
            await this.#updateRefsCounts(hash, 1);
            refCountsUpdated = true;
            break;
          }
        }
      } while (refCountsUpdated);
    }
    for (const o of this.#oldHeads) {
      await this.#changeRefCount(o, -1);
    }
    if (!isProd) {
      for (const [hash, update] of this.#refCountUpdates) {
        assert(
          update >= 0,
          `ref count update must be non-negative. ${hash}:${update}`
        );
      }
    }
    return this.#refCountUpdates;
  }
  async #changeRefCount(hash, delta) {
    await this.#ensureRefCountLoaded(hash);
    if (this.#updateRefCount(hash, delta)) {
      await this.#updateRefsCounts(hash, delta);
    }
  }
  async #updateRefsCounts(hash, delta) {
    if (hash === emptyHash) {
      return;
    }
    const refs = await this.#delegate.getRefs(hash);
    if (!isProd) {
      assert(
        refs || this.#isLazyDelegate && !this.#putChunks.has(hash),
        "refs must be defined"
      );
    }
    if (refs !== void 0) {
      this.#refsCounted?.add(hash);
      const ps = refs.map((ref) => this.#changeRefCount(ref, delta));
      await Promise.all(ps);
    }
  }
  #ensureRefCountLoaded(hash) {
    let p = this.#loadedRefCountPromises.get(hash);
    if (p === void 0) {
      p = (async () => {
        const value = await this.#delegate.getRefCount(hash) || 0;
        this.#refCountUpdates.set(hash, value);
        return value;
      })();
      this.#loadedRefCountPromises.set(hash, p);
    }
    return p;
  }
  #updateRefCount(hash, delta) {
    const oldCount = this.#refCountUpdates.get(hash);
    assertNumber(oldCount);
    this.#refCountUpdates.set(hash, oldCount + delta);
    return oldCount === 0 && delta === 1 || oldCount === 1 && delta === -1;
  }
};

// ../replicache/src/dag/key.ts
function chunkDataKey(hash) {
  return `c/${hash}/d`;
}
function chunkMetaKey(hash) {
  return `c/${hash}/m`;
}
function chunkRefCountKey(hash) {
  return `c/${hash}/r`;
}
function headKey(name) {
  return `h/${name}`;
}

// ../replicache/src/dag/store-impl.ts
var StoreImpl = class {
  #kv;
  #chunkHasher;
  #assertValidHash;
  constructor(kv, chunkHasher, assertValidHash) {
    this.#kv = kv;
    this.#chunkHasher = chunkHasher;
    this.#assertValidHash = assertValidHash;
  }
  async read() {
    return new ReadImpl2(await this.#kv.read(), this.#assertValidHash);
  }
  async write() {
    return new WriteImpl2(
      await this.#kv.write(),
      this.#chunkHasher,
      this.#assertValidHash
    );
  }
  close() {
    return this.#kv.close();
  }
};
var ReadImpl2 = class {
  _tx;
  assertValidHash;
  constructor(kv, assertValidHash) {
    this._tx = kv;
    this.assertValidHash = assertValidHash;
  }
  hasChunk(hash) {
    return this._tx.has(chunkDataKey(hash));
  }
  async getChunk(hash) {
    const data = await this._tx.get(chunkDataKey(hash));
    if (data === void 0) {
      return void 0;
    }
    const refsVal = await this._tx.get(chunkMetaKey(hash));
    let refs;
    if (refsVal !== void 0) {
      assertRefs(refsVal);
      refs = refsVal;
    } else {
      refs = [];
    }
    return new Chunk(hash, data, refs);
  }
  mustGetChunk(hash) {
    return mustGetChunk(this, hash);
  }
  async getHead(name) {
    const data = await this._tx.get(headKey(name));
    if (data === void 0) {
      return void 0;
    }
    assertHash(data);
    return data;
  }
  release() {
    this._tx.release();
  }
  get closed() {
    return this._tx.closed;
  }
};
var WriteImpl2 = class extends ReadImpl2 {
  #chunkHasher;
  #putChunks = /* @__PURE__ */ new Set();
  #changedHeads = /* @__PURE__ */ new Map();
  constructor(kvw, chunkHasher, assertValidHash) {
    super(kvw, assertValidHash);
    this.#chunkHasher = chunkHasher;
  }
  createChunk = (data, refs) => createChunk(data, refs, this.#chunkHasher);
  get kvWrite() {
    return this._tx;
  }
  async putChunk(c) {
    const { hash, data, meta } = c;
    this.assertValidHash(hash);
    const key = chunkDataKey(hash);
    const p1 = this._tx.put(key, data);
    let p2;
    if (meta.length > 0) {
      for (const h of meta) {
        this.assertValidHash(h);
      }
      p2 = this._tx.put(chunkMetaKey(hash), meta);
    }
    this.#putChunks.add(hash);
    await p1;
    await p2;
  }
  setHead(name, hash) {
    return this.#setHead(name, hash);
  }
  removeHead(name) {
    return this.#setHead(name, void 0);
  }
  async #setHead(name, hash) {
    const oldHash = await this.getHead(name);
    const hk = headKey(name);
    let p1;
    if (hash === void 0) {
      p1 = this._tx.del(hk);
    } else {
      p1 = this._tx.put(hk, hash);
    }
    const v = this.#changedHeads.get(name);
    if (v === void 0) {
      this.#changedHeads.set(name, { new: hash, old: oldHash });
    } else {
      v.new = hash;
    }
    await p1;
  }
  async commit() {
    const refCountUpdates = await computeRefCountUpdates(
      this.#changedHeads.values(),
      this.#putChunks,
      this
    );
    await this.#applyRefCountUpdates(refCountUpdates);
    await this._tx.commit();
  }
  async getRefCount(hash) {
    const value = await this._tx.get(chunkRefCountKey(hash));
    if (value === void 0) {
      return void 0;
    }
    assertNumber(value);
    if (value < 0 || value > 65535 || value !== (value | 0)) {
      throw new Error(
        `Invalid ref count ${value}. We expect the value to be a Uint16`
      );
    }
    return value;
  }
  async getRefs(hash) {
    const meta = await this._tx.get(chunkMetaKey(hash));
    if (meta === void 0) {
      return [];
    }
    assertRefs(meta);
    return meta;
  }
  async #applyRefCountUpdates(refCountCache) {
    const ps = [];
    for (const [hash, count] of refCountCache) {
      if (count === 0) {
        ps.push(this.#removeAllRelatedKeys(hash));
      } else {
        const refCountKey = chunkRefCountKey(hash);
        ps.push(this._tx.put(refCountKey, count));
      }
    }
    await Promise.all(ps);
  }
  async #removeAllRelatedKeys(hash) {
    await Promise.all([
      this._tx.del(chunkDataKey(hash)),
      this._tx.del(chunkMetaKey(hash)),
      this._tx.del(chunkRefCountKey(hash))
    ]);
    this.#putChunks.delete(hash);
  }
  release() {
    this._tx.release();
  }
};

// ../replicache/src/deleted-clients.ts
var DELETED_CLIENTS_HEAD_NAME = "deleted-clients";
var deletedClientsSchema = readonlyObject({
  clientIDs: readonlyArray(valita_exports.string()),
  clientGroupIDs: readonlyArray(valita_exports.string())
});
var legacyDeletedClientsSchema = readonlyArray(valita_exports.string());
async function setDeletedClients(dagWrite, clientIDs, clientGroupIDs) {
  const data = {
    clientIDs: normalize(clientIDs),
    clientGroupIDs: normalize(clientGroupIDs)
  };
  const chunkData = deepFreeze(data);
  const chunk = dagWrite.createChunk(chunkData, []);
  await dagWrite.putChunk(chunk);
  await dagWrite.setHead(DELETED_CLIENTS_HEAD_NAME, chunk.hash);
  return data;
}
async function getDeletedClients(dagRead) {
  const hash = await dagRead.getHead(DELETED_CLIENTS_HEAD_NAME);
  if (hash === void 0) {
    return { clientIDs: [], clientGroupIDs: [] };
  }
  const chunk = await dagRead.mustGetChunk(hash);
  const res = test(chunk.data, legacyDeletedClientsSchema);
  if (res.ok) {
    return { clientIDs: res.value, clientGroupIDs: [] };
  }
  return parse(chunk.data, deletedClientsSchema);
}
async function addDeletedClients(dagWrite, clientIDs, clientGroupIDs) {
  const { clientIDs: oldClientIDs, clientGroupIDs: oldClientGroupIDs } = await getDeletedClients(dagWrite);
  return setDeletedClients(
    dagWrite,
    [...oldClientIDs, ...clientIDs],
    [...oldClientGroupIDs, ...clientGroupIDs]
  );
}
async function removeDeletedClients(dagWrite, clientIDs, clientGroupIDs) {
  const { clientIDs: oldClientIDs, clientGroupIDs: oldClientGroupIDs } = await getDeletedClients(dagWrite);
  const newDeletedClients = oldClientIDs.filter(
    (clientID) => !clientIDs.includes(clientID)
  );
  const newDeletedClientGroups = oldClientGroupIDs.filter(
    (clientGroupID) => !clientGroupIDs.includes(clientGroupID)
  );
  return setDeletedClients(dagWrite, newDeletedClients, newDeletedClientGroups);
}
function normalize(arr) {
  return [...new Set(arr)].sort();
}

// ../replicache/src/log-options.ts
import {
  consoleLogSink,
  LogContext,
  TeeLogSink
} from "@rocicorp/logger";
function createLogContext(logLevel = "info", logSinks = [consoleLogSink], context) {
  const logSink = logSinks.length === 1 ? logSinks[0] : new TeeLogSink(logSinks);
  return new LogContext(logLevel, context, logSink);
}

// ../shared/src/navigator.ts
var localNavigator = typeof navigator !== "undefined" ? navigator : void 0;

// ../replicache/src/kv/mem-store.ts
import { RWLock } from "@rocicorp/lock";

// ../replicache/src/kv/read-impl.ts
var ReadImpl3 = class {
  #map;
  #release;
  #closed = false;
  constructor(map, release) {
    this.#map = map;
    this.#release = release;
  }
  release() {
    this.#release();
    this.#closed = true;
  }
  get closed() {
    return this.#closed;
  }
  has(key) {
    return Promise.resolve(this.#map.has(key));
  }
  get(key) {
    return Promise.resolve(this.#map.get(key));
  }
};

// ../replicache/src/kv/write-impl.ts
var WriteImpl3 = class extends WriteImplBase {
  #map;
  constructor(map, release) {
    super(new ReadImpl3(map, release));
    this.#map = map;
  }
  commit() {
    this._pending.forEach((value, key) => {
      if (value === deleteSentinel) {
        this.#map.delete(key);
      } else {
        this.#map.set(key, value);
      }
    });
    this._pending.clear();
    this.release();
    return promiseVoid;
  }
};

// ../replicache/src/kv/mem-store.ts
var stores = /* @__PURE__ */ new Map();
function dropMemStore(name) {
  stores.delete(name);
  return promiseVoid;
}
var MemStore = class {
  #map;
  #rwLock;
  #closed = false;
  constructor(name) {
    const entry = stores.get(name);
    let lock;
    let map;
    if (entry) {
      ({ lock, map } = entry);
    } else {
      lock = new RWLock();
      map = /* @__PURE__ */ new Map();
      stores.set(name, { lock, map });
    }
    this.#rwLock = lock;
    this.#map = map;
  }
  async read() {
    const release = await this.#rwLock.read();
    return new ReadImpl3(this.#map, release);
  }
  async write() {
    const release = await this.#rwLock.write();
    return new WriteImpl3(this.#map, release);
  }
  close() {
    this.#closed = true;
    return promiseVoid;
  }
  get closed() {
    return this.#closed;
  }
};

// ../replicache/src/kv/idb-store-with-mem-fallback.ts
var IDBStoreWithMemFallback = class {
  #lc;
  #name;
  #store;
  constructor(lc, name) {
    this.#lc = lc;
    this.#name = name;
    this.#store = new IDBStore(name);
  }
  read() {
    return this.#withBrainTransplant((s) => s.read());
  }
  write() {
    return this.#withBrainTransplant((s) => s.write());
  }
  async #withBrainTransplant(f) {
    try {
      return await f(this.#store);
    } catch (e) {
      if (isFirefoxPrivateBrowsingError(e)) {
        if (this.#store instanceof IDBStore) {
          this.#lc.info?.(
            "Switching to MemStore because of Firefox private browsing error"
          );
          this.#store = new MemStore(this.#name);
        }
        return f(this.#store);
      }
      throw e;
    }
  }
  close() {
    return this.#store.close();
  }
  get closed() {
    return this.#store.closed;
  }
};
function isFirefoxPrivateBrowsingError(e) {
  return isFirefox() && e instanceof DOMException && e.name === "InvalidStateError" && e.message === "A mutation operation was attempted on a database that did not allow mutations.";
}
function isFirefox() {
  return localNavigator?.userAgent.includes("Firefox") ?? false;
}
function newIDBStoreWithMemFallback(lc, name) {
  if (isFirefox()) {
    return new IDBStoreWithMemFallback(lc, name);
  }
  return new IDBStore(name);
}
function dropIDBStoreWithMemFallback(name) {
  if (!isFirefox()) {
    return dropIDBStore(name);
  }
  try {
    return dropIDBStore(name);
  } catch (e) {
    if (isFirefoxPrivateBrowsingError(e)) {
      return dropMemStore(name);
    }
  }
  return promiseVoid;
}
function dropIDBStore(name) {
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ../replicache/src/replicache-impl.ts
import { Lock } from "@rocicorp/lock";
import { consoleLogSink as consoleLogSink2 } from "@rocicorp/logger";
import { resolver as resolver6 } from "@rocicorp/resolver";

// ../shared/src/document-visible.ts
import { resolver as resolver3 } from "@rocicorp/resolver";
function getDocumentVisibilityWatcher(doc, hiddenIntervalMS, signal) {
  return doc ? new DocumentVisibilityWatcherImpl(doc, hiddenIntervalMS, signal) : new DocumentVisibilityWatcherNoDoc();
}
var DocumentVisibilityWatcherImpl = class {
  #doc;
  #hiddenIntervalMS;
  #timeoutID = 0;
  // This trails doc.visibilityState by hiddenIntervalMS when being hidden. This
  // is because we want to wait for the tab to be hidden for a while before
  // considering as hidden.
  visibilityState;
  #promises = /* @__PURE__ */ new Set();
  constructor(doc, hiddenIntervalMS, signal) {
    this.#doc = doc;
    this.#hiddenIntervalMS = hiddenIntervalMS;
    this.visibilityState = doc.visibilityState;
    this.#doc.addEventListener("visibilitychange", this.#onVisibilityChange, {
      signal
    });
  }
  #onVisibilityChange = () => {
    if (this.#doc.visibilityState === "visible") {
      clearTimeout(this.#timeoutID);
      this.#setVisibilityState("visible");
    } else {
      this.#timeoutID = setTimeout(() => {
        this.#setVisibilityState("hidden");
      }, this.#hiddenIntervalMS);
    }
  };
  #setVisibilityState(visibilityState) {
    if (visibilityState === this.visibilityState) {
      return;
    }
    this.visibilityState = visibilityState;
    for (const entry of this.#promises) {
      const { resolve, state } = entry;
      if (state === visibilityState) {
        resolve();
        this.#promises.delete(entry);
      }
    }
  }
  waitForVisible() {
    return this.#waitFor("visible");
  }
  waitForHidden() {
    return this.#waitFor("hidden");
  }
  #waitFor(state) {
    if (this.visibilityState === state) {
      return Promise.resolve();
    }
    const { promise, resolve } = resolver3();
    this.#promises.add({ resolve, state });
    return promise;
  }
};
var resolvedPromise = Promise.resolve();
var promiseThatNeverResolves = new Promise(() => void 0);
var DocumentVisibilityWatcherNoDoc = class {
  visibilityState = "visible";
  waitForVisible() {
    return resolvedPromise;
  }
  waitForHidden() {
    return promiseThatNeverResolves;
  }
};

// ../replicache/src/connection-loop-delegates.ts
var ConnectionLoopDelegateImpl = class {
  rep;
  invokeSend;
  // TODO: Remove the ability to have more than one concurrent connection and update tests.
  // Bug: https://github.com/rocicorp/replicache-internal/issues/303
  maxConnections = 1;
  constructor(rep, invokeSend) {
    this.rep = rep;
    this.invokeSend = invokeSend;
  }
  get maxDelayMs() {
    return this.rep.requestOptions.maxDelayMs;
  }
  get minDelayMs() {
    return this.rep.requestOptions.minDelayMs;
  }
};
var PullDelegate = class extends ConnectionLoopDelegateImpl {
  debounceDelay = 0;
  get watchdogTimer() {
    return this.rep.pullInterval;
  }
};
var PushDelegate = class extends ConnectionLoopDelegateImpl {
  get debounceDelay() {
    return this.rep.pushDelay;
  }
  watchdogTimer = null;
};

// ../replicache/src/connection-loop.ts
import { resolver as resolver4 } from "@rocicorp/resolver";
var MIN_DELAY_MS = 30;
var MAX_DELAY_MS = 6e4;
var ConnectionLoop = class {
  // ConnectionLoop runs a loop sending network requests (either pushes or
  // pulls) to the server. Our goal, generally, is to send requests as fast as
  // we can, but to adjust in case of slowness, network errors, etc. We will
  // send requests in parallel if the server supports it. We also debounce
  // pushes since they frequently happen in series very near to one another
  // (e.g., during drag'n drops).
  //
  // The loop flows through the following states forever, until it is closed:
  //
  // Pending: Wait for event or watchdog
  //          |
  //          v
  // Debounce: Wait for more events (we debounce pushes)
  //          |
  //          v
  // Wait for available connection (we limit number of parallel requests
  // allowed)
  //          |
  //          v
  // Wait to send (if requests are taking too long, we will slow down)
  //          |
  //          v
  // Send (asynchronously, wrt the loop)
  //          |
  //          v
  // Back to the pending!
  // Controls whether the next iteration of the loop will wait at the pending
  // state.
  #pendingResolver = resolver4();
  /**
   * This resolver is used to allow us to skip sleeps when we do send(true)
   */
  #skipSleepsResolver = resolver4();
  /**
   * Resolver for the next send. Never rejects. Returns an error instead since
   * this resolver is used in cases where they might not be someone waiting,
   * and we don't want an unhandled promise rejection in that case.
   */
  #sendResolver = resolver4();
  #delegate;
  #closed = false;
  /**
   * Number of pending send calls.
   *
   * We keep track of this because if close happens while we are waiting for the
   * send to resolve we should reject the send promise.
   */
  #sendCounter = 0;
  #lc;
  #visibilityWatcher;
  constructor(lc, delegate, visibilityWatcher) {
    this.#lc = lc;
    this.#delegate = delegate;
    this.#visibilityWatcher = visibilityWatcher;
    void this.run();
  }
  close() {
    this.#closed = true;
    if (this.#sendCounter > 0) {
      this.#sendResolver.resolve({ error: closeError() });
    }
  }
  /**
   *
   * @returns Returns undefined if ok, otherwise it return the error that caused
   * the send to fail.
   */
  async send(now) {
    if (this.#closed) {
      return { error: closeError() };
    }
    this.#sendCounter++;
    this.#lc.debug?.("send", now);
    if (now) {
      this.#skipSleepsResolver.resolve();
    } else {
      await this.#visibilityWatcher?.waitForVisible();
    }
    this.#pendingResolver.resolve();
    const result = await this.#sendResolver.promise;
    this.#sendCounter--;
    return result;
  }
  async run() {
    const sendRecords = [];
    let recoverResolver = resolver4();
    let lastSendTime;
    let counter = 0;
    const delegate = this.#delegate;
    const { debug } = this.#lc;
    let delay = 0;
    debug?.("Starting connection loop");
    const sleepMaybeSkip = (ms) => Promise.race([this.#skipSleepsResolver.promise, sleep(ms)]);
    while (!this.#closed) {
      debug?.(
        didLastSendRequestFail(sendRecords) ? "Last request failed. Trying again" : "Waiting for a send"
      );
      const races = [this.#pendingResolver.promise];
      const t2 = delegate.watchdogTimer;
      if (t2 !== null) {
        races.push(sleep(t2));
      }
      await Promise.race(races);
      if (this.#closed) break;
      debug?.("Waiting for debounce");
      await sleepMaybeSkip(delegate.debounceDelay);
      if (this.#closed) break;
      debug?.("debounced");
      this.#pendingResolver = resolver4();
      if (counter >= delegate.maxConnections) {
        debug?.("Too many request in flight. Waiting until one finishes...");
        await this.#waitUntilAvailableConnection();
        if (this.#closed) break;
        debug?.("...finished");
      }
      if (counter > 0 || didLastSendRequestFail(sendRecords)) {
        delay = computeDelayAndUpdateDurations(delay, delegate, sendRecords);
        debug?.(
          didLastSendRequestFail(sendRecords) ? "Last connection errored. Sleeping for" : "More than one outstanding connection (" + counter + "). Sleeping for",
          delay,
          "ms"
        );
      } else {
        delay = 0;
      }
      const clampedDelay = Math.min(
        delegate.maxDelayMs,
        Math.max(delegate.minDelayMs, delay)
      );
      if (lastSendTime !== void 0) {
        const timeSinceLastSend = Date.now() - lastSendTime;
        if (clampedDelay > timeSinceLastSend) {
          await Promise.race([
            sleepMaybeSkip(clampedDelay - timeSinceLastSend),
            recoverResolver.promise
          ]);
          if (this.#closed) break;
        }
      }
      counter++;
      (async () => {
        const start = Date.now();
        let ok;
        let error;
        try {
          lastSendTime = start;
          debug?.("Sending request");
          this.#skipSleepsResolver = resolver4();
          ok = await delegate.invokeSend();
          debug?.("Send returned", ok);
        } catch (e) {
          debug?.("Send failed", e);
          error = e;
          ok = false;
        }
        if (this.#closed) {
          debug?.("Closed after invokeSend");
          return;
        }
        debug?.("Request done", { duration: Date.now() - start, ok });
        sendRecords.push({ duration: Date.now() - start, ok });
        if (recovered(sendRecords)) {
          recoverResolver.resolve();
          recoverResolver = resolver4();
        }
        counter--;
        this.#connectionAvailable();
        const sendResolver = this.#sendResolver;
        this.#sendResolver = resolver4();
        if (error) {
          sendResolver.resolve({ error });
        } else {
          sendResolver.resolve(void 0);
        }
        if (!ok) {
          this.#pendingResolver.resolve();
        }
      })();
    }
  }
  #waitingConnectionResolve = void 0;
  #connectionAvailable() {
    if (this.#waitingConnectionResolve) {
      const resolve = this.#waitingConnectionResolve;
      this.#waitingConnectionResolve = void 0;
      resolve();
    }
  }
  #waitUntilAvailableConnection() {
    const { promise, resolve } = resolver4();
    this.#waitingConnectionResolve = resolve;
    return promise;
  }
};
var CONNECTION_MEMORY_COUNT = 9;
function closeError() {
  return new Error("Closed");
}
function computeDelayAndUpdateDurations(delay, delegate, sendRecords) {
  const { length } = sendRecords;
  if (length === 0) {
    return delay;
  }
  const { ok } = sendRecords[sendRecords.length - 1];
  const { maxConnections, minDelayMs } = delegate;
  if (!ok) {
    return delay === 0 ? minDelayMs : delay * 2;
  }
  if (length > 1) {
    const previous = sendRecords[sendRecords.length - 2];
    while (sendRecords.length > CONNECTION_MEMORY_COUNT) {
      sendRecords.shift();
    }
    if (ok && !previous.ok) {
      return minDelayMs;
    }
  }
  const med = median(
    sendRecords.filter(({ ok: ok2 }) => ok2).map(({ duration }) => duration)
  );
  return med / maxConnections | 0;
}
function median(values) {
  values.sort();
  const { length } = values;
  const half = length >> 1;
  if (length % 2 === 1) {
    return values[half];
  }
  return (values[half - 1] + values[half]) / 2;
}
function didLastSendRequestFail(sendRecords) {
  return sendRecords.length > 0 && !sendRecords[sendRecords.length - 1].ok;
}
function recovered(sendRecords) {
  return sendRecords.length > 1 && !sendRecords[sendRecords.length - 2].ok && sendRecords[sendRecords.length - 1].ok;
}

// ../replicache/src/dag/lazy-store.ts
import { RWLock as RWLock2 } from "@rocicorp/lock";
var LazyStore = class {
  /**
   * This lock is used to ensure correct isolation of Reads and Writes.
   * Multiple Reads are allowed in parallel but only a single Write.  Reads and
   * Writes see an isolated view of the store (corresponding to the Serializable
   * level of transaction isolation defined in the SQL standard).
   *
   * To ensure these semantics the read lock must be acquired when a Read is
   * created and held til it is closed, and a Write lock must be acquired when a
   * Write is created and held til it is committed or closed.
   *
   * Code must have a read or write lock to
   * - read `_heads`
   * - read `_memOnlyChunks`
   * - read `_sourceStore`
   * - read and write `_sourceChunksCache`
   * - read and write `_refCounts`
   * - read and write `_refs`
   * and must have a write lock to
   * - write `_heads`
   * - write `_memOnlyChunks`
   */
  #rwLock = new RWLock2();
  #heads = /* @__PURE__ */ new Map();
  #sourceStore;
  #chunkHasher;
  #assertValidHash;
  /** The following are protected so testing subclass can access. */
  _memOnlyChunks = /* @__PURE__ */ new Map();
  _sourceChunksCache;
  /**
   * Ref counts are maintained so that chunks which are unreachable
   * from this stores heads can be eagerly and deterministically deleted from
   * `this._memOnlyChunks` and `this._sourceChunksCache`.
   *
   * These ref counts are independent from `this._sourceStore`'s ref counts.
   * These ref counts are based on reachability from `this._heads`.
   * A chunk is deleted from `this._memOnlyChunks` or
   * `this._sourceChunksCache` (which ever it is in) when its ref count becomes
   * zero.
   * These ref counts count the refs in `this._heads` and `this._refs`.
   *
   * Not all reachable chunk's refs are included in `this._refs`, because this
   * would require loading all chunks reachable in the source store in a
   * non-lazy manner. `this._refs` contains the refs of all currently reachable
   * chunks that were ever in `this._memOnlyChunks` or
   * `this._sourceChunksCache` (even if they have been evicted).  A
   * chunk's ref information is lazily discovered and stored in `this._refs` and
   * counted in `this._refCounts`. A chunk's entries in `this._refs` and
   * `this._refCounts` are only deleted when a chunk is deleted due to it
   * becoming unreachable (it is not deleted if the chunk is evicted from the
   * source-store cache).
   *
   * The major implication of this lazy discovery of source store refs, is that
   * a reachable source store chunk may not be cached when loaded, because it is
   * not known to be reachable because some of the pertinent refs have not been
   * discovered.  However, in practice chunks are read by traversing the graph
   * starting from a head, and all pertinent refs are discovered as part of the
   * traversal.
   *
   * These ref counts can be changed in two ways:
   * 1. A LazyRead has a cache miss and loads a chunk from the source store that
   * is reachable from this._heads.  If this chunk's refs are not currently
   * counted, it will not have an entry in `this._refs`.  In this case, the
   * chunks refs will be put in `this._refs` and `this._refCounts` will be
   * updated to count them.
   * 2. A LazyWrite commit updates a head (which can result in increasing or
   * decreasing ref count) or puts a reachable chunk (either a `memory-only` or
   * `source` chunk) that references this hash (increasing ref count).  The
   * computation of these ref count changes is delegated to the
   * `computeRefCountUpdates` shared with dag.StoreImpl.  In order to
   * delegate determining reachability to `computeRefCountUpdates` and defer
   * this determination until commit time, LazyWrite treats cache misses
   * as a 'put' of the lazily-loaded chunk.
   *
   * A chunk's hash may have an entry in `this._refCounts` without that
   * chunk have ever been in `this._memOnlyChunks` or `this._sourceChunksCache`.
   * This is the case when a head or a reachable chunk that was ever in
   * `this._memOnlyChunks` or `this._sourceChunksCache` references a chunk
   * which is not currently cached (either because it has not been read, or
   * because it has been evicted).
   */
  _refCounts = /* @__PURE__ */ new Map();
  _refs = /* @__PURE__ */ new Map();
  constructor(sourceStore, sourceCacheSizeLimit, chunkHasher, assertValidHash, getSizeOfChunk = getSizeOfValue) {
    this._sourceChunksCache = new ChunksCache(
      sourceCacheSizeLimit,
      getSizeOfChunk,
      this._refCounts,
      this._refs
    );
    this.#sourceStore = sourceStore;
    this.#chunkHasher = chunkHasher;
    this.#assertValidHash = assertValidHash;
  }
  async read(sourceRead) {
    const release = await this.#rwLock.read();
    return new LazyRead(
      this.#heads,
      this._memOnlyChunks,
      this._sourceChunksCache,
      this.#sourceStore,
      release,
      this.#assertValidHash,
      sourceRead
    );
  }
  async write() {
    const release = await this.#rwLock.write();
    return new LazyWrite(
      this.#heads,
      this._memOnlyChunks,
      this._sourceChunksCache,
      this.#sourceStore,
      this._refCounts,
      this._refs,
      release,
      this.#chunkHasher,
      this.#assertValidHash
    );
  }
  close() {
    return promiseVoid;
  }
  /**
   * Does not acquire any lock on the store.
   */
  isCached(chunkHash) {
    return this._sourceChunksCache.getWithoutUpdatingLRU(chunkHash) !== void 0;
  }
  withSuspendedSourceCacheEvictsAndDeletes(fn) {
    return this._sourceChunksCache.withSuspendedEvictsAndDeletes(fn);
  }
};
var LazyRead = class {
  _heads;
  _memOnlyChunks;
  _sourceChunksCache;
  _sourceStore;
  #sourceRead = void 0;
  #release;
  #closed = false;
  assertValidHash;
  #sourceReadOwnedByCaller;
  constructor(heads, memOnlyChunks, sourceChunksCache, sourceStore, release, assertValidHash, sourceRead) {
    this._heads = heads;
    this._memOnlyChunks = memOnlyChunks;
    this._sourceChunksCache = sourceChunksCache;
    this._sourceStore = sourceStore;
    this.#release = release;
    this.assertValidHash = assertValidHash;
    this.#sourceRead = sourceRead !== void 0 ? Promise.resolve(sourceRead) : void 0;
    this.#sourceReadOwnedByCaller = sourceRead !== void 0;
  }
  isMemOnlyChunkHash(hash) {
    return this._memOnlyChunks.has(hash);
  }
  async hasChunk(hash) {
    return await this.getChunk(hash) !== void 0;
  }
  async getChunk(hash) {
    const memOnlyChunk = this._memOnlyChunks.get(hash);
    if (memOnlyChunk !== void 0) {
      return memOnlyChunk;
    }
    let chunk = this._sourceChunksCache.get(hash);
    if (chunk === void 0) {
      chunk = await (await this._getSourceRead()).getChunk(hash);
      if (chunk !== void 0) {
        this._sourceChunksCache.put(chunk);
      }
    }
    return chunk;
  }
  mustGetChunk(hash) {
    return mustGetChunk(this, hash);
  }
  getHead(name) {
    return Promise.resolve(this._heads.get(name));
  }
  release() {
    if (!this.#closed) {
      this.#release();
      if (!this.#sourceReadOwnedByCaller) {
        this.#sourceRead?.then((read) => read.release()).catch((_) => {
        });
      }
      this.#closed = true;
    }
  }
  get closed() {
    return this.#closed;
  }
  _getSourceRead() {
    if (!this.#sourceRead) {
      this.#sourceRead = this._sourceStore.read();
    }
    return this.#sourceRead;
  }
};
var LazyWrite = class extends LazyRead {
  #refCounts;
  #refs;
  #chunkHasher;
  _pendingHeadChanges = /* @__PURE__ */ new Map();
  _pendingMemOnlyChunks = /* @__PURE__ */ new Map();
  _pendingCachedChunks = /* @__PURE__ */ new Map();
  #createdChunks = /* @__PURE__ */ new Set();
  constructor(heads, memOnlyChunks, sourceChunksCache, sourceStore, refCounts, refs, release, chunkHasher, assertValidHash) {
    super(
      heads,
      memOnlyChunks,
      sourceChunksCache,
      sourceStore,
      release,
      assertValidHash
    );
    this.#refCounts = refCounts;
    this.#refs = refs;
    this.#chunkHasher = chunkHasher;
  }
  createChunk = (data, refs) => {
    const chunk = createChunk(data, refs, this.#chunkHasher);
    this.#createdChunks.add(chunk.hash);
    return chunk;
  };
  putChunk(c, size) {
    const { hash, meta } = c;
    this.assertValidHash(hash);
    if (meta.length > 0) {
      for (const h of meta) {
        this.assertValidHash(h);
      }
    }
    if (this.#createdChunks.has(hash) || this.isMemOnlyChunkHash(hash)) {
      this._pendingMemOnlyChunks.set(hash, c);
    } else {
      this._pendingCachedChunks.set(hash, { chunk: c, size: size ?? -1 });
    }
    return promiseVoid;
  }
  async setHead(name, hash) {
    await this.#setHead(name, hash);
  }
  async removeHead(name) {
    await this.#setHead(name, void 0);
  }
  async #setHead(name, hash) {
    const oldHash = await this.getHead(name);
    const v = this._pendingHeadChanges.get(name);
    if (v === void 0) {
      this._pendingHeadChanges.set(name, { new: hash, old: oldHash });
    } else {
      v.new = hash;
    }
  }
  isMemOnlyChunkHash(hash) {
    return this._pendingMemOnlyChunks.has(hash) || super.isMemOnlyChunkHash(hash);
  }
  async getChunk(hash) {
    const pendingMemOnlyChunk = this._pendingMemOnlyChunks.get(hash);
    if (pendingMemOnlyChunk !== void 0) {
      return pendingMemOnlyChunk;
    }
    const memOnlyChunk = this._memOnlyChunks.get(hash);
    if (memOnlyChunk !== void 0) {
      return memOnlyChunk;
    }
    const pendingCachedChunk = this._pendingCachedChunks.get(hash);
    if (pendingCachedChunk !== void 0) {
      return pendingCachedChunk.chunk;
    }
    let chunk = this._sourceChunksCache.get(hash);
    if (chunk === void 0) {
      chunk = await (await this._getSourceRead()).getChunk(hash);
      if (chunk !== void 0) {
        this._pendingCachedChunks.set(chunk.hash, { chunk, size: -1 });
      }
    }
    return chunk;
  }
  getHead(name) {
    const headChange = this._pendingHeadChanges.get(name);
    if (headChange) {
      return Promise.resolve(headChange.new);
    }
    return super.getHead(name);
  }
  async commit() {
    const pendingChunks = new Set(
      joinIterables(
        this._pendingMemOnlyChunks.keys(),
        this._pendingCachedChunks.keys()
      )
    );
    const refCountUpdates = await computeRefCountUpdates(
      this._pendingHeadChanges.values(),
      pendingChunks,
      this
    );
    for (const [hash, count] of refCountUpdates) {
      if (this.isMemOnlyChunkHash(hash)) {
        if (count === 0) {
          this.#refCounts.delete(hash);
          this._memOnlyChunks.delete(hash);
          this.#refs.delete(hash);
        } else {
          this.#refCounts.set(hash, count);
          const chunk = this._pendingMemOnlyChunks.get(hash);
          if (chunk) {
            this.#refs.set(hash, chunk.meta);
            this._memOnlyChunks.set(hash, chunk);
          }
        }
        refCountUpdates.delete(hash);
      }
    }
    this._sourceChunksCache.updateForCommit(
      this._pendingCachedChunks,
      refCountUpdates
    );
    for (const [name, headChange] of this._pendingHeadChanges) {
      if (headChange.new) {
        this._heads.set(name, headChange.new);
      } else {
        this._heads.delete(name);
      }
    }
    this._pendingMemOnlyChunks.clear();
    this._pendingCachedChunks.clear();
    this._pendingHeadChanges.clear();
    this.release();
  }
  getRefCount(hash) {
    return this.#refCounts.get(hash);
  }
  getRefs(hash) {
    const pendingMemOnlyChunk = this._pendingMemOnlyChunks.get(hash);
    if (pendingMemOnlyChunk) {
      return pendingMemOnlyChunk.meta;
    }
    const memOnlyChunk = this._memOnlyChunks.get(hash);
    if (memOnlyChunk) {
      return memOnlyChunk.meta;
    }
    const pendingCachedChunk = this._pendingCachedChunks.get(hash);
    if (pendingCachedChunk !== void 0) {
      return pendingCachedChunk.chunk.meta;
    }
    return this.#refs.get(hash);
  }
  areRefsCounted(hash) {
    return this.#refs.has(hash);
  }
  chunksPersisted(chunkHashes) {
    const chunksToCache = [];
    for (const chunkHash of chunkHashes) {
      const chunk = this._memOnlyChunks.get(chunkHash);
      if (chunk) {
        this._memOnlyChunks.delete(chunkHash);
        chunksToCache.push(chunk);
      }
    }
    this._sourceChunksCache.persisted(chunksToCache);
  }
};
var ChunksCache = class {
  #cacheSizeLimit;
  #getSizeOfChunk;
  #refCounts;
  #refs;
  #size = 0;
  #evictsAndDeletesSuspended = false;
  #suspendedDeletes = [];
  /**
   * Iteration order is from least to most recently used.
   *
   * Public so that testing subclass can access.
   */
  cacheEntries = /* @__PURE__ */ new Map();
  constructor(cacheSizeLimit, getSizeOfChunk, refCounts, refs) {
    this.#cacheSizeLimit = cacheSizeLimit;
    this.#getSizeOfChunk = getSizeOfChunk;
    this.#refCounts = refCounts;
    this.#refs = refs;
  }
  get(hash) {
    const cacheEntry = this.cacheEntries.get(hash);
    if (cacheEntry) {
      this.cacheEntries.delete(hash);
      this.cacheEntries.set(hash, cacheEntry);
    }
    return cacheEntry?.chunk;
  }
  getWithoutUpdatingLRU(hash) {
    return this.cacheEntries.get(hash)?.chunk;
  }
  put(chunk) {
    const { hash } = chunk;
    const oldCacheEntry = this.cacheEntries.get(hash);
    if (oldCacheEntry) {
      this.cacheEntries.delete(hash);
      this.cacheEntries.set(hash, oldCacheEntry);
      return;
    }
    const refCount = this.#refCounts.get(hash);
    if (refCount === void 0 || refCount < 1) {
      return;
    }
    if (!this.#cacheChunk(chunk)) {
      return;
    }
    if (!this.#refs.has(hash)) {
      for (const refHash of chunk.meta) {
        this.#refCounts.set(refHash, (this.#refCounts.get(refHash) || 0) + 1);
      }
      this.#refs.set(hash, chunk.meta);
    }
    this.#ensureCacheSizeLimit();
  }
  #ensureCacheSizeLimit() {
    if (this.#evictsAndDeletesSuspended) {
      return;
    }
    for (const entry of this.cacheEntries.values()) {
      if (this.#size <= this.#cacheSizeLimit) {
        break;
      }
      this.#evict(entry);
    }
  }
  #cacheChunk(chunk, size) {
    const chunkSize = size ?? this.#getSizeOfChunk(chunk);
    if (chunkSize > this.#cacheSizeLimit) {
      return false;
    }
    this.#size += chunkSize;
    this.cacheEntries.set(chunk.hash, { chunk, size: chunkSize });
    return true;
  }
  #evict(cacheEntry) {
    const { hash } = cacheEntry.chunk;
    this.#size -= cacheEntry.size;
    this.cacheEntries.delete(hash);
  }
  #deleteEntryByHash(hash) {
    this.#refCounts.delete(hash);
    this.#refs.delete(hash);
    const cacheEntry = this.cacheEntries.get(hash);
    if (cacheEntry) {
      this.#size -= cacheEntry.size;
      this.cacheEntries.delete(hash);
    }
  }
  updateForCommit(chunksToPut, refCountUpdates) {
    for (const [hash, count] of refCountUpdates) {
      if (count === 0) {
        if (!this.#evictsAndDeletesSuspended) {
          this.#deleteEntryByHash(hash);
        } else {
          this.#refCounts.set(hash, 0);
          this.#suspendedDeletes.push(hash);
        }
      } else {
        this.#refCounts.set(hash, count);
        const chunkAndSize = chunksToPut.get(hash);
        if (chunkAndSize) {
          const { chunk, size } = chunkAndSize;
          const oldCacheEntry = this.cacheEntries.get(hash);
          if (oldCacheEntry) {
            this.cacheEntries.delete(hash);
            this.cacheEntries.set(hash, oldCacheEntry);
          } else {
            this.#cacheChunk(chunk, size !== -1 ? size : void 0);
            this.#refs.set(hash, chunk.meta);
          }
        }
      }
    }
    this.#ensureCacheSizeLimit();
  }
  persisted(chunks) {
    for (const chunk of chunks) {
      this.#cacheChunk(chunk);
    }
    this.#ensureCacheSizeLimit();
  }
  async withSuspendedEvictsAndDeletes(fn) {
    this.#evictsAndDeletesSuspended = true;
    try {
      return await fn();
    } finally {
      this.#evictsAndDeletesSuspended = false;
      for (const hash of this.#suspendedDeletes) {
        if (this.#refCounts.get(hash) === 0) {
          this.#deleteEntryByHash(hash);
        }
      }
      this.#ensureCacheSizeLimit();
    }
  }
};

// ../replicache/src/transactions.ts
import { greaterThan as greaterThan2 } from "compare-utf8";

// ../replicache/src/scan-iterator.ts
import { greaterThan } from "compare-utf8";

// ../replicache/src/scan-options.ts
function isScanIndexOptions(options) {
  return options.indexName !== void 0;
}
function normalizeScanOptionIndexedStartKey(startKey) {
  if (typeof startKey === "string") {
    return [startKey];
  }
  return startKey;
}
function toDbScanOptions(options) {
  if (!options) {
    return {};
  }
  let key;
  let exclusive;
  let primary;
  let secondary;
  if (options.start) {
    ({ key, exclusive } = options.start);
    if (options.indexName) {
      if (typeof key === "string") {
        secondary = key;
      } else {
        secondary = key[0];
        primary = key[1];
      }
    } else {
      primary = key;
    }
  }
  return {
    prefix: options.prefix,
    startSecondaryKey: secondary,
    startKey: primary,
    startExclusive: exclusive,
    limit: options.limit,
    indexName: options.indexName
  };
}

// ../replicache/src/transaction-closed-error.ts
var TransactionClosedError = class extends Error {
  constructor() {
    super("Transaction is closed");
  }
};
function throwIfClosed(tx) {
  if (tx.closed) {
    throw new TransactionClosedError();
  }
}
function rejectIfClosed(tx) {
  return tx.closed ? Promise.reject(new TransactionClosedError()) : void 0;
}

// ../replicache/src/scan-iterator.ts
var ScanResultImpl = class {
  #iter;
  #options;
  #dbDelegateOptions;
  #onLimitKey;
  constructor(iter, options, dbDelegateOptions, onLimitKey) {
    this.#iter = iter;
    this.#options = options;
    this.#dbDelegateOptions = dbDelegateOptions;
    this.#onLimitKey = onLimitKey;
  }
  /** The default AsyncIterable. This is the same as {@link values}. */
  [Symbol.asyncIterator]() {
    return this.values();
  }
  /** Async iterator over the values of the {@link ReadTransaction.scan | scan} call. */
  values() {
    return new AsyncIterableIteratorToArrayWrapperImpl(
      this.#newIterator((e) => e[1])
    );
  }
  /**
   * Async iterator over the keys of the {@link ReadTransaction.scan | scan}
   * call. If the {@link ReadTransaction.scan | scan} is over an index the key
   * is a tuple of `[secondaryKey: string, primaryKey]`
   */
  keys() {
    return new AsyncIterableIteratorToArrayWrapperImpl(
      this.#newIterator((e) => e[0])
    );
  }
  /**
   * Async iterator over the entries of the {@link ReadTransaction.scan | scan}
   * call. An entry is a tuple of key values. If the
   * {@link ReadTransaction.scan | scan} is over an index the key is a tuple of
   * `[secondaryKey: string, primaryKey]`
   */
  entries() {
    return new AsyncIterableIteratorToArrayWrapperImpl(
      this.#newIterator((e) => [e[0], e[1]])
    );
  }
  /** Returns all the values as an array. Same as `values().toArray()` */
  toArray() {
    return this.values().toArray();
  }
  #newIterator(toValue) {
    return scanIterator(
      toValue,
      this.#iter,
      this.#options,
      this.#dbDelegateOptions,
      this.#onLimitKey
    );
  }
};
var AsyncIterableIteratorToArrayWrapperImpl = class {
  #it;
  constructor(it) {
    this.#it = it;
  }
  next() {
    return this.#it.next();
  }
  [Symbol.asyncIterator]() {
    return this.#it[Symbol.asyncIterator]();
  }
  toArray() {
    return asyncIterableToArray(this.#it);
  }
};
async function* scanIterator(toValue, iter, options, closed, onLimitKey) {
  throwIfClosed(closed);
  let { limit = Infinity } = options;
  const { prefix = "" } = options;
  let exclusive = options.start?.exclusive;
  const isIndexScan = isScanIndexOptions(options);
  for await (const entry of iter) {
    const key = entry[0];
    const keyToMatch = isIndexScan ? key[0] : key;
    if (!keyToMatch.startsWith(prefix)) {
      return;
    }
    if (exclusive) {
      exclusive = true;
      if (isIndexScan) {
        if (shouldSkipIndexScan(key, options.start.key)) {
          continue;
        }
      } else {
        if (shouldSkipNonIndexScan(key, options.start.key)) {
          continue;
        }
      }
    }
    yield toValue(entry);
    if (--limit === 0) {
      if (!isIndexScan) {
        onLimitKey(key);
      }
      return;
    }
  }
}
function shouldSkipIndexScan(key, startKey) {
  const [secondaryStartKey, primaryStartKey] = normalizeScanOptionIndexedStartKey(startKey);
  const [secondaryKey, primaryKey] = normalizeScanOptionIndexedStartKey(key);
  if (secondaryKey !== secondaryStartKey) {
    return false;
  }
  if (primaryStartKey === void 0) {
    return true;
  }
  return primaryKey === primaryStartKey;
}
function shouldSkipNonIndexScan(key, startKey) {
  return key === startKey;
}
function fromKeyForIndexScanInternal(options) {
  const { prefix, start } = options;
  let prefix2 = "";
  if (prefix !== void 0) {
    prefix2 = encodeIndexScanKey(prefix, void 0);
  }
  if (!start) {
    return prefix2;
  }
  const { key } = start;
  const [secondary, primary] = normalizeScanOptionIndexedStartKey(key);
  const startKey = encodeIndexScanKey(secondary, primary);
  if (greaterThan(startKey, prefix2)) {
    return startKey;
  }
  return prefix2;
}

// ../replicache/src/transactions.ts
var transactionIDCounter = 0;
var ReadTransactionImpl = class {
  clientID;
  dbtx;
  _lc;
  /**
   * The location in which this transaction is being used. This is either `client` or `server`.
   */
  location;
  /** @deprecated Use {@link ReadTransaction.location} instead. */
  environment;
  constructor(clientID, dbRead, lc, rpcName = "openReadTransaction") {
    this.clientID = clientID;
    this.dbtx = dbRead;
    this._lc = lc.withContext(rpcName).withContext("txid", transactionIDCounter++);
    this.environment = "client";
    this.location = "client";
  }
  get(key) {
    return rejectIfClosed(this.dbtx) || this.dbtx.get(key);
  }
  // eslint-disable-next-line require-await
  async has(key) {
    throwIfClosed(this.dbtx);
    return this.dbtx.has(key);
  }
  // eslint-disable-next-line require-await
  async isEmpty() {
    throwIfClosed(this.dbtx);
    return this.dbtx.isEmpty();
  }
  scan(options) {
    return scan(options, this.dbtx, noop);
  }
};
function noop(_) {
}
function scan(options, dbRead, onLimitKey) {
  const iter = getScanIterator(dbRead, options);
  return makeScanResultFromScanIteratorInternal(
    iter,
    options ?? {},
    dbRead,
    onLimitKey
  );
}
var SubscriptionTransactionWrapper = class {
  #keys = /* @__PURE__ */ new Set();
  #scans = [];
  #tx;
  constructor(tx) {
    this.#tx = tx;
  }
  get environment() {
    return this.#tx.location;
  }
  get location() {
    return this.#tx.location;
  }
  get clientID() {
    return this.#tx.clientID;
  }
  isEmpty() {
    this.#scans.push({ options: {} });
    return this.#tx.isEmpty();
  }
  get(key) {
    this.#keys.add(key);
    return this.#tx.get(key);
  }
  has(key) {
    this.#keys.add(key);
    return this.#tx.has(key);
  }
  scan(options) {
    const scanInfo = {
      options: toDbScanOptions(options),
      inclusiveLimitKey: void 0
    };
    this.#scans.push(scanInfo);
    return scan(options, this.#tx.dbtx, (inclusiveLimitKey) => {
      scanInfo.inclusiveLimitKey = inclusiveLimitKey;
    });
  }
  get keys() {
    return this.#keys;
  }
  get scans() {
    return this.#scans;
  }
};
var zeroData = Symbol();
var WriteTransactionImpl = class extends ReadTransactionImpl {
  reason;
  mutationID;
  [zeroData];
  constructor(clientID, mutationID, reason, zData, dbWrite, lc, rpcName = "openWriteTransaction") {
    super(clientID, dbWrite, lc, rpcName);
    this.mutationID = mutationID;
    this.reason = reason;
    this[zeroData] = zData;
  }
  put(key, value) {
    return this.set(key, value);
  }
  async set(key, value) {
    throwIfClosed(this.dbtx);
    await this.dbtx.put(this._lc, key, deepFreeze(value));
  }
  del(key) {
    return rejectIfClosed(this.dbtx) ?? this.dbtx.del(this._lc, key);
  }
};
function getScanIterator(dbRead, options) {
  if (options && isScanIndexOptions(options)) {
    return getScanIteratorForIndexMap(dbRead, options);
  }
  return dbRead.map.scan(fromKeyForNonIndexScan(options));
}
function fromKeyForNonIndexScan(options) {
  if (!options) {
    return "";
  }
  const { prefix = "", start } = options;
  if (start && greaterThan2(start.key, prefix)) {
    return start.key;
  }
  return prefix;
}
function makeScanResultFromScanIteratorInternal(iter, options, dbRead, onLimitKey) {
  return new ScanResultImpl(iter, options, dbRead, onLimitKey);
}
async function* getScanIteratorForIndexMap(dbRead, options) {
  const map = dbRead.getMapForIndex(options.indexName);
  for await (const entry of map.scan(fromKeyForIndexScanInternal(options))) {
    yield [decodeIndexKey(entry[0]), entry[1]];
  }
}

// ../replicache/src/db/rebase.ts
async function rebaseMutation(mutation, dagWrite, basisHash, mutators, lc, mutationClientID, formatVersion, zeroData2) {
  const localMeta = mutation.meta;
  const name = localMeta.mutatorName;
  if (isLocalMetaDD31(localMeta)) {
    assert(
      localMeta.clientID === mutationClientID,
      "mutationClientID must match clientID of LocalMeta"
    );
  }
  const maybeMutatorImpl = mutators[name];
  if (!maybeMutatorImpl) {
    lc.error?.(`Cannot rebase unknown mutator ${name}`);
  }
  const mutatorImpl = maybeMutatorImpl || (async () => {
  });
  const args = localMeta.mutatorArgsJSON;
  const basisCommit = await commitFromHash(basisHash, dagWrite);
  const nextMutationID = await basisCommit.getNextMutationID(
    mutationClientID,
    dagWrite
  );
  if (nextMutationID !== localMeta.mutationID) {
    throw new Error(
      `Inconsistent mutation ID: original: ${localMeta.mutationID}, next: ${nextMutationID} - mutationClientID: ${mutationClientID} mutatorName: ${name}`
    );
  }
  if (formatVersion >= DD31) {
    assertLocalMetaDD31(localMeta);
  }
  const dbWrite = await newWriteLocal(
    basisHash,
    name,
    args,
    mutation.chunk.hash,
    dagWrite,
    localMeta.timestamp,
    mutationClientID,
    formatVersion
  );
  const tx = new WriteTransactionImpl(
    mutationClientID,
    await dbWrite.getMutationID(),
    "rebase",
    zeroData2,
    dbWrite,
    lc
  );
  await mutatorImpl(tx, args);
  return dbWrite;
}
async function rebaseMutationAndPutCommit(mutation, dagWrite, basis, mutators, lc, mutationClientID, formatVersion, zeroData2) {
  const tx = await rebaseMutation(
    mutation,
    dagWrite,
    basis,
    mutators,
    lc,
    mutationClientID,
    formatVersion,
    zeroData2
  );
  return tx.putCommit();
}
async function rebaseMutationAndCommit(mutation, dagWrite, basis, headName, mutators, lc, mutationClientID, formatVersion, zeroData2) {
  const dbWrite = await rebaseMutation(
    mutation,
    dagWrite,
    basis,
    mutators,
    lc,
    mutationClientID,
    formatVersion,
    zeroData2
  );
  return dbWrite.commit(headName);
}

// ../replicache/src/get-default-pusher.ts
function getDefaultPusher(rep) {
  async function pusher(requestBody, requestID) {
    const [response, httpRequestInfo] = await callDefaultFetch(
      rep.pushURL,
      rep.auth,
      requestID,
      requestBody
    );
    if (!response) {
      return { httpRequestInfo };
    }
    const rv = {
      httpRequestInfo
    };
    let result;
    try {
      result = await response.json();
    } catch {
      return rv;
    }
    if (isClientStateNotFoundResponse(result) || isVersionNotSupportedResponse(result)) {
      rv.response = result;
    }
    return rv;
  }
  defaultPushers.add(pusher);
  return pusher;
}
var defaultPushers = /* @__PURE__ */ new WeakSet();
function isDefaultPusher(pusher) {
  return defaultPushers.has(pusher);
}

// ../replicache/src/to-error.ts
function toError(e) {
  if (e instanceof Error) {
    return e;
  }
  return new Error(String(e));
}

// ../replicache/src/sync/handle-pull-response-result-type-enum.ts
var Applied = 0;
var NoOp = 1;
var CookieMismatch = 2;

// ../replicache/src/sync/patch.ts
async function apply(lc, dbWrite, patch) {
  for (const p of patch) {
    switch (p.op) {
      case "put": {
        const frozen = deepFreeze(p.value);
        await dbWrite.put(lc, p.key, frozen);
        break;
      }
      case "update": {
        const existing = await dbWrite.get(p.key);
        const entries = [];
        const addToEntries = (toAdd) => {
          for (const [key, value] of Object.entries(toAdd)) {
            if (!p.constrain || p.constrain.length === 0 || p.constrain.indexOf(key) > -1) {
              entries.push([key, value]);
            }
          }
        };
        if (existing !== void 0) {
          assertObject(existing);
          addToEntries(existing);
        }
        if (p.merge) {
          addToEntries(p.merge);
        }
        const frozen = deepFreeze(Object.fromEntries(entries));
        await dbWrite.put(lc, p.key, frozen);
        break;
      }
      case "del": {
        const existing = await dbWrite.get(p.key);
        if (existing === void 0) {
          continue;
        }
        await dbWrite.del(lc, p.key);
        break;
      }
      case "clear":
        await dbWrite.clear();
        break;
    }
  }
}

// ../replicache/src/sync/pull-error.ts
var PullError = class extends Error {
  name = "PullError";
  // causedBy is used instead of cause, because while cause has been proposed as a
  // JavaScript language standard for this purpose (see
  // https://github.com/tc39/proposal-error-cause) current browser behavior is
  // inconsistent.
  causedBy;
  constructor(causedBy) {
    super("Failed to pull");
    this.causedBy = causedBy;
  }
};

// ../replicache/src/sync/sync-head-name.ts
var SYNC_HEAD_NAME = "sync";

// ../replicache/src/sync/pull.ts
var PULL_VERSION_DD31 = 1;
async function beginPullV1(profileID, clientID, clientGroupID, schemaVersion, puller, requestID, store, formatVersion, lc, createSyncBranch = true) {
  const baseCookie = await withRead(store, async (dagRead) => {
    const mainHeadHash = await dagRead.getHead(DEFAULT_HEAD_NAME);
    if (!mainHeadHash) {
      throw new Error("Internal no main head found");
    }
    const baseSnapshot = await baseSnapshotFromHash(mainHeadHash, dagRead);
    const baseSnapshotMeta = baseSnapshot.meta;
    assertSnapshotMetaDD31(baseSnapshotMeta);
    return baseSnapshotMeta.cookieJSON;
  });
  const pullReq = {
    profileID,
    clientGroupID,
    cookie: baseCookie,
    pullVersion: PULL_VERSION_DD31,
    schemaVersion
  };
  const { response, httpRequestInfo } = await callPuller(
    lc,
    puller,
    pullReq,
    requestID
  );
  if (!response) {
    return {
      httpRequestInfo,
      syncHead: emptyHash
    };
  }
  if (!createSyncBranch || isErrorResponse(response)) {
    return {
      httpRequestInfo,
      pullResponse: response,
      syncHead: emptyHash
    };
  }
  const result = await handlePullResponseV1(
    lc,
    store,
    baseCookie,
    response,
    clientID,
    formatVersion
  );
  return {
    httpRequestInfo,
    pullResponse: response,
    syncHead: result.type === Applied ? result.syncHead : emptyHash
  };
}
async function callPuller(lc, puller, pullReq, requestID) {
  lc.debug?.("Starting pull...");
  const pullStart = Date.now();
  let pullerResult;
  try {
    pullerResult = await puller(pullReq, requestID);
    lc.debug?.(
      `...Pull ${pullerResult.response ? "complete" : "failed"} in `,
      Date.now() - pullStart,
      "ms"
    );
  } catch (e) {
    throw new PullError(toError(e));
  }
  try {
    assertPullerResultV1(pullerResult);
    return pullerResult;
  } catch (e) {
    throw new ReportError("Invalid puller result", toError(e));
  }
}
function badOrderMessage(name, receivedValue, lastSnapshotValue) {
  return `Received ${name} ${receivedValue} is < than last snapshot ${name} ${lastSnapshotValue}; ignoring client view`;
}
function handlePullResponseV1(lc, store, expectedBaseCookie, response, clientID, formatVersion) {
  return withWriteNoImplicitCommit(store, async (dagWrite) => {
    const dagRead = dagWrite;
    const mainHead = await dagRead.getHead(DEFAULT_HEAD_NAME);
    if (mainHead === void 0) {
      throw new Error("Main head disappeared");
    }
    const baseSnapshot = await baseSnapshotFromHash(mainHead, dagRead);
    const baseSnapshotMeta = baseSnapshot.meta;
    assertSnapshotMetaDD31(baseSnapshotMeta);
    const baseCookie = baseSnapshotMeta.cookieJSON;
    if (!deepEqual(expectedBaseCookie, baseCookie)) {
      lc.debug?.(
        "handlePullResponse: cookie mismatch, response is not applicable"
      );
      return {
        type: CookieMismatch
      };
    }
    for (const [clientID2, lmidChange] of Object.entries(
      response.lastMutationIDChanges
    )) {
      const lastMutationID = baseSnapshotMeta.lastMutationIDs[clientID2];
      if (lastMutationID !== void 0 && lmidChange < lastMutationID) {
        throw new Error(
          badOrderMessage(
            `${clientID2} lastMutationID`,
            String(lmidChange),
            String(lastMutationID)
          )
        );
      }
    }
    const frozenResponseCookie = deepFreeze(response.cookie);
    if (compareCookies(frozenResponseCookie, baseCookie) < 0) {
      throw new Error(
        badOrderMessage(
          "cookie",
          JSON.stringify(frozenResponseCookie),
          JSON.stringify(baseCookie)
        )
      );
    }
    if (deepEqual(frozenResponseCookie, baseCookie)) {
      if (response.patch.length > 0) {
        lc.error?.(
          `handlePullResponse: cookie ${JSON.stringify(
            baseCookie
          )} did not change, but patch is not empty`
        );
      }
      if (Object.keys(response.lastMutationIDChanges).length > 0) {
        lc.error?.(
          `handlePullResponse: cookie ${JSON.stringify(
            baseCookie
          )} did not change, but lastMutationIDChanges is not empty`
        );
      }
      return {
        type: NoOp
      };
    }
    const dbWrite = await newWriteSnapshotDD31(
      baseSnapshot.chunk.hash,
      { ...baseSnapshotMeta.lastMutationIDs, ...response.lastMutationIDChanges },
      frozenResponseCookie,
      dagWrite,
      clientID,
      formatVersion
    );
    await apply(lc, dbWrite, response.patch);
    return {
      type: Applied,
      syncHead: await dbWrite.commit(SYNC_HEAD_NAME)
    };
  });
}
function maybeEndPull(store, lc, expectedSyncHead, clientID, diffConfig, formatVersion) {
  return withWriteNoImplicitCommit(store, async (dagWrite) => {
    const dagRead = dagWrite;
    const syncHeadHash = await dagRead.getHead(SYNC_HEAD_NAME);
    if (syncHeadHash === void 0) {
      throw new Error("Missing sync head");
    }
    if (syncHeadHash !== expectedSyncHead) {
      lc.error?.(
        "maybeEndPull, Wrong sync head. Expecting:",
        expectedSyncHead,
        "got:",
        syncHeadHash
      );
      throw new Error("Wrong sync head");
    }
    const syncSnapshot = await baseSnapshotFromHash(syncHeadHash, dagRead);
    const mainHeadHash = await dagRead.getHead(DEFAULT_HEAD_NAME);
    if (mainHeadHash === void 0) {
      throw new Error("Missing main head");
    }
    const mainSnapshot = await baseSnapshotFromHash(mainHeadHash, dagRead);
    const { meta } = syncSnapshot;
    const syncSnapshotBasis = meta.basisHash;
    if (syncSnapshot === null) {
      throw new Error("Sync snapshot with no basis");
    }
    if (syncSnapshotBasis !== mainSnapshot.chunk.hash) {
      throw new Error("Overlapping syncs");
    }
    const syncHead = await commitFromHash(syncHeadHash, dagRead);
    const pending = [];
    const localMutations2 = await localMutations(mainHeadHash, dagRead);
    for (const commit of localMutations2) {
      let cid = clientID;
      assert(commitIsLocalDD31(commit));
      cid = commit.meta.clientID;
      if (await commit.getMutationID(cid, dagRead) > await syncHead.getMutationID(cid, dagRead)) {
        pending.push(commit);
      }
    }
    pending.reverse();
    const diffsMap = new DiffsMap();
    if (pending.length > 0) {
      return {
        syncHead: syncHeadHash,
        oldMainHead: mainHeadHash,
        mainHead: mainHeadHash,
        replayMutations: pending,
        // The changed keys are not reported when further replays are
        // needed. The diffs will be reported at the end when there
        // are no more mutations to be replay and then it will be reported
        // relative to DEFAULT_HEAD_NAME.
        diffs: diffsMap
      };
    }
    const mainHead = await commitFromHash(mainHeadHash, dagRead);
    if (diffConfig.shouldComputeDiffs()) {
      const mainHeadMap = new BTreeRead(
        dagRead,
        formatVersion,
        mainHead.valueHash
      );
      const syncHeadMap = new BTreeRead(
        dagRead,
        formatVersion,
        syncHead.valueHash
      );
      const valueDiff = await diff(mainHeadMap, syncHeadMap);
      diffsMap.set("", valueDiff);
      await addDiffsForIndexes(
        mainHead,
        syncHead,
        dagRead,
        diffsMap,
        diffConfig,
        formatVersion
      );
    }
    await Promise.all([
      dagWrite.setHead(DEFAULT_HEAD_NAME, syncHeadHash),
      dagWrite.removeHead(SYNC_HEAD_NAME)
    ]);
    await dagWrite.commit();
    const newMainHeadHash = syncHeadHash;
    if (lc.debug) {
      const [oldLastMutationID, oldCookie] = snapshotMetaParts(
        mainSnapshot,
        clientID
      );
      const [newLastMutationID, newCookie] = snapshotMetaParts(
        syncSnapshot,
        clientID
      );
      lc.debug(
        `Successfully pulled new snapshot with lastMutationID:`,
        newLastMutationID,
        `(prev:`,
        oldLastMutationID,
        `), cookie: `,
        newCookie,
        `(prev:`,
        oldCookie,
        `), sync head hash:`,
        syncHeadHash,
        ", main head hash:",
        mainHeadHash,
        `, valueHash:`,
        syncHead.valueHash,
        `(prev:`,
        mainSnapshot.valueHash
      );
    }
    return {
      syncHead: syncHeadHash,
      oldMainHead: mainHeadHash,
      mainHead: newMainHeadHash,
      replayMutations: [],
      diffs: diffsMap
    };
  });
}

// ../replicache/src/pusher.ts
function assertPusherResult(v) {
  assertObject(v);
  assertHTTPRequestInfo(v.httpRequestInfo);
  if (v.response !== void 0) {
    assertPushResponse(v.response);
  }
}
function assertPushResponse(v) {
  if (isClientStateNotFoundResponse(v)) {
    return;
  }
  assertVersionNotSupportedResponse(v);
}
var PushError = class extends Error {
  name = "PushError";
  // causedBy is used instead of cause, because while cause has been proposed as a
  // JavaScript language standard for this purpose (see
  // https://github.com/tc39/proposal-error-cause) current browser behavior is
  // inconsistent.
  causedBy;
  constructor(causedBy) {
    super("Failed to push");
    this.causedBy = causedBy;
  }
};

// ../replicache/src/sync/push.ts
var PUSH_VERSION_DD31 = 1;
var mutationV1Schema = readonlyObject({
  id: valita_exports.number(),
  name: valita_exports.string(),
  args: jsonSchema,
  timestamp: valita_exports.number(),
  clientID: clientIDSchema
});
var pushRequestV1Schema = valita_exports.object({
  pushVersion: valita_exports.literal(1),
  schemaVersion: valita_exports.string(),
  profileID: valita_exports.string(),
  clientGroupID: clientGroupIDSchema,
  mutations: valita_exports.array(mutationV1Schema)
});
function convertDD31(lm) {
  return {
    id: lm.mutationID,
    name: lm.mutatorName,
    args: lm.mutatorArgsJSON,
    timestamp: lm.timestamp,
    clientID: lm.clientID
  };
}
async function push(requestID, store, lc, profileID, clientGroupID, _clientID, pusher, schemaVersion, pushVersion) {
  const pending = await withRead(store, async (dagRead) => {
    const mainHeadHash = await dagRead.getHead(DEFAULT_HEAD_NAME);
    if (!mainHeadHash) {
      throw new Error("Internal no main head");
    }
    return localMutations(mainHeadHash, dagRead);
  });
  if (pending.length === 0) {
    return void 0;
  }
  pending.reverse();
  assert(pushVersion === PUSH_VERSION_DD31);
  const pushMutations = [];
  for (const commit of pending) {
    if (commitIsLocalDD31(commit)) {
      pushMutations.push(convertDD31(commit.meta));
    } else {
      throw new Error("Internal non local pending commit");
    }
  }
  assert(clientGroupID);
  const pushReq = {
    profileID,
    clientGroupID,
    mutations: pushMutations,
    pushVersion: PUSH_VERSION_DD31,
    schemaVersion
  };
  lc.debug?.("Starting push...");
  const pushStart = Date.now();
  const pusherResult = await callPusher(pusher, pushReq, requestID);
  lc.debug?.("...Push complete in ", Date.now() - pushStart, "ms");
  return pusherResult;
}
async function callPusher(pusher, body, requestID) {
  let pusherResult;
  try {
    pusherResult = await pusher(body, requestID);
  } catch (e) {
    throw new PushError(toError(e));
  }
  try {
    assertPusherResult(pusherResult);
    return pusherResult;
  } catch (e) {
    throw new ReportError("Invalid pusher result", toError(e));
  }
}

// ../replicache/src/mutation-recovery.ts
var MUTATION_RECOVERY_LAZY_STORE_SOURCE_CHUNK_CACHE_SIZE_LIMIT = 10 * 2 ** 20;

// ../replicache/src/broadcast-channel.ts
var NoopBroadcastChannel = class {
  name;
  onmessage = null;
  onmessageerror = null;
  constructor(name) {
    this.name = name;
  }
  addEventListener() {
  }
  removeEventListener() {
  }
  dispatchEvent() {
    return false;
  }
  close() {
  }
  postMessage() {
  }
};
var bc = typeof BroadcastChannel === "undefined" ? NoopBroadcastChannel : BroadcastChannel;

// ../replicache/src/new-client-channel.ts
function makeChannelNameV0(replicacheName) {
  return `replicache-new-client-group:${replicacheName}`;
}
function makeChannelNameV1(replicacheName) {
  return `replicache-new-client-group-v1:${replicacheName}`;
}
function isNewClientChannelMessageV1(message) {
  return typeof message === "object" && typeof message.clientGroupID === "string" && typeof message.idbName === "string";
}
function initNewClientChannel(replicacheName, idbName, signal, clientGroupID, isNewClientGroup, onUpdateNeeded, perdag) {
  if (signal.aborted) {
    return;
  }
  const channelV1 = new bc(makeChannelNameV1(replicacheName));
  if (isNewClientGroup) {
    channelV1.postMessage({ clientGroupID, idbName });
    const channelV0 = new bc(makeChannelNameV0(replicacheName));
    channelV0.postMessage([clientGroupID]);
    channelV0.close();
  }
  channelV1.onmessage = async (e) => {
    const { data } = e;
    if (isNewClientChannelMessageV1(data)) {
      const { clientGroupID: newClientGroupID, idbName: newClientIDBName } = data;
      if (newClientGroupID !== clientGroupID) {
        if (newClientIDBName === idbName) {
          const updateNeeded = await withRead(
            perdag,
            async (perdagRead) => await getClientGroup(newClientGroupID, perdagRead) !== void 0
          );
          if (updateNeeded) {
            onUpdateNeeded();
          }
        } else {
          onUpdateNeeded();
          return;
        }
      }
    }
  };
  signal.addEventListener("abort", () => channelV1.close(), { once: true });
}

// ../replicache/src/on-persist-channel.ts
function makeChannelName(replicacheName) {
  return `replicache-on-persist:${replicacheName}`;
}
function assertPersistInfo(value) {
  assertObject(value);
  assertString(value.clientGroupID);
  assertString(value.clientID);
}
function initOnPersistChannel(replicacheName, signal, handlePersist) {
  if (signal.aborted) {
    return () => void 0;
  }
  const channel = new bc(makeChannelName(replicacheName));
  channel.onmessage = (e) => {
    const { data } = e;
    assertPersistInfo(data);
    handlePersist({
      clientGroupID: data.clientGroupID,
      clientID: data.clientID
    });
  };
  signal.addEventListener("abort", () => channel.close(), { once: true });
  return (persistInfo) => {
    if (signal.aborted) {
      return;
    }
    channel.postMessage(persistInfo);
    handlePersist(persistInfo);
  };
}

// ../replicache/src/pending-mutations.ts
async function pendingMutationsForAPI(dagRead) {
  const mainHeadHash = await mustGetHeadHash(DEFAULT_HEAD_NAME, dagRead);
  const pending = await localMutationsDD31(mainHeadHash, dagRead);
  return pending.map((p) => ({
    id: p.meta.mutationID,
    name: p.meta.mutatorName,
    args: p.meta.mutatorArgsJSON,
    clientID: p.meta.clientID
  })).reverse();
}

// ../replicache/src/persist/client-gc.ts
var CLIENT_MAX_INACTIVE_TIME = 24 * 60 * 60 * 1e3;
var GC_INTERVAL = 5 * 60 * 1e3;
var latestGCUpdate;
function initClientGC(clientID, dagStore, clientMaxInactiveTime, gcInterval, onClientsDeleted, lc, signal) {
  initBgIntervalProcess(
    "ClientGC",
    () => {
      latestGCUpdate = gcClients(
        clientID,
        dagStore,
        clientMaxInactiveTime,
        onClientsDeleted
      );
      return latestGCUpdate;
    },
    () => gcInterval,
    lc,
    signal
  );
}
function gcClients(clientID, dagStore, clientMaxInactiveTime, onClientsDeleted) {
  return withWrite(dagStore, async (dagWrite) => {
    const now = Date.now();
    const clients = await getClients(dagWrite);
    const deletedClients = [];
    const newClients = /* @__PURE__ */ new Map();
    for (const [id, client] of clients) {
      if (id === clientID || now - client.heartbeatTimestampMs <= clientMaxInactiveTime) {
        newClients.set(id, client);
      } else {
        deletedClients.push(id);
      }
    }
    if (newClients.size === clients.size) {
      return clients;
    }
    await setClients(newClients, dagWrite);
    const { clientIDs, clientGroupIDs } = await addDeletedClients(
      dagWrite,
      deletedClients,
      // gcClients does not delete client groups
      []
    );
    onClientsDeleted(clientIDs, clientGroupIDs);
    return newClients;
  });
}

// ../replicache/src/persist/client-group-gc.ts
var GC_INTERVAL_MS = 5 * 60 * 1e3;
var latestGCUpdate2;
function initClientGroupGC(dagStore, enableMutationRecovery, onClientsDeleted, lc, signal) {
  initBgIntervalProcess(
    "ClientGroupGC",
    () => {
      latestGCUpdate2 = gcClientGroups(
        dagStore,
        enableMutationRecovery,
        onClientsDeleted
      );
      return latestGCUpdate2;
    },
    () => GC_INTERVAL_MS,
    lc,
    signal
  );
}
function gcClientGroups(dagStore, enableMutationRecovery, onClientsDeleted) {
  return withWrite(dagStore, async (tx) => {
    const clients = await getClients(tx);
    const clientGroupIDs = /* @__PURE__ */ new Set();
    for (const client of clients.values()) {
      clientGroupIDs.add(client.clientGroupID);
    }
    const clientGroups = /* @__PURE__ */ new Map();
    const removeClientGroups = /* @__PURE__ */ new Set();
    for (const [clientGroupID, clientGroup] of await getClientGroups(tx)) {
      if (clientGroupIDs.has(clientGroupID) || enableMutationRecovery && clientGroupHasPendingMutations(clientGroup)) {
        clientGroups.set(clientGroupID, clientGroup);
      } else {
        removeClientGroups.add(clientGroupID);
      }
    }
    await setClientGroups(clientGroups, tx);
    onClientsDeleted([], [...removeClientGroups].sort());
    return clientGroups;
  });
}

// ../replicache/src/persist/heartbeat.ts
var HEARTBEAT_INTERVAL = 60 * 1e3;
var latestHeartbeatUpdate;
function startHeartbeats(clientID, dagStore, onClientStateNotFound, heartbeatIntervalMs, lc, signal) {
  initBgIntervalProcess(
    "Heartbeat",
    async () => {
      latestHeartbeatUpdate = writeHeartbeat(clientID, dagStore);
      try {
        return await latestHeartbeatUpdate;
      } catch (e) {
        if (e instanceof ClientStateNotFoundError) {
          onClientStateNotFound();
          return;
        }
        throw e;
      }
    },
    () => heartbeatIntervalMs,
    lc,
    signal
  );
}
function writeHeartbeat(clientID, dagStore) {
  return withWrite(dagStore, async (dagWrite) => {
    const clients = await getClients(dagWrite);
    const client = clients.get(clientID);
    if (!client) {
      throw new ClientStateNotFoundError(clientID);
    }
    const newClient = {
      ...client,
      heartbeatTimestampMs: Date.now()
    };
    const newClients = new Map(clients).set(clientID, newClient);
    await setClients(newClients, dagWrite);
    return newClients;
  });
}

// ../replicache/src/persist/idb-databases-store-db-name.ts
var IDB_DATABASES_VERSION = 0;
var IDB_DATABASES_DB_NAME = "replicache-dbs-v" + IDB_DATABASES_VERSION;
var testNamespace = "";
function getIDBDatabasesDBName() {
  return testNamespace + IDB_DATABASES_DB_NAME;
}

// ../replicache/src/persist/idb-databases-store.ts
var DBS_KEY = "dbs";
var PROFILE_ID_KEY = "profileId";
function assertIndexedDBDatabaseRecord(value) {
  assertObject(value);
  for (const [name, db] of Object.entries(value)) {
    assertString(name);
    assertIndexedDBDatabase(db);
    assert(name === db.name);
  }
}
function assertIndexedDBDatabase(value) {
  assertObject(value);
  assertString(value.name);
  assertString(value.replicacheName);
  assertNumber(value.replicacheFormatVersion);
  assertString(value.schemaVersion);
  if (value.lastOpenedTimestampMS !== void 0) {
    assertNumber(value.lastOpenedTimestampMS);
  }
}
var IDBDatabasesStore = class {
  #kvStore;
  constructor(createKVStore) {
    this.#kvStore = createKVStore(getIDBDatabasesDBName());
  }
  putDatabase(db) {
    return this.#putDatabase({ ...db, lastOpenedTimestampMS: Date.now() });
  }
  putDatabaseForTesting(db) {
    return this.#putDatabase(db);
  }
  #putDatabase(db) {
    return withWrite(this.#kvStore, async (write) => {
      const oldDbRecord = await getDatabases(write);
      const dbRecord = {
        ...oldDbRecord,
        [db.name]: db
      };
      await write.put(DBS_KEY, dbRecord);
      return dbRecord;
    });
  }
  clearDatabases() {
    return withWrite(this.#kvStore, (write) => write.del(DBS_KEY));
  }
  deleteDatabases(names) {
    return withWrite(this.#kvStore, async (write) => {
      const oldDbRecord = await getDatabases(write);
      const dbRecord = {
        ...oldDbRecord
      };
      for (const name of names) {
        delete dbRecord[name];
      }
      await write.put(DBS_KEY, dbRecord);
    });
  }
  getDatabases() {
    return withRead(this.#kvStore, getDatabases);
  }
  close() {
    return this.#kvStore.close();
  }
  getProfileID() {
    return withWrite(this.#kvStore, async (write) => {
      let profileId = await write.get(PROFILE_ID_KEY);
      if (profileId === void 0) {
        profileId = `p${makeClientID()}`;
        await write.put(PROFILE_ID_KEY, profileId);
      }
      assertString(profileId);
      return profileId;
    });
  }
};
async function getDatabases(read) {
  let dbRecord = await read.get(DBS_KEY);
  if (!dbRecord) {
    dbRecord = deepFreeze({});
  }
  assertIndexedDBDatabaseRecord(dbRecord);
  return dbRecord;
}

// ../replicache/src/dag/visitor.ts
var Visitor = class {
  #seen = /* @__PURE__ */ new Set();
  #dagRead;
  constructor(dagRead) {
    this.#dagRead = dagRead;
  }
  async visit(h) {
    if (this.#seen.has(h)) {
      return;
    }
    this.#seen.add(h);
    const chunk = await this.#dagRead.mustGetChunk(h);
    await this.visitChunk(chunk);
  }
  async visitChunk(chunk) {
    await Promise.all(chunk.meta.map((ref) => this.visit(ref)));
  }
};

// ../replicache/src/persist/gather-mem-only-visitor.ts
var GatherMemoryOnlyVisitor = class extends Visitor {
  #gatheredChunks = /* @__PURE__ */ new Map();
  #lazyRead;
  constructor(dagRead) {
    super(dagRead);
    this.#lazyRead = dagRead;
  }
  get gatheredChunks() {
    return this.#gatheredChunks;
  }
  visit(h) {
    if (!this.#lazyRead.isMemOnlyChunkHash(h)) {
      return promiseVoid;
    }
    return super.visit(h);
  }
  visitChunk(chunk) {
    this.#gatheredChunks.set(chunk.hash, chunk);
    return super.visitChunk(chunk);
  }
};

// ../replicache/src/persist/persist.ts
async function persistDD31(lc, clientID, memdag, perdag, mutators, closed, formatVersion, getZeroData, onGatherMemOnlyChunksForTest = () => Promise.resolve()) {
  if (closed()) {
    return;
  }
  const [perdagLMID, perdagBaseSnapshot, mainClientGroupID] = await withRead(
    perdag,
    async (perdagRead) => {
      await assertHasClientState(clientID, perdagRead);
      const mainClientGroupID2 = await getClientGroupIDForClient(
        clientID,
        perdagRead
      );
      assert(
        mainClientGroupID2,
        `No main client group for clientID: ${clientID}`
      );
      const [, perdagMainClientGroupHeadCommit] = await getClientGroupInfo(
        perdagRead,
        mainClientGroupID2
      );
      const perdagLMID2 = await perdagMainClientGroupHeadCommit.getMutationID(
        clientID,
        perdagRead
      );
      const perdagBaseSnapshot2 = await baseSnapshotFromCommit(
        perdagMainClientGroupHeadCommit,
        perdagRead
      );
      assertSnapshotCommitDD31(perdagBaseSnapshot2);
      return [perdagLMID2, perdagBaseSnapshot2, mainClientGroupID2];
    }
  );
  if (closed()) {
    return;
  }
  const [newMemdagMutations, memdagBaseSnapshot, gatheredChunks] = await withRead(memdag, async (memdagRead) => {
    const memdagHeadCommit = await commitFromHead(
      DEFAULT_HEAD_NAME,
      memdagRead
    );
    const newMutations = await localMutationsGreaterThan(
      memdagHeadCommit,
      { [clientID]: perdagLMID || 0 },
      memdagRead
    );
    const memdagBaseSnapshot2 = await baseSnapshotFromCommit(
      memdagHeadCommit,
      memdagRead
    );
    assertSnapshotCommitDD31(memdagBaseSnapshot2);
    let gatheredChunks2;
    if (compareCookiesForSnapshots(memdagBaseSnapshot2, perdagBaseSnapshot) > 0) {
      await onGatherMemOnlyChunksForTest();
      const memdagBaseSnapshotHash = memdagBaseSnapshot2.chunk.hash;
      const visitor = new GatherMemoryOnlyVisitor(memdagRead);
      await visitor.visit(memdagBaseSnapshotHash);
      gatheredChunks2 = visitor.gatheredChunks;
    }
    return [newMutations, memdagBaseSnapshot2, gatheredChunks2];
  });
  if (closed()) {
    return;
  }
  let memdagBaseSnapshotPersisted = false;
  const zeroDataForMemdagBaseSnapshot = getZeroData && await getZeroData(memdagBaseSnapshot.chunk.hash);
  await withWrite(perdag, async (perdagWrite) => {
    const [mainClientGroup, latestPerdagMainClientGroupHeadCommit] = await getClientGroupInfo(perdagWrite, mainClientGroupID);
    let newMainClientGroupHeadHash = latestPerdagMainClientGroupHeadCommit.chunk.hash;
    let mutationIDs = {
      ...mainClientGroup.mutationIDs
    };
    let { lastServerAckdMutationIDs } = mainClientGroup;
    if (gatheredChunks) {
      const client = await mustGetClient(clientID, perdagWrite);
      assertClientV6(client);
      const latestPerdagBaseSnapshot = await baseSnapshotFromCommit(
        latestPerdagMainClientGroupHeadCommit,
        perdagWrite
      );
      assertSnapshotCommitDD31(latestPerdagBaseSnapshot);
      if (compareCookiesForSnapshots(
        memdagBaseSnapshot,
        latestPerdagBaseSnapshot
      ) > 0) {
        memdagBaseSnapshotPersisted = true;
        await Promise.all(
          Array.from(gatheredChunks.values(), (c) => perdagWrite.putChunk(c))
        );
        await setClient(
          clientID,
          {
            ...client,
            persistHash: memdagBaseSnapshot.chunk.hash
          },
          perdagWrite
        );
        newMainClientGroupHeadHash = memdagBaseSnapshot.chunk.hash;
        const mainClientGroupLocalMutations = await localMutationsDD31(
          mainClientGroup.headHash,
          perdagWrite
        );
        lastServerAckdMutationIDs = memdagBaseSnapshot.meta.lastMutationIDs;
        mutationIDs = { ...lastServerAckdMutationIDs };
        newMainClientGroupHeadHash = await rebase(
          mainClientGroupLocalMutations,
          newMainClientGroupHeadHash,
          perdagWrite,
          mutators,
          mutationIDs,
          lc,
          formatVersion,
          zeroDataForMemdagBaseSnapshot
        );
      }
    }
    let zeroDataForPerdagHeadCommit;
    if (!memdagBaseSnapshotPersisted) {
      zeroDataForPerdagHeadCommit = getZeroData && await getZeroData(newMainClientGroupHeadHash, {
        openLazySourceRead: perdagWrite
      });
    }
    newMainClientGroupHeadHash = await rebase(
      newMemdagMutations,
      newMainClientGroupHeadHash,
      perdagWrite,
      mutators,
      mutationIDs,
      lc,
      formatVersion,
      zeroDataForPerdagHeadCommit ?? zeroDataForMemdagBaseSnapshot
    );
    const newMainClientGroup = {
      ...mainClientGroup,
      headHash: newMainClientGroupHeadHash,
      mutationIDs,
      lastServerAckdMutationIDs
    };
    await setClientGroup(mainClientGroupID, newMainClientGroup, perdagWrite);
  });
  if (gatheredChunks && memdagBaseSnapshotPersisted) {
    await withWrite(
      memdag,
      (memdagWrite) => memdagWrite.chunksPersisted([...gatheredChunks.keys()])
    );
  }
}
async function getClientGroupInfo(perdagRead, clientGroupID) {
  const clientGroup = await getClientGroup(clientGroupID, perdagRead);
  assert(clientGroup, `No client group for clientGroupID: ${clientGroupID}`);
  return [clientGroup, await commitFromHash(clientGroup.headHash, perdagRead)];
}
async function rebase(mutations, basis, write, mutators, mutationIDs, lc, formatVersion, zeroData2) {
  for (let i = mutations.length - 1; i >= 0; i--) {
    const mutationCommit = mutations[i];
    const { meta } = mutationCommit;
    const newMainHead = await commitFromHash(basis, write);
    if (await mutationCommit.getMutationID(meta.clientID, write) > await newMainHead.getMutationID(meta.clientID, write)) {
      mutationIDs[meta.clientID] = meta.mutationID;
      basis = (await rebaseMutationAndPutCommit(
        mutationCommit,
        write,
        basis,
        mutators,
        lc,
        meta.clientID,
        formatVersion,
        zeroData2
      )).chunk.hash;
    }
  }
  return basis;
}

// ../replicache/src/persist/gather-not-cached-visitor.ts
var GatherNotCachedVisitor = class extends Visitor {
  #gatheredChunks = /* @__PURE__ */ new Map();
  #gatheredChunksTotalSize = 0;
  #lazyStore;
  #gatherSizeLimit;
  #getSizeOfChunk;
  constructor(dagRead, lazyStore, gatherSizeLimit, getSizeOfChunk = getSizeOfValue) {
    super(dagRead);
    this.#lazyStore = lazyStore;
    this.#gatherSizeLimit = gatherSizeLimit;
    this.#getSizeOfChunk = getSizeOfChunk;
  }
  get gatheredChunks() {
    return this.#gatheredChunks;
  }
  visit(h) {
    if (this.#gatheredChunksTotalSize >= this.#gatherSizeLimit || this.#lazyStore.isCached(h)) {
      return promiseVoid;
    }
    return super.visit(h);
  }
  visitChunk(chunk) {
    if (this.#gatheredChunksTotalSize < this.#gatherSizeLimit) {
      const size = this.#getSizeOfChunk(chunk);
      this.#gatheredChunks.set(chunk.hash, { chunk, size });
      this.#gatheredChunksTotalSize += size;
    }
    return super.visitChunk(chunk);
  }
};

// ../replicache/src/persist/refresh.ts
var GATHER_SIZE_LIMIT = 5 * 2 ** 20;
var DELAY_MS = 300;
async function refresh(lc, memdag, perdag, clientID, mutators, diffConfig, closed, formatVersion, zero) {
  if (closed()) {
    return;
  }
  const memdagBaseSnapshot = await withRead(
    memdag,
    (memdagRead) => baseSnapshotFromHead(DEFAULT_HEAD_NAME, memdagRead)
  );
  assertSnapshotCommitDD31(memdagBaseSnapshot);
  const result = await memdag.withSuspendedSourceCacheEvictsAndDeletes(async () => {
    const perdagWriteResult = await withWrite(
      perdag,
      async (perdagWrite) => {
        const clientGroup = await getClientGroupForClient(
          clientID,
          perdagWrite
        );
        if (!clientGroup) {
          throw new ClientStateNotFoundError(clientID);
        }
        const perdagClientGroupHeadHash2 = clientGroup.headHash;
        const perdagClientGroupHeadCommit = await commitFromHash(
          perdagClientGroupHeadHash2,
          perdagWrite
        );
        const perdagLmid2 = await perdagClientGroupHeadCommit.getMutationID(
          clientID,
          perdagWrite
        );
        const client = await mustGetClient(clientID, perdagWrite);
        assertClientV6(client);
        const perdagClientGroupBaseSnapshot2 = await baseSnapshotFromHash(
          perdagClientGroupHeadHash2,
          perdagWrite
        );
        assertSnapshotCommitDD31(perdagClientGroupBaseSnapshot2);
        if (shouldAbortRefresh(
          memdagBaseSnapshot,
          perdagClientGroupBaseSnapshot2,
          perdagClientGroupHeadHash2
        )) {
          return void 0;
        }
        const visitor = new GatherNotCachedVisitor(
          perdagWrite,
          memdag,
          GATHER_SIZE_LIMIT
        );
        await visitor.visit(perdagClientGroupHeadHash2);
        const { gatheredChunks: gatheredChunks2 } = visitor;
        const refreshHashesSet = new Set(client.refreshHashes);
        refreshHashesSet.add(perdagClientGroupHeadHash2);
        const newClient = {
          ...client,
          refreshHashes: [...refreshHashesSet]
        };
        await setClient(clientID, newClient, perdagWrite);
        return [
          perdagClientGroupHeadHash2,
          perdagClientGroupBaseSnapshot2,
          perdagLmid2,
          gatheredChunks2,
          client.refreshHashes
        ];
      }
    );
    if (closed() || !perdagWriteResult) {
      return {
        type: "aborted"
      };
    }
    await sleep(DELAY_MS);
    if (closed()) {
      return {
        type: "aborted"
      };
    }
    const [
      perdagClientGroupHeadHash,
      perdagClientGroupBaseSnapshot,
      perdagLmid,
      gatheredChunks,
      refreshHashesForRevert
    ] = perdagWriteResult;
    return withWrite(memdag, async (memdagWrite) => {
      const memdagHeadCommit = await commitFromHead(
        DEFAULT_HEAD_NAME,
        memdagWrite
      );
      const memdagBaseSnapshot2 = await baseSnapshotFromCommit(
        memdagHeadCommit,
        memdagWrite
      );
      assertSnapshotCommitDD31(memdagBaseSnapshot2);
      if (shouldAbortRefresh(
        memdagBaseSnapshot2,
        perdagClientGroupBaseSnapshot,
        perdagClientGroupHeadHash
      )) {
        return {
          type: "aborted",
          refreshHashesForRevert
        };
      }
      const newMemdagMutations = await localMutationsGreaterThan(
        memdagHeadCommit,
        { [clientID]: perdagLmid },
        memdagWrite
      );
      const ps = [];
      for (const { chunk, size } of gatheredChunks.values()) {
        ps.push(memdagWrite.putChunk(chunk, size));
      }
      await Promise.all(ps);
      let newMemdagHeadHash = perdagClientGroupHeadHash;
      if (newMemdagMutations.length > 0) {
        const zeroData2 = await zero?.getTxData?.(newMemdagHeadHash, {
          openLazyRead: memdagWrite
        });
        for (let i = newMemdagMutations.length - 1; i >= 0; i--) {
          newMemdagHeadHash = (await rebaseMutationAndPutCommit(
            newMemdagMutations[i],
            memdagWrite,
            newMemdagHeadHash,
            mutators,
            lc,
            newMemdagMutations[i].meta.clientID,
            formatVersion,
            zeroData2
          )).chunk.hash;
        }
      }
      const newMemdagHeadCommit = await commitFromHash(
        newMemdagHeadHash,
        memdagWrite
      );
      const diffs = await diffCommits(
        memdagHeadCommit,
        newMemdagHeadCommit,
        memdagWrite,
        diffConfig,
        formatVersion
      );
      await memdagWrite.setHead(DEFAULT_HEAD_NAME, newMemdagHeadHash);
      return {
        type: "complete",
        diffs,
        oldHead: memdagHeadCommit.chunk.hash,
        newHead: newMemdagHeadHash,
        newPerdagClientHeadHash: perdagClientGroupHeadHash
      };
    });
  });
  if (closed()) {
    return;
  }
  const setRefreshHashes = (refreshHashes) => withWrite(perdag, async (perdagWrite) => {
    const client = await mustGetClient(clientID, perdagWrite);
    const newClient = {
      ...client,
      refreshHashes
    };
    await setClient(clientID, newClient, perdagWrite);
  });
  if (result.type === "aborted") {
    if (result.refreshHashesForRevert) {
      await setRefreshHashes(result.refreshHashesForRevert);
    }
    return void 0;
  }
  zero?.advance(result.oldHead, result.newHead, result.diffs.get("") ?? []);
  await setRefreshHashes([result.newPerdagClientHeadHash]);
  return {
    oldHead: result.oldHead,
    newHead: result.newHead,
    diffs: result.diffs
  };
}
function shouldAbortRefresh(memdagBaseSnapshot, perdagClientGroupBaseSnapshot, perdagClientGroupHeadHash) {
  const baseSnapshotCookieCompareResult = compareCookiesForSnapshots(
    memdagBaseSnapshot,
    perdagClientGroupBaseSnapshot
  );
  return baseSnapshotCookieCompareResult > 0 || baseSnapshotCookieCompareResult === 0 && perdagClientGroupHeadHash === perdagClientGroupBaseSnapshot.chunk.hash;
}

// ../replicache/src/process-scheduler.ts
import { resolver as resolver5 } from "@rocicorp/resolver";

// ../replicache/src/request-idle.ts
function requestIdle(timeout) {
  return new Promise((resolve) => {
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(() => resolve(), { timeout });
    } else {
      setTimeout(() => resolve(), timeout);
    }
  });
}

// ../replicache/src/process-scheduler.ts
var ProcessScheduler = class {
  #process;
  #idleTimeoutMs;
  #throttleMs;
  #abortSignal;
  #requestIdle;
  #scheduledResolver = void 0;
  #runResolver = void 0;
  #runPromise = Promise.resolve();
  #throttlePromise = Promise.resolve();
  /**
   * Supports scheduling a `process` to be run with certain constraints.
   *  - Process runs are never concurrent.
   *  - Multiple calls to schedule will be fulfilled by a single process
   *    run started after the call to schedule.  A call is never fulfilled by an
   *    already running process run.  This can be thought of as debouncing.
   *  - Process runs are throttled so that the process runs at most once every
   *    `throttleMs`.
   *  - Process runs try to run during an idle period, but will delay at most
   *    `idleTimeoutMs`.
   *  - Scheduled runs which have not completed when `abortSignal` is aborted
   *    will reject with an `AbortError`.
   */
  constructor(process2, idleTimeoutMs, throttleMs, abortSignal, requestIdle2 = requestIdle) {
    this.#process = process2;
    this.#idleTimeoutMs = idleTimeoutMs;
    this.#throttleMs = throttleMs;
    this.#abortSignal = abortSignal;
    this.#requestIdle = requestIdle2;
    this.#abortSignal.addEventListener(
      "abort",
      () => {
        const abortError = new AbortError("Aborted");
        this.#runResolver?.reject(abortError);
        this.#scheduledResolver?.reject(abortError);
        this.#runResolver = void 0;
        this.#scheduledResolver = void 0;
      },
      { once: true }
    );
  }
  schedule() {
    if (this.#abortSignal.aborted) {
      return Promise.reject(new AbortError("Aborted"));
    }
    if (this.#scheduledResolver) {
      return this.#scheduledResolver.promise;
    }
    this.#scheduledResolver = resolver5();
    void this.#scheduleInternal();
    return this.#scheduledResolver.promise;
  }
  async #scheduleInternal() {
    try {
      await this.#runPromise;
    } catch (e) {
    }
    await this.#throttlePromise;
    if (!this.#scheduledResolver) {
      return;
    }
    await this.#requestIdle(this.#idleTimeoutMs);
    if (!this.#scheduledResolver) {
      return;
    }
    this.#throttlePromise = throttle(this.#throttleMs, this.#abortSignal);
    this.#runResolver = this.#scheduledResolver;
    this.#scheduledResolver = void 0;
    try {
      this.#runPromise = this.#process();
      await this.#runPromise;
      this.#runResolver?.resolve();
    } catch (e) {
      this.#runResolver?.reject(e);
    }
    this.#runResolver = void 0;
  }
};
async function throttle(timeMs, abortSignal) {
  try {
    await sleep(timeMs, abortSignal);
  } catch (e) {
    assert(e instanceof AbortError);
  }
}

// ../replicache/src/set-interval-with-signal.ts
function setIntervalWithSignal(fn, ms, signal) {
  if (!signal.aborted) {
    const interval = setInterval(fn, ms);
    signal.addEventListener("abort", () => {
      clearInterval(interval);
    });
  }
}

// ../replicache/src/subscriptions.ts
import { compareUTF8, greaterThan as greaterThan3, lessThan, lessThanEq } from "compare-utf8";

// ../replicache/src/invoke-kind-enum.ts
var InitialRun = 0;
var Regular = 1;

// ../replicache/src/subscriptions.ts
var emptySet = /* @__PURE__ */ new Set();
var unitializedLastValue = Symbol();
var SubscriptionImpl = class {
  #body;
  #onData;
  #lastValue = unitializedLastValue;
  #keys = emptySet;
  #scans = [];
  onError;
  onDone;
  #isEqual;
  constructor(body, onData, onError, onDone, isEqual = deepEqual) {
    this.#body = body;
    this.#onData = onData;
    this.onError = onError;
    this.onDone = onDone;
    this.#isEqual = isEqual;
  }
  hasIndexSubscription(indexName) {
    for (const scan2 of this.#scans) {
      if (scan2.options.indexName === indexName) {
        return true;
      }
    }
    return false;
  }
  invoke(tx, _kind, _diffs) {
    return this.#body(tx);
  }
  matches(diffs) {
    for (const [indexName, diff3] of diffs) {
      if (diffMatchesSubscription(this.#keys, this.#scans, indexName, diff3)) {
        return true;
      }
    }
    return false;
  }
  updateDeps(keys, scans) {
    this.#keys = keys;
    this.#scans = scans;
  }
  onData(result) {
    if (this.#lastValue === unitializedLastValue || !this.#isEqual(this.#lastValue, result)) {
      this.#lastValue = result;
      this.#onData(result);
    }
  }
};
var WatchSubscription = class {
  #callback;
  #prefix;
  #indexName;
  #initialValuesInFirstDiff;
  onError = void 0;
  onDone = void 0;
  constructor(callback, options) {
    this.#callback = callback;
    this.#prefix = options?.prefix ?? "";
    this.#indexName = options?.indexName;
    this.#initialValuesInFirstDiff = options?.initialValuesInFirstDiff ?? false;
  }
  hasIndexSubscription(indexName) {
    return this.#indexName === indexName;
  }
  onData(result) {
    if (result !== void 0) {
      this.#callback(result);
    }
  }
  invoke(tx, kind, diffs) {
    const invoke = async (indexName, prefix, compareKey, convertInternalDiff) => {
      let diff3;
      if (kind === InitialRun) {
        if (!this.#initialValuesInFirstDiff) {
          return void 0;
        }
        assert(diffs === void 0);
        const newDiff2 = [];
        for await (const entry of tx.scan({ prefix, indexName }).entries()) {
          newDiff2.push({
            op: "add",
            key: entry[0],
            newValue: entry[1]
          });
        }
        diff3 = newDiff2;
      } else {
        assert(diffs);
        const maybeDiff = diffs.get(indexName ?? "") ?? [];
        diff3 = convertInternalDiff(maybeDiff);
      }
      const newDiff = [];
      const { length } = diff3;
      for (let i = diffBinarySearch(diff3, prefix, compareKey); i < length; i++) {
        if (compareKey(diff3[i]).startsWith(prefix)) {
          newDiff.push(diff3[i]);
        } else {
          break;
        }
      }
      return kind === InitialRun || newDiff.length > 0 ? newDiff : void 0;
    };
    if (this.#indexName) {
      return invoke(
        this.#indexName,
        this.#prefix,
        (diff3) => diff3.key[0],
        (internalDiff) => convertDiffValues(internalDiff, decodeIndexKey)
      );
    }
    return invoke(
      void 0,
      this.#prefix,
      (diff3) => diff3.key,
      (internalDiff) => convertDiffValues(internalDiff, (k) => k)
    );
  }
  matches(diffs) {
    const diff3 = diffs.get(this.#indexName ?? "");
    if (diff3 === void 0) {
      return false;
    }
    return watcherMatchesDiff(diff3, this.#prefix, this.#indexName);
  }
  updateDeps(_keys, _scans) {
  }
};
function convertDiffValues(diff3, convertKey) {
  return diff3.map((op) => {
    const key = convertKey(op.key);
    switch (op.op) {
      case "add":
        return {
          op: "add",
          key,
          newValue: op.newValue
        };
      case "change":
        return {
          op: "change",
          key,
          oldValue: op.oldValue,
          newValue: op.newValue
        };
      case "del":
        return {
          op: "del",
          key,
          oldValue: op.oldValue
        };
    }
  });
}
var SubscriptionsManagerImpl = class {
  #subscriptions = /* @__PURE__ */ new Set();
  #pendingSubscriptions = /* @__PURE__ */ new Set();
  #queryInternal;
  #lc;
  hasPendingSubscriptionRuns = false;
  #signal;
  constructor(queryInternal, lc, signal) {
    this.#queryInternal = queryInternal;
    this.#lc = lc;
    this.#signal = signal;
  }
  add(subscription) {
    this.#subscriptions.add(subscription);
    void this.#scheduleInitialSubscriptionRun(
      subscription
    );
    return () => this.#subscriptions.delete(subscription);
  }
  clear() {
    for (const subscription of this.#subscriptions) {
      subscription.onDone?.();
    }
    this.#subscriptions.clear();
  }
  fire(diffs) {
    const subscriptions = subscriptionsForDiffs(this.#subscriptions, diffs);
    return this.#fireSubscriptions(subscriptions, Regular, diffs);
  }
  async #fireSubscriptions(subscriptions, kind, diffs) {
    if (this.#signal.aborted) {
      return;
    }
    const subs = [...subscriptions];
    if (subs.length === 0) {
      return;
    }
    const results = await this.#queryInternal(
      (tx) => Promise.allSettled(
        subs.map(async (s) => {
          const stx = new SubscriptionTransactionWrapper(tx);
          try {
            return await s.invoke(stx, kind, diffs);
          } finally {
            s.updateDeps(stx.keys, stx.scans);
          }
        })
      )
    );
    this.callCallbacks(subs, results);
  }
  // Public method so that ZQL can wrap it in a transaction.
  callCallbacks(subs, results) {
    for (let i = 0; i < subs.length; i++) {
      const s = subs[i];
      const result = results[i];
      if (result.status === "fulfilled") {
        s.onData(result.value);
      } else {
        if (s.onError) {
          s.onError(result.reason);
        } else {
          this.#lc.error?.("Error in subscription body:", result.reason);
        }
      }
    }
  }
  async #scheduleInitialSubscriptionRun(s) {
    this.#pendingSubscriptions.add(s);
    if (!this.hasPendingSubscriptionRuns) {
      this.hasPendingSubscriptionRuns = true;
      await Promise.resolve();
      this.hasPendingSubscriptionRuns = false;
      const subscriptions = [...this.#pendingSubscriptions];
      this.#pendingSubscriptions.clear();
      await this.#fireSubscriptions(
        subscriptions,
        InitialRun,
        void 0
      );
    }
  }
  shouldComputeDiffs() {
    return this.#subscriptions.size > 0;
  }
  shouldComputeDiffsForIndex(indexName) {
    for (const s of this.#subscriptions) {
      if (s.hasIndexSubscription(indexName)) {
        return true;
      }
    }
    return false;
  }
};
function diffMatchesSubscription(keys, scans, indexName, diff3) {
  if (indexName === "") {
    for (const diffEntry of diff3) {
      if (keys.has(diffEntry.key)) {
        return true;
      }
    }
  }
  for (const scanInfo of scans) {
    if (scanInfoMatchesDiff(scanInfo, indexName, diff3)) {
      return true;
    }
  }
  return false;
}
function scanInfoMatchesDiff(scanInfo, changeIndexName, diff3) {
  for (const diffEntry of diff3) {
    if (scanInfoMatchesKey(scanInfo, changeIndexName, diffEntry.key)) {
      return true;
    }
  }
  return false;
}
function scanInfoMatchesKey(scanInfo, changeIndexName, changedKey) {
  const {
    indexName = "",
    limit,
    prefix,
    startKey,
    startExclusive,
    startSecondaryKey
  } = scanInfo.options;
  if (changeIndexName !== indexName) {
    return false;
  }
  if (!indexName) {
    if (limit !== void 0 && limit <= 0) {
      return false;
    }
    if (!prefix && !startKey) {
      return true;
    }
    if (prefix && (!changedKey.startsWith(prefix) || isKeyPastInclusiveLimit(scanInfo, changedKey))) {
      return false;
    }
    if (startKey && (startExclusive && lessThanEq(changedKey, startKey) || lessThan(changedKey, startKey) || isKeyPastInclusiveLimit(scanInfo, changedKey))) {
      return false;
    }
    return true;
  }
  if (!prefix && !startKey && !startSecondaryKey) {
    return true;
  }
  const [changedKeySecondary, changedKeyPrimary] = decodeIndexKey(changedKey);
  if (prefix) {
    if (!changedKeySecondary.startsWith(prefix)) {
      return false;
    }
  }
  if (startSecondaryKey && (startExclusive && lessThanEq(changedKeySecondary, startSecondaryKey) || lessThan(changedKeySecondary, startSecondaryKey))) {
    return false;
  }
  if (startKey && (startExclusive && lessThanEq(changedKeyPrimary, startKey) || lessThan(changedKeyPrimary, startKey))) {
    return false;
  }
  return true;
}
function isKeyPastInclusiveLimit(scanInfo, changedKey) {
  const { inclusiveLimitKey } = scanInfo;
  return scanInfo.options.limit !== void 0 && inclusiveLimitKey !== void 0 && greaterThan3(changedKey, inclusiveLimitKey);
}
function* subscriptionsForDiffs(subscriptions, diffs) {
  for (const subscription of subscriptions) {
    if (subscription.matches(diffs)) {
      yield subscription;
    }
  }
}
function watcherMatchesDiff(diff3, prefix, indexName) {
  if (prefix === "") {
    return true;
  }
  const compareKey = indexName ? (diffOp) => decodeIndexKey(diffOp.key)[0] : (diffOp) => diffOp.key;
  const i = diffBinarySearch(diff3, prefix, compareKey);
  return i < diff3.length && compareKey(diff3[i]).startsWith(prefix);
}
function diffBinarySearch(diff3, prefix, compareKey) {
  return binarySearch(
    diff3.length,
    (i) => compareUTF8(prefix, compareKey(diff3[i]))
  );
}

// ../replicache/src/sync/request-id.ts
var sessionID = "";
function getSessionID() {
  if (sessionID === "") {
    const buf = new Uint8Array(4);
    getNonCryptoRandomValues(buf);
    sessionID = Array.from(buf, (x) => x.toString(16)).join("");
  }
  return sessionID;
}
var REQUEST_COUNTERS = /* @__PURE__ */ new Map();
function newRequestID(clientID) {
  const counter = REQUEST_COUNTERS.get(clientID) ?? 0;
  REQUEST_COUNTERS.set(clientID, counter + 1);
  return `${clientID}-${getSessionID()}-${counter}`;
}

// ../replicache/src/version.ts
var version = "15.2.1";

// ../replicache/src/replicache-impl.ts
var MAX_REAUTH_TRIES = 8;
var PERSIST_IDLE_TIMEOUT_MS = 1e3;
var REFRESH_IDLE_TIMEOUT_MS = 1e3;
var PERSIST_THROTTLE_MS = 500;
var REFRESH_THROTTLE_MS = 500;
var LAZY_STORE_SOURCE_CHUNK_CACHE_SIZE_LIMIT = 100 * 2 ** 20;
var RECOVER_MUTATIONS_INTERVAL_MS = 5 * 60 * 1e3;
var noop2 = () => {
};
var updateNeededReasonNewClientGroup = {
  type: "NewClientGroup"
};
var ReplicacheImpl = class {
  /** The URL to use when doing a pull request. */
  pullURL;
  /** The URL to use when doing a push request. */
  pushURL;
  /** The authorization token used when doing a push request. */
  #auth;
  /** The name of the Replicache database. Populated by {@link ReplicacheOptions#name}. */
  name;
  #subscriptions;
  #mutationRecovery;
  /**
   * Client groups gets disabled when the server does not know about it.
   * A disabled client group prevents the client from pushing and pulling.
   */
  isClientGroupDisabled = false;
  #kvStoreProvider;
  lastMutationID = 0;
  /**
   * This is the name Replicache uses for the IndexedDB database where data is
   * stored.
   */
  get idbName() {
    return makeIDBName(this.name, this.schemaVersion);
  }
  set auth(auth) {
    if (this.#zero) {
      this.#zero.auth = auth;
    }
    this.#auth = auth;
  }
  get auth() {
    return this.#auth;
  }
  /** The schema version of the data understood by this application. */
  schemaVersion;
  get #idbDatabase() {
    return {
      name: this.idbName,
      replicacheName: this.name,
      replicacheFormatVersion: Latest,
      schemaVersion: this.schemaVersion
    };
  }
  #closed = false;
  #online = true;
  #clientID = makeClientID();
  #ready;
  #profileIDPromise;
  #clientGroupIDPromise;
  #mutatorRegistry = {};
  /**
   * The mutators that was registered in the constructor.
   */
  mutate;
  // Number of pushes/pulls at the moment.
  #pushCounter = 0;
  #pullCounter = 0;
  #pullConnectionLoop;
  #pushConnectionLoop;
  /**
   * The duration between each periodic {@link pull}. Setting this to `null`
   * disables periodic pull completely. Pull will still happen if you call
   * {@link pull} manually.
   */
  pullInterval;
  /**
   * The delay between when a change is made to Replicache and when Replicache
   * attempts to push that change.
   */
  pushDelay;
  #requestOptions;
  /**
   * The function to use to pull data from the server.
   */
  puller;
  /**
   * The function to use to push data to the server.
   */
  pusher;
  memdag;
  perdag;
  #idbDatabases;
  #lc;
  #zero;
  #closeAbortController = new AbortController();
  #persistLock = new Lock();
  #enableScheduledPersist;
  #enableScheduledRefresh;
  #enablePullAndPushInOpen;
  #persistScheduler = new ProcessScheduler(
    () => this.persist(),
    PERSIST_IDLE_TIMEOUT_MS,
    PERSIST_THROTTLE_MS,
    this.#closeAbortController.signal
  );
  #onPersist;
  #refreshScheduler = new ProcessScheduler(
    () => this.refresh(),
    REFRESH_IDLE_TIMEOUT_MS,
    REFRESH_THROTTLE_MS,
    this.#closeAbortController.signal
  );
  /**
   * The options used to control the {@link pull} and push request behavior. This
   * object is live so changes to it will affect the next pull or push call.
   */
  get requestOptions() {
    return this.#requestOptions;
  }
  /**
   * `onSync(true)` is called when Replicache transitions from no push or pull
   * happening to at least one happening. `onSync(false)` is called in the
   * opposite case: when Replicache transitions from at least one push or pull
   * happening to none happening.
   *
   * This can be used in a React like app by doing something like the following:
   *
   * ```js
   * const [syncing, setSyncing] = useState(false);
   * useEffect(() => {
   *   rep.onSync = setSyncing;
   * }, [rep]);
   * ```
   */
  onSync = null;
  /**
   * `onClientStateNotFound` is called when the persistent client has been
   * garbage collected. This can happen if the client has no pending mutations
   * and has not been used for a while.
   *
   * The default behavior is to reload the page (using `location.reload()`). Set
   * this to `null` or provide your own function to prevent the page from
   * reloading automatically.
   */
  onClientStateNotFound = reload;
  /**
   * `onUpdateNeeded` is called when a code update is needed.
   *
   * A code update can be needed because:
   * - the server no longer supports the {@link pushVersion},
   *   {@link pullVersion} or {@link schemaVersion} of the current code.
   * - a new Replicache client has created a new client group, because its code
   *   has different mutators, indexes, schema version and/or format version
   *   from this Replicache client. This is likely due to the new client having
   *   newer code. A code update is needed to be able to locally sync with this
   *   new Replicache client (i.e. to sync while offline, the clients can still
   *   sync with each other via the server).
   *
   * The default behavior is to reload the page (using `location.reload()`). Set
   * this to `null` or provide your own function to prevent the page from
   * reloading automatically. You may want to provide your own function to
   * display a toast to inform the end user there is a new version of your app
   * available and prompting them to refresh.
   */
  onUpdateNeeded = reload;
  /**
   * This gets called when we get an HTTP unauthorized (401) response from the
   * push or pull endpoint. Set this to a function that will ask your user to
   * reauthenticate.
   */
  getAuth = null;
  // These three are used for testing
  onPushInvoked = () => void 0;
  onBeginPull = () => void 0;
  onRecoverMutations = (r) => r;
  constructor(options, implOptions = {}) {
    validateOptions(options);
    const {
      name,
      logLevel = "info",
      logSinks = [consoleLogSink2],
      pullURL = "",
      auth,
      pushDelay = 10,
      pushURL = "",
      schemaVersion = "",
      pullInterval = 6e4,
      mutators = {},
      requestOptions = {},
      puller,
      pusher,
      indexes = {},
      clientMaxAgeMs = CLIENT_MAX_INACTIVE_TIME
    } = options;
    const {
      enableMutationRecovery = true,
      enableScheduledPersist = true,
      enableScheduledRefresh = true,
      enablePullAndPushInOpen = true,
      enableClientGroupForking = true,
      onClientsDeleted = () => {
      }
    } = implOptions;
    this.#zero = implOptions.zero;
    this.#auth = auth ?? "";
    this.pullURL = pullURL;
    this.pushURL = pushURL;
    this.name = name;
    this.schemaVersion = schemaVersion;
    this.pullInterval = pullInterval;
    this.pushDelay = pushDelay;
    this.puller = puller ?? getDefaultPuller(this);
    this.pusher = pusher ?? getDefaultPusher(this);
    this.#enableScheduledPersist = enableScheduledPersist;
    this.#enableScheduledRefresh = enableScheduledRefresh;
    this.#enablePullAndPushInOpen = enablePullAndPushInOpen;
    this.#lc = createLogContext(logLevel, logSinks, { name });
    this.#lc.debug?.("Constructing Replicache", {
      name,
      "replicache version": version
    });
    this.#subscriptions = new SubscriptionsManagerImpl(
      this.#queryInternal,
      this.#lc,
      this.#closeAbortController.signal
    );
    const kvStoreProvider = getKVStoreProvider(this.#lc, options.kvStore);
    this.#kvStoreProvider = kvStoreProvider;
    const perKVStore = kvStoreProvider.create(this.idbName);
    this.#idbDatabases = new IDBDatabasesStore(kvStoreProvider.create);
    this.perdag = new StoreImpl(perKVStore, newRandomHash, assertHash);
    this.memdag = new LazyStore(
      this.perdag,
      LAZY_STORE_SOURCE_CHUNK_CACHE_SIZE_LIMIT,
      newRandomHash,
      assertHash
    );
    const readyResolver = resolver6();
    this.#ready = readyResolver.promise;
    const { minDelayMs = MIN_DELAY_MS, maxDelayMs = MAX_DELAY_MS } = requestOptions;
    this.#requestOptions = { maxDelayMs, minDelayMs };
    const visibilityWatcher = getDocumentVisibilityWatcher(
      getBrowserGlobal("document"),
      0,
      this.#closeAbortController.signal
    );
    this.#pullConnectionLoop = new ConnectionLoop(
      this.#lc.withContext("PULL"),
      new PullDelegate(this, () => this.#invokePull()),
      visibilityWatcher
    );
    this.#pushConnectionLoop = new ConnectionLoop(
      this.#lc.withContext("PUSH"),
      new PushDelegate(this, () => this.#invokePush())
    );
    this.mutate = this.#registerMutators(mutators);
    const profileIDResolver = resolver6();
    this.#profileIDPromise = profileIDResolver.promise;
    const clientGroupIDResolver = resolver6();
    this.#clientGroupIDPromise = clientGroupIDResolver.promise;
    if (false) {
      this.#mutationRecovery = new MutationRecovery({
        delegate: this,
        lc: this.#lc,
        enableMutationRecovery,
        wrapInOnlineCheck: this.#wrapInOnlineCheck.bind(this),
        wrapInReauthRetries: this.#wrapInReauthRetries.bind(this),
        isPullDisabled: this.#isPullDisabled.bind(this),
        isPushDisabled: this.#isPushDisabled.bind(this),
        clientGroupIDPromise: this.#clientGroupIDPromise
      });
    }
    this.#onPersist = initOnPersistChannel(
      this.name,
      this.#closeAbortController.signal,
      (persistInfo) => {
        void this.#handlePersist(persistInfo);
      }
    );
    void this.#open(
      indexes,
      enableClientGroupForking,
      enableMutationRecovery,
      clientMaxAgeMs,
      profileIDResolver.resolve,
      clientGroupIDResolver.resolve,
      readyResolver.resolve,
      onClientsDeleted
    );
  }
  async #open(indexes, enableClientGroupForking, enableMutationRecovery, clientMaxAgeMs, profileIDResolver, resolveClientGroupID, resolveReady, onClientsDeleted) {
    const { clientID } = this;
    await closingInstances.get(this.name);
    await this.#idbDatabases.getProfileID().then(profileIDResolver);
    await this.#idbDatabases.putDatabase(this.#idbDatabase);
    const [client, headHash, , isNewClientGroup] = await initClientV6(
      clientID,
      this.#lc,
      this.perdag,
      Object.keys(this.#mutatorRegistry),
      indexes,
      Latest,
      enableClientGroupForking
    );
    resolveClientGroupID(client.clientGroupID);
    await withWrite(
      this.memdag,
      (write) => write.setHead(DEFAULT_HEAD_NAME, headHash)
    );
    await this.#zero?.init(headHash, this.memdag);
    resolveReady();
    if (this.#enablePullAndPushInOpen) {
      this.pull().catch(noop2);
      this.push().catch(noop2);
    }
    const { signal } = this.#closeAbortController;
    startHeartbeats(
      clientID,
      this.perdag,
      () => {
        this.#clientStateNotFoundOnClient(clientID);
      },
      HEARTBEAT_INTERVAL,
      this.#lc,
      signal
    );
    initClientGC(
      clientID,
      this.perdag,
      clientMaxAgeMs,
      GC_INTERVAL,
      onClientsDeleted,
      this.#lc,
      signal
    );
    initCollectIDBDatabases(
      this.#idbDatabases,
      this.#kvStoreProvider.drop,
      COLLECT_IDB_INTERVAL,
      INITIAL_COLLECT_IDB_DELAY,
      2 * clientMaxAgeMs,
      enableMutationRecovery,
      onClientsDeleted,
      this.#lc,
      signal
    );
    initClientGroupGC(
      this.perdag,
      enableMutationRecovery,
      onClientsDeleted,
      this.#lc,
      signal
    );
    initNewClientChannel(
      this.name,
      this.idbName,
      signal,
      client.clientGroupID,
      isNewClientGroup,
      () => {
        this.#fireOnUpdateNeeded(updateNeededReasonNewClientGroup);
      },
      this.perdag
    );
    setIntervalWithSignal(
      () => this.recoverMutations(),
      RECOVER_MUTATIONS_INTERVAL_MS,
      signal
    );
    void this.recoverMutations();
    getBrowserGlobal("document")?.addEventListener(
      "visibilitychange",
      this.#onVisibilityChange
    );
  }
  #onVisibilityChange = async () => {
    if (this.#closed) {
      return;
    }
    if (getBrowserGlobal("document")?.visibilityState !== "visible") {
      return;
    }
    await this.#checkForClientStateNotFoundAndCallHandler();
  };
  async #checkForClientStateNotFoundAndCallHandler() {
    const { clientID } = this;
    const hasClientState2 = await withRead(
      this.perdag,
      (read) => hasClientState(clientID, read)
    );
    if (!hasClientState2) {
      this.#clientStateNotFoundOnClient(clientID);
    }
    return !hasClientState2;
  }
  /**
   * The browser profile ID for this browser profile. Every instance of Replicache
   * browser-profile-wide shares the same profile ID.
   */
  get profileID() {
    return this.#profileIDPromise;
  }
  /**
   * The client ID for this instance of Replicache. Each instance of Replicache
   * gets a unique client ID.
   */
  get clientID() {
    return this.#clientID;
  }
  /**
   * The client group ID for this instance of Replicache. Instances of
   * Replicache will have the same client group ID if and only if they have
   * the same name, mutators, indexes, schema version, format version, and
   * browser profile.
   */
  get clientGroupID() {
    return this.#clientGroupIDPromise;
  }
  /**
   * `onOnlineChange` is called when the {@link online} property changes. See
   * {@link online} for more details.
   */
  onOnlineChange = null;
  /**
   * A rough heuristic for whether the client is currently online. Note that
   * there is no way to know for certain whether a client is online - the next
   * request can always fail. This property returns true if the last sync attempt succeeded,
   * and false otherwise.
   */
  get online() {
    return this.#online;
  }
  /**
   * Whether the Replicache database has been closed. Once Replicache has been
   * closed it no longer syncs and you can no longer read or write data out of
   * it. After it has been closed it is pretty much useless and should not be
   * used any more.
   */
  get closed() {
    return this.#closed;
  }
  /**
   * Closes this Replicache instance.
   *
   * When closed all subscriptions end and no more read or writes are allowed.
   */
  async close() {
    this.#closed = true;
    const { promise, resolve } = resolver6();
    closingInstances.set(this.name, promise);
    this.#closeAbortController.abort();
    getBrowserGlobal("document")?.removeEventListener(
      "visibilitychange",
      this.#onVisibilityChange
    );
    await this.#ready;
    const closingPromises = [
      this.memdag.close(),
      this.perdag.close(),
      this.#idbDatabases.close()
    ];
    this.#pullConnectionLoop.close();
    this.#pushConnectionLoop.close();
    this.#subscriptions.clear();
    await Promise.all(closingPromises);
    closingInstances.delete(this.name);
    resolve();
  }
  async maybeEndPull(syncHead, requestID) {
    for (; ; ) {
      if (this.#closed) {
        return;
      }
      await this.#ready;
      const { clientID } = this;
      const lc = this.#lc.withContext("maybeEndPull").withContext("requestID", requestID);
      const { replayMutations, diffs, oldMainHead, mainHead } = await maybeEndPull(
        this.memdag,
        lc,
        syncHead,
        clientID,
        this.#subscriptions,
        Latest
      );
      if (!replayMutations || replayMutations.length === 0) {
        this.#zero?.advance(oldMainHead, mainHead, diffs.get("") ?? []);
        await this.#subscriptions.fire(diffs);
        void this.#schedulePersist();
        return;
      }
      const zeroData2 = await this.#zero?.getTxData?.(syncHead);
      for (const mutation of replayMutations) {
        if (this.#subscriptions.hasPendingSubscriptionRuns) {
          await Promise.resolve();
        }
        const { meta } = mutation;
        syncHead = await withWriteNoImplicitCommit(
          this.memdag,
          (dagWrite) => rebaseMutationAndCommit(
            mutation,
            dagWrite,
            syncHead,
            SYNC_HEAD_NAME,
            this.#mutatorRegistry,
            lc,
            isLocalMetaDD31(meta) ? meta.clientID : clientID,
            Latest,
            zeroData2
          )
        );
      }
    }
  }
  #invokePull() {
    if (this.#isPullDisabled()) {
      return Promise.resolve(true);
    }
    return this.#wrapInOnlineCheck(async () => {
      try {
        this.#changeSyncCounters(0, 1);
        const { syncHead, requestID, ok } = await this.beginPull();
        if (!ok) {
          return false;
        }
        if (syncHead !== emptyHash) {
          await this.maybeEndPull(syncHead, requestID);
        }
      } catch (e) {
        throw await this.#convertToClientStateNotFoundError(e);
      } finally {
        this.#changeSyncCounters(0, -1);
      }
      return true;
    }, "Pull");
  }
  #isPullDisabled() {
    return this.isClientGroupDisabled || this.pullURL === "" && isDefaultPuller(this.puller);
  }
  async #wrapInOnlineCheck(f, name) {
    let online = true;
    try {
      return await f();
    } catch (e) {
      if (e instanceof PushError || e instanceof PullError) {
        online = false;
        this.#lc.debug?.(`${name} threw:
`, e, "\nwith cause:\n", e.causedBy);
      } else if (e instanceof ReportError) {
        this.#lc.error?.(e);
      } else {
        this.#lc.info?.(`${name} threw:
`, e);
      }
      return false;
    } finally {
      if (this.#online !== online) {
        this.#online = online;
        this.onOnlineChange?.(online);
        if (online) {
          void this.recoverMutations();
        }
      }
    }
  }
  async #wrapInReauthRetries(f, verb, lc, preAuth = noop2, postAuth = noop2) {
    const { clientID } = this;
    let reauthAttempts = 0;
    let lastResult;
    lc = lc.withContext(verb);
    do {
      const requestID = newRequestID(clientID);
      const requestLc = lc.withContext("requestID", requestID);
      const { httpRequestInfo, result } = await f(requestID, requestLc);
      lastResult = result;
      if (!httpRequestInfo) {
        return {
          result,
          authFailure: false
        };
      }
      const { errorMessage, httpStatusCode } = httpRequestInfo;
      if (errorMessage || httpStatusCode !== 200) {
        requestLc.error?.(
          `Got a non 200 response doing ${verb}: ${httpStatusCode}` + (errorMessage ? `: ${errorMessage}` : "")
        );
      }
      if (httpStatusCode !== httpStatusUnauthorized) {
        return {
          result,
          authFailure: false
        };
      }
      if (!this.getAuth) {
        return {
          result,
          authFailure: true
        };
      }
      let auth;
      try {
        await preAuth();
        auth = await this.getAuth();
      } finally {
        await postAuth();
      }
      if (auth === null || auth === void 0) {
        return {
          result,
          authFailure: true
        };
      }
      this.auth = auth;
      reauthAttempts++;
    } while (reauthAttempts < MAX_REAUTH_TRIES);
    lc.info?.("Tried to reauthenticate too many times");
    return {
      result: lastResult,
      authFailure: true
    };
  }
  #isPushDisabled() {
    return this.isClientGroupDisabled || this.pushURL === "" && isDefaultPusher(this.pusher);
  }
  async #invokePush() {
    if (false) {
      this.onPushInvoked();
    }
    if (this.#isPushDisabled()) {
      return true;
    }
    await this.#ready;
    const profileID = await this.#profileIDPromise;
    const { clientID } = this;
    const clientGroupID = await this.#clientGroupIDPromise;
    return this.#wrapInOnlineCheck(async () => {
      const { result: pusherResult } = await this.#wrapInReauthRetries(
        async (requestID, requestLc) => {
          try {
            this.#changeSyncCounters(1, 0);
            const pusherResult2 = await push(
              requestID,
              this.memdag,
              requestLc,
              profileID,
              clientGroupID,
              clientID,
              this.pusher,
              this.schemaVersion,
              PUSH_VERSION_DD31
            );
            return {
              result: pusherResult2,
              httpRequestInfo: pusherResult2?.httpRequestInfo
            };
          } finally {
            this.#changeSyncCounters(-1, 0);
          }
        },
        "push",
        this.#lc
      );
      if (pusherResult === void 0) {
        return true;
      }
      const { response, httpRequestInfo } = pusherResult;
      if (isVersionNotSupportedResponse(response)) {
        this.#handleVersionNotSupportedResponse(response);
      } else if (isClientStateNotFoundResponse(response)) {
        await this.#clientStateNotFoundOnServer();
      }
      return httpRequestInfo.httpStatusCode === 200;
    }, "Push");
  }
  #handleVersionNotSupportedResponse(response) {
    const reason = {
      type: response.error
    };
    if (response.versionType) {
      reason.versionType = response.versionType;
    }
    this.#fireOnUpdateNeeded(reason);
  }
  /**
   * Push pushes pending changes to the {@link pushURL}.
   *
   * You do not usually need to manually call push. If {@link pushDelay} is
   * non-zero (which it is by default) pushes happen automatically shortly after
   * mutations.
   *
   * If the server endpoint fails push will be continuously retried with an
   * exponential backoff.
   *
   * @param [now=false] If true, push will happen immediately and ignore
   *   {@link pushDelay}, {@link RequestOptions.minDelayMs} as well as the
   *   exponential backoff in case of errors.
   * @returns A promise that resolves when the next push completes. In case of
   * errors the first error will reject the returned promise. Subsequent errors
   * will not be reflected in the promise.
   */
  push({ now = false } = {}) {
    return throwIfError(this.#pushConnectionLoop.send(now));
  }
  /**
   * Pull pulls changes from the {@link pullURL}. If there are any changes local
   * changes will get replayed on top of the new server state.
   *
   * If the server endpoint fails pull will be continuously retried with an
   * exponential backoff.
   *
   * @param [now=false] If true, pull will happen immediately and ignore
   *   {@link RequestOptions.minDelayMs} as well as the exponential backoff in
   *   case of errors.
   * @returns A promise that resolves when the next pull completes. In case of
   * errors the first error will reject the returned promise. Subsequent errors
   * will not be reflected in the promise.
   */
  pull({ now = false } = {}) {
    return throwIfError(this.#pullConnectionLoop.send(now));
  }
  /**
   * Applies an update from the server to Replicache.
   * Throws an error if cookie does not match. In that case the server thinks
   * this client has a different cookie than it does; the caller should disconnect
   * from the server and re-register, which transmits the cookie the client actually
   * has.
   *
   * @experimental This method is under development and its semantics will change.
   */
  async poke(poke) {
    await this.#ready;
    const { clientID } = this;
    const requestID = newRequestID(clientID);
    const lc = this.#lc.withContext("handlePullResponse").withContext("requestID", requestID);
    const { pullResponse } = poke;
    if (isVersionNotSupportedResponse(pullResponse)) {
      this.#handleVersionNotSupportedResponse(pullResponse);
      return;
    }
    if (isClientStateNotFoundResponse(pullResponse)) {
      await this.#clientStateNotFoundOnServer();
      return;
    }
    const result = await handlePullResponseV1(
      lc,
      this.memdag,
      deepFreeze(poke.baseCookie),
      pullResponse,
      clientID,
      Latest
    );
    switch (result.type) {
      case Applied:
        await this.maybeEndPull(result.syncHead, requestID);
        break;
      case CookieMismatch:
        throw new Error(
          "unexpected base cookie for poke: " + JSON.stringify(poke)
        );
      case NoOp:
        break;
    }
  }
  async beginPull() {
    if (false) {
      this.onBeginPull();
    }
    await this.#ready;
    const profileID = await this.profileID;
    const { clientID } = this;
    const clientGroupID = await this.#clientGroupIDPromise;
    const {
      result: { beginPullResponse, requestID }
    } = await this.#wrapInReauthRetries(
      async (requestID2, requestLc) => {
        const beginPullResponse2 = await beginPullV1(
          profileID,
          clientID,
          clientGroupID,
          this.schemaVersion,
          this.puller,
          requestID2,
          this.memdag,
          Latest,
          requestLc
        );
        return {
          result: { beginPullResponse: beginPullResponse2, requestID: requestID2 },
          httpRequestInfo: beginPullResponse2.httpRequestInfo
        };
      },
      "pull",
      this.#lc,
      () => this.#changeSyncCounters(0, -1),
      () => this.#changeSyncCounters(0, 1)
    );
    const { pullResponse } = beginPullResponse;
    if (isVersionNotSupportedResponse(pullResponse)) {
      this.#handleVersionNotSupportedResponse(pullResponse);
    } else if (isClientStateNotFoundResponse(beginPullResponse.pullResponse)) {
      await this.#clientStateNotFoundOnServer();
    }
    const { syncHead, httpRequestInfo } = beginPullResponse;
    return { requestID, syncHead, ok: httpRequestInfo.httpStatusCode === 200 };
  }
  persist() {
    return this.#persistLock.withLock(async () => {
      const { clientID } = this;
      await this.#ready;
      if (this.#closed) {
        return;
      }
      try {
        await persistDD31(
          this.#lc,
          clientID,
          this.memdag,
          this.perdag,
          this.#mutatorRegistry,
          () => this.#closed,
          Latest,
          this.#zero?.getTxData
        );
      } catch (e) {
        if (e instanceof ClientStateNotFoundError) {
          this.#clientStateNotFoundOnClient(clientID);
        } else if (this.#closed) {
          this.#lc.debug?.("Exception persisting during close", e);
        } else {
          throw e;
        }
      }
      const clientGroupID = await this.#clientGroupIDPromise;
      assert(clientGroupID);
      this.#onPersist({ clientID, clientGroupID });
    });
  }
  async refresh() {
    await this.#ready;
    const { clientID } = this;
    if (this.#closed) {
      return;
    }
    let refreshResult;
    try {
      refreshResult = await refresh(
        this.#lc,
        this.memdag,
        this.perdag,
        clientID,
        this.#mutatorRegistry,
        this.#subscriptions,
        () => this.closed,
        Latest,
        this.#zero
      );
    } catch (e) {
      if (e instanceof ClientStateNotFoundError) {
        this.#clientStateNotFoundOnClient(clientID);
      } else if (this.#closed) {
        this.#lc.debug?.("Exception refreshing during close", e);
      } else {
        throw e;
      }
    }
    if (refreshResult !== void 0) {
      await this.#subscriptions.fire(refreshResult.diffs);
    }
  }
  #fireOnClientStateNotFound() {
    this.onClientStateNotFound?.();
  }
  #clientStateNotFoundOnClient(clientID) {
    this.#lc.error?.(`Client state not found on client, clientID: ${clientID}`);
    this.#fireOnClientStateNotFound();
  }
  async #clientStateNotFoundOnServer() {
    const clientGroupID = await this.#clientGroupIDPromise;
    this.#lc.error?.(
      `Client state not found on server, clientGroupID: ${clientGroupID}`
    );
    await this.disableClientGroup();
    this.#fireOnClientStateNotFound();
  }
  async disableClientGroup() {
    const clientGroupID = await this.#clientGroupIDPromise;
    assert(clientGroupID);
    this.isClientGroupDisabled = true;
    await withWrite(
      this.perdag,
      (dagWrite) => disableClientGroup(clientGroupID, dagWrite)
    );
  }
  #fireOnUpdateNeeded(reason) {
    this.#lc.debug?.(`Update needed, reason: ${reason}`);
    this.onUpdateNeeded?.(reason);
  }
  async #schedulePersist() {
    if (!this.#enableScheduledPersist) {
      return;
    }
    await this.#schedule("persist", this.#persistScheduler);
  }
  async #handlePersist(persistInfo) {
    this.#lc.debug?.("Handling persist", persistInfo);
    const clientGroupID = await this.#clientGroupIDPromise;
    if (persistInfo.clientGroupID === clientGroupID) {
      void this.#scheduleRefresh();
    }
  }
  async #scheduleRefresh() {
    if (!this.#enableScheduledRefresh) {
      return;
    }
    await this.#schedule("refresh from storage", this.#refreshScheduler);
  }
  async #schedule(name, scheduler) {
    try {
      await scheduler.schedule();
    } catch (e) {
      if (e instanceof AbortError) {
        this.#lc.debug?.(`Scheduled ${name} did not complete before close.`);
      } else {
        this.#lc.error?.(`Error during ${name}`, e);
      }
    }
  }
  #changeSyncCounters(pushDelta, pullDelta) {
    this.#pushCounter += pushDelta;
    this.#pullCounter += pullDelta;
    const delta = pushDelta + pullDelta;
    const counter = this.#pushCounter + this.#pullCounter;
    if (delta === 1 && counter === 1 || counter === 0) {
      const syncing = counter > 0;
      Promise.resolve().then(() => this.onSync?.(syncing));
    }
  }
  /**
   * Subscribe to the result of a {@link query}. The `body` function is
   * evaluated once and its results are returned via `onData`.
   *
   * Thereafter, each time the the result of `body` changes, `onData` is fired
   * again with the new result.
   *
   * `subscribe()` goes to significant effort to avoid extraneous work
   * re-evaluating subscriptions:
   *
   * 1. subscribe tracks the keys that `body` accesses each time it runs. `body`
   *    is only re-evaluated when those keys change.
   * 2. subscribe only re-fires `onData` in the case that a result changes by
   *    way of the `isEqual` option which defaults to doing a deep JSON value
   *    equality check.
   *
   * Because of (1), `body` must be a pure function of the data in Replicache.
   * `body` must not access anything other than the `tx` parameter passed to it.
   *
   * Although subscribe is as efficient as it can be, it is somewhat constrained
   * by the goal of returning an arbitrary computation of the cache. For even
   * better performance (but worse dx), see {@link experimentalWatch}.
   *
   * If an error occurs in the `body` the `onError` function is called if
   * present. Otherwise, the error is logged at log level 'error'.
   *
   * To cancel the subscription, call the returned function.
   *
   * @param body The function to evaluate to get the value to pass into
   *    `onData`.
   * @param options Options is either a function or an object. If it is a
   *    function it is equivalent to passing it as the `onData` property of an
   *    object.
   */
  subscribe(body, options) {
    if (typeof options === "function") {
      options = { onData: options };
    }
    const { onData, onError, onDone, isEqual } = options;
    return this.#subscriptions.add(
      new SubscriptionImpl(body, onData, onError, onDone, isEqual)
    );
  }
  experimentalWatch(callback, options) {
    return this.#subscriptions.add(
      new WatchSubscription(callback, options)
    );
  }
  /**
   * Query is used for read transactions. It is recommended to use transactions
   * to ensure you get a consistent view across multiple calls to `get`, `has`
   * and `scan`.
   */
  query(body) {
    return this.#queryInternal(body);
  }
  get cookie() {
    return this.#ready.then(
      () => withRead(this.memdag, async (dagRead) => {
        const mainHeadHash = await dagRead.getHead(DEFAULT_HEAD_NAME);
        if (!mainHeadHash) {
          throw new Error("Internal no main head found");
        }
        const baseSnapshot = await baseSnapshotFromHash(mainHeadHash, dagRead);
        const baseSnapshotMeta = baseSnapshot.meta;
        const cookie = baseSnapshotMeta.cookieJSON;
        assertCookie(cookie);
        return cookie;
      })
    );
  }
  #queryInternal = async (body) => {
    await this.#ready;
    const { clientID } = this;
    return withRead(this.memdag, async (dagRead) => {
      try {
        const dbRead = await readFromDefaultHead(dagRead, Latest);
        const tx = new ReadTransactionImpl(clientID, dbRead, this.#lc);
        return await body(tx);
      } catch (ex) {
        throw await this.#convertToClientStateNotFoundError(ex);
      }
    });
  };
  #register(name, mutatorImpl) {
    this.#mutatorRegistry[name] = mutatorImpl;
    return (args) => {
      const trackingData = name === "_zero_crud" ? void 0 : this.#zero?.trackMutation();
      const result = this.#mutate(
        trackingData,
        name,
        mutatorImpl,
        args,
        performance.now()
      );
      if (trackingData) {
        result.server = trackingData.serverPromise;
      }
      return result;
    };
  }
  #registerMutators(regs) {
    const rv = /* @__PURE__ */ Object.create(null);
    for (const k in regs) {
      rv[k] = this.#register(k, regs[k]);
    }
    return rv;
  }
  async #mutate(trackingData, name, mutatorImpl, args, timestamp) {
    const frozenArgs = deepFreeze(args ?? null);
    if (this.#subscriptions.hasPendingSubscriptionRuns) {
      await Promise.resolve();
    }
    await this.#ready;
    const { clientID } = this;
    return withWriteNoImplicitCommit(this.memdag, async (dagWrite) => {
      try {
        let result;
        let newHead;
        let diffs;
        let headHash;
        try {
          headHash = await mustGetHeadHash(DEFAULT_HEAD_NAME, dagWrite);
          const originalHash = null;
          const dbWrite = await newWriteLocal(
            headHash,
            name,
            frozenArgs,
            originalHash,
            dagWrite,
            timestamp,
            clientID,
            Latest
          );
          const mutationID = await dbWrite.getMutationID();
          const tx = new WriteTransactionImpl(
            clientID,
            mutationID,
            "initial",
            await this.#zero?.getTxData(headHash, {
              openLazyRead: dagWrite
            }),
            dbWrite,
            this.#lc
          );
          if (trackingData) {
            this.#zero?.mutationIDAssigned(
              trackingData.ephemeralID,
              mutationID
            );
          }
          result = await mutatorImpl(tx, args);
          throwIfClosed(dbWrite);
          const lastMutationID = await dbWrite.getMutationID();
          [newHead, diffs] = await dbWrite.commitWithDiffs(
            DEFAULT_HEAD_NAME,
            this.#subscriptions
          );
          this.lastMutationID = lastMutationID;
        } catch (e) {
          if (trackingData) {
            this.#zero?.rejectMutation(trackingData.ephemeralID, e);
          }
          throw e;
        }
        this.#zero?.advance(headHash, newHead, diffs.get("") ?? []);
        this.#pushConnectionLoop.send(false).catch(() => void 0);
        await this.#subscriptions.fire(diffs);
        void this.#schedulePersist();
        return result;
      } catch (ex) {
        throw await this.#convertToClientStateNotFoundError(ex);
      }
    });
  }
  /**
   * In the case we get a ChunkNotFoundError we check if the client got garbage
   * collected and if so change the error to a ClientStateNotFoundError instead
   */
  async #convertToClientStateNotFoundError(ex) {
    if (ex instanceof ChunkNotFoundError && await this.#checkForClientStateNotFoundAndCallHandler()) {
      return new ClientStateNotFoundError(this.clientID);
    }
    return ex;
  }
  recoverMutations() {
    if (false) {
      const result = this.#mutationRecovery.recoverMutations(
        this.#ready,
        this.perdag,
        this.#idbDatabase,
        this.#idbDatabases,
        this.#kvStoreProvider.create
      );
      if (false) {
        void this.onRecoverMutations(result);
      }
      return result;
    }
  }
  /**
   * List of pending mutations. The order of this is from oldest to newest.
   *
   * Gives a list of local mutations that have `mutationID` >
   * `syncHead.mutationID` that exists on the main client group.
   *
   * @experimental This method is experimental and may change in the future.
   */
  experimentalPendingMutations() {
    return withRead(this.memdag, pendingMutationsForAPI);
  }
};
var closingInstances = /* @__PURE__ */ new Map();
async function throwIfError(p) {
  const res = await p;
  if (res) {
    throw res.error;
  }
}
function reload() {
  if (typeof location !== "undefined") {
    location.reload();
  }
}
function validateOptions(options) {
  const { name, clientMaxAgeMs } = options;
  if (typeof name !== "string" || !name) {
    throw new TypeError("name is required and must be non-empty");
  }
  if (clientMaxAgeMs !== void 0) {
    const min = Math.max(GC_INTERVAL, HEARTBEAT_INTERVAL);
    if (typeof clientMaxAgeMs !== "number" || clientMaxAgeMs <= min) {
      throw new TypeError(
        `clientAgeMaxMs must be a number larger than ${min}ms`
      );
    }
  }
}

// ../replicache/src/replicache.ts
var httpStatusUnauthorized = 401;
function makeIDBName(name, schemaVersion) {
  return makeIDBNameInternal(name, schemaVersion, Latest);
}
function makeIDBNameInternal(name, schemaVersion, formatVersion) {
  const n = `rep:${name}:${formatVersion}`;
  return schemaVersion ? `${n}:${schemaVersion}` : n;
}
var ReportError = class extends Error {
};
function createMemStore(name) {
  return new MemStore(name);
}
function getKVStoreProvider(lc, kvStore) {
  switch (kvStore) {
    case "idb":
    case void 0:
      return {
        create: (name) => newIDBStoreWithMemFallback(lc, name),
        drop: dropIDBStoreWithMemFallback
      };
    case "mem":
      return {
        create: createMemStore,
        drop: (name) => dropMemStore(name)
      };
    default:
      return kvStore;
  }
}

// ../replicache/src/persist/collect-idb-databases.ts
var COLLECT_IDB_INTERVAL = 12 * 60 * 60 * 1e3;
var INITIAL_COLLECT_IDB_DELAY = 5 * 60 * 1e3;
function initCollectIDBDatabases(idbDatabasesStore, kvDropStore, collectInterval, initialCollectDelay, maxAge, enableMutationRecovery, onClientsDeleted, lc, signal) {
  let initial = true;
  initBgIntervalProcess(
    "CollectIDBDatabases",
    async () => {
      await collectIDBDatabases(
        idbDatabasesStore,
        Date.now(),
        maxAge,
        kvDropStore,
        enableMutationRecovery,
        onClientsDeleted
      );
    },
    () => {
      if (initial) {
        initial = false;
        return initialCollectDelay;
      }
      return collectInterval;
    },
    lc,
    signal
  );
}
async function collectIDBDatabases(idbDatabasesStore, now, maxAge, kvDropStore, enableMutationRecovery, onClientsDeleted, newDagStore = defaultNewDagStore) {
  const databases = await idbDatabasesStore.getDatabases();
  const dbs = Object.values(databases);
  const collectResults = await Promise.all(
    dbs.map(
      async (db) => [
        db.name,
        await gatherDatabaseInfoForCollect(
          db,
          now,
          maxAge,
          enableMutationRecovery,
          newDagStore
        )
      ]
    )
  );
  const dbNamesToRemove = [];
  const dbNamesToKeep = [];
  const clientIDsToRemove = [];
  const clientGroupIDsToRemove = [];
  for (const [
    dbName,
    [canCollect, clientIDs, clientGroupIDs]
  ] of collectResults) {
    if (canCollect) {
      dbNamesToRemove.push(dbName);
      clientIDsToRemove.push(...clientIDs);
      clientGroupIDsToRemove.push(...clientGroupIDs);
    } else {
      dbNamesToKeep.push(dbName);
    }
  }
  const { errors } = await dropDatabases(
    idbDatabasesStore,
    dbNamesToRemove,
    kvDropStore
  );
  if (errors.length) {
    throw errors[0];
  }
  if (clientIDsToRemove.length || clientGroupIDsToRemove.length) {
    const newClientIDsToRemove = clientIDsToRemove;
    const newClientGroupIDsToRemove = clientGroupIDsToRemove;
    for (const name of dbNamesToKeep) {
      await withWrite(newDagStore(name), async (dagWrite) => {
        const { clientIDs, clientGroupIDs } = await addDeletedClients(
          dagWrite,
          clientIDsToRemove,
          clientGroupIDsToRemove
        );
        newClientIDsToRemove.push(...clientIDs);
        newClientGroupIDsToRemove.push(...clientGroupIDs);
      });
    }
    onClientsDeleted(
      normalize(newClientIDsToRemove),
      normalize(newClientGroupIDsToRemove)
    );
  }
}
async function dropDatabaseInternal(name, idbDatabasesStore, kvDropStore) {
  await kvDropStore(name);
  await idbDatabasesStore.deleteDatabases([name]);
}
async function dropDatabases(idbDatabasesStore, namesToRemove, kvDropStore) {
  const dropStoreResults = await Promise.allSettled(
    namesToRemove.map(async (name) => {
      await dropDatabaseInternal(name, idbDatabasesStore, kvDropStore);
      return name;
    })
  );
  const dropped = [];
  const errors = [];
  for (const result of dropStoreResults) {
    if (result.status === "fulfilled") {
      dropped.push(result.value);
    } else {
      errors.push(result.reason);
    }
  }
  return { dropped, errors };
}
function defaultNewDagStore(name) {
  const perKvStore = new IDBStore(name);
  return new StoreImpl(perKvStore, newRandomHash, assertHash);
}
function gatherDatabaseInfoForCollect(db, now, maxAge, enableMutationRecovery, newDagStore) {
  if (db.replicacheFormatVersion > Latest) {
    return [false];
  }
  assert(db.lastOpenedTimestampMS !== void 0);
  if (now - db.lastOpenedTimestampMS < maxAge) {
    return [false];
  }
  assert(
    db.replicacheFormatVersion === DD31 || db.replicacheFormatVersion === V6 || db.replicacheFormatVersion === V7
  );
  return canDatabaseBeCollectedAndGetDeletedClientIDs(
    enableMutationRecovery,
    newDagStore(db.name)
  );
}
async function dropDatabase(dbName, opts) {
  const logContext = createLogContext(opts?.logLevel, opts?.logSinks, {
    dropDatabase: void 0
  });
  const kvStoreProvider = getKVStoreProvider(logContext, opts?.kvStore);
  await dropDatabaseInternal(
    dbName,
    new IDBDatabasesStore(kvStoreProvider.create),
    kvStoreProvider.drop
  );
}
async function dropAllDatabases(opts) {
  const logContext = createLogContext(opts?.logLevel, opts?.logSinks, {
    dropAllDatabases: void 0
  });
  const kvStoreProvider = getKVStoreProvider(logContext, opts?.kvStore);
  const store = new IDBDatabasesStore(kvStoreProvider.create);
  const databases = await store.getDatabases();
  const dbNames = Object.values(databases).map((db) => db.name);
  const result = await dropDatabases(store, dbNames, kvStoreProvider.drop);
  return result;
}
function canDatabaseBeCollectedAndGetDeletedClientIDs(enableMutationRecovery, perdag) {
  return withRead(perdag, async (read) => {
    if (enableMutationRecovery) {
      const clientGroups = await getClientGroups(read);
      for (const clientGroup of clientGroups.values()) {
        if (clientGroupHasPendingMutations(clientGroup)) {
          return [false];
        }
      }
    }
    const clients = await getClients(read);
    const { clientIDs, clientGroupIDs } = await getDeletedClients(read);
    const newClientIDs = [...clientIDs];
    const newClientGroupIDs = [...clientGroupIDs];
    for (const [clientID, client] of clients) {
      newClientIDs.push(clientID);
      newClientGroupIDs.push(client.clientGroupID);
    }
    return [true, newClientIDs, newClientGroupIDs];
  });
}

// ../zero-schema/src/builder/relationship-builder.ts
function relationships(table2, cb) {
  const relationships2 = cb({ many, one });
  return {
    name: table2.schema.name,
    relationships: relationships2
  };
}
function many(...args) {
  return args.map((arg) => ({
    sourceField: arg.sourceField,
    destField: arg.destField,
    destSchema: arg.destSchema.schema.name,
    cardinality: "many"
  }));
}
function one(...args) {
  return args.map((arg) => ({
    sourceField: arg.sourceField,
    destField: arg.destField,
    destSchema: arg.destSchema.schema.name,
    cardinality: "one"
  }));
}

// ../shared/src/objects.ts
function mapEntries(input, mapper) {
  const output = {};
  for (const entry of Object.entries(input)) {
    const mapped = mapper(entry[0], entry[1]);
    output[mapped[0]] = mapped[1];
  }
  return output;
}
function mapAllEntries(input, mapper) {
  const output = {};
  for (const mapped of mapper(Object.entries(input))) {
    output[mapped[0]] = mapped[1];
  }
  return output;
}

// ../zero-protocol/src/client-schema.ts
var valueTypeSchema = valita_exports.union(
  valita_exports.literal("string"),
  valita_exports.literal("number"),
  valita_exports.literal("boolean"),
  valita_exports.literal("null"),
  valita_exports.literal("json")
);
var columnSchemaSchema = valita_exports.object({
  type: valueTypeSchema
});
var tableSchemaSchema = valita_exports.object({
  columns: valita_exports.record(columnSchemaSchema)
});
var clientSchemaSchema = valita_exports.object({
  tables: valita_exports.record(tableSchemaSchema)
});
var keyCmp = ([a], [b]) => a < b ? -1 : a > b ? 1 : 0;
function normalizeClientSchema(schema) {
  return {
    tables: mapAllEntries(
      schema.tables,
      (tables) => tables.sort(keyCmp).map(([name, table2]) => [
        name,
        { columns: mapAllEntries(table2.columns, (e) => e.sort(keyCmp)) }
      ])
    )
  };
}

// ../zero-schema/src/builder/table-builder.ts
function table(name) {
  return new TableBuilder({
    name,
    columns: {},
    primaryKey: []
  });
}
function string3() {
  return new ColumnBuilder({
    type: "string",
    optional: false,
    customType: null
  });
}
function number2() {
  return new ColumnBuilder({
    type: "number",
    optional: false,
    customType: null
  });
}
function boolean() {
  return new ColumnBuilder({
    type: "boolean",
    optional: false,
    customType: null
  });
}
function json() {
  return new ColumnBuilder({
    type: "json",
    optional: false,
    customType: null
  });
}
function enumeration() {
  return new ColumnBuilder({
    type: "string",
    optional: false,
    customType: null
  });
}
var TableBuilder = class _TableBuilder {
  #schema;
  constructor(schema) {
    this.#schema = schema;
  }
  from(serverName) {
    return new _TableBuilder({
      ...this.#schema,
      serverName
    });
  }
  columns(columns) {
    const columnSchemas = Object.fromEntries(
      Object.entries(columns).map(([k, v]) => [k, v.schema])
    );
    return new TableBuilderWithColumns({
      ...this.#schema,
      columns: columnSchemas
    });
  }
};
var TableBuilderWithColumns = class _TableBuilderWithColumns {
  #schema;
  constructor(schema) {
    this.#schema = schema;
  }
  primaryKey(...pkColumnNames) {
    return new _TableBuilderWithColumns({
      ...this.#schema,
      primaryKey: pkColumnNames
    });
  }
  get schema() {
    return this.#schema;
  }
  build() {
    if (this.#schema.primaryKey.length === 0) {
      throw new Error(`Table "${this.#schema.name}" is missing a primary key`);
    }
    const names = /* @__PURE__ */ new Set();
    for (const [col, { serverName }] of Object.entries(this.#schema.columns)) {
      const name = serverName ?? col;
      if (names.has(name)) {
        throw new Error(
          `Table "${this.#schema.name}" has multiple columns referencing "${name}"`
        );
      }
      names.add(name);
    }
    return this.#schema;
  }
};
var ColumnBuilder = class _ColumnBuilder {
  #schema;
  constructor(schema) {
    this.#schema = schema;
  }
  from(serverName) {
    return new _ColumnBuilder({
      ...this.#schema,
      serverName
    });
  }
  optional() {
    return new _ColumnBuilder({
      ...this.#schema,
      optional: true
    });
  }
  get schema() {
    return this.#schema;
  }
};

// ../zero-schema/src/builder/schema-builder.ts
function createSchema(options) {
  const retTables = {};
  const retRelationships = {};
  const serverNames = /* @__PURE__ */ new Set();
  options.tables.forEach((table2) => {
    const { serverName = table2.schema.name } = table2.schema;
    if (serverNames.has(serverName)) {
      throw new Error(`Multiple tables reference the name "${serverName}"`);
    }
    serverNames.add(serverName);
    if (retTables[table2.schema.name]) {
      throw new Error(
        `Table "${table2.schema.name}" is defined more than once in the schema`
      );
    }
    retTables[table2.schema.name] = table2.build();
  });
  options.relationships?.forEach((relationships2) => {
    if (retRelationships[relationships2.name]) {
      throw new Error(
        `Relationships for table "${relationships2.name}" are defined more than once in the schema`
      );
    }
    retRelationships[relationships2.name] = relationships2.relationships;
    checkRelationship(
      relationships2.relationships,
      relationships2.name,
      retTables
    );
  });
  return {
    tables: retTables,
    relationships: retRelationships
  };
}
function checkRelationship(relationships2, tableName, tables) {
  Object.entries(relationships2).forEach(([name, rel]) => {
    let source = tables[tableName];
    rel.forEach((connection) => {
      if (!tables[connection.destSchema]) {
        throw new Error(
          `For relationship "${tableName}"."${name}", destination table "${connection.destSchema}" is missing in the schema`
        );
      }
      if (!source.columns[connection.sourceField[0]]) {
        throw new Error(
          `For relationship "${tableName}"."${name}", the source field "${connection.sourceField[0]}" is missing in the table schema "${source.name}"`
        );
      }
      source = tables[connection.destSchema];
    });
  });
}
function clientSchemaFrom(schema) {
  const client = {
    tables: mapEntries(schema.tables, (name, { serverName, columns }) => [
      serverName ?? name,
      {
        columns: mapEntries(columns, (name2, { serverName: serverName2, type }) => [
          serverName2 ?? name2,
          { type }
        ])
      }
    ])
  };
  const clientSchema = normalizeClientSchema(client);
  const hash = h64(JSON.stringify(clientSchema)).toString(36);
  return { clientSchema, hash };
}

// ../zql/src/query/static-query.ts
var StaticQuery = class _StaticQuery extends AbstractQuery {
  expressionBuilder() {
    return new ExpressionBuilder(this._exists);
  }
  _system = "permissions";
  _newQuery(schema, tableName, ast, format) {
    return new _StaticQuery(schema, tableName, ast, format);
  }
  get ast() {
    return this._completeAst();
  }
  materialize() {
    throw new Error("StaticQuery cannot be materialized");
  }
  run() {
    return Promise.reject(new Error("StaticQuery cannot be run"));
  }
  preload() {
    throw new Error("StaticQuery cannot be preloaded");
  }
  updateTTL(_ttl) {
    throw new Error("StaticQuery cannot have a TTL");
  }
};

// ../zero-schema/src/name-mapper.ts
function clientToServer(tables) {
  return createMapperFrom("client", tables);
}
function serverToClient(tables) {
  return createMapperFrom("server", tables);
}
function createMapperFrom(src, tables) {
  const mapping = new Map(
    Object.entries(tables).map(
      ([tableName, { serverName: serverTableName, columns }]) => {
        let allColumnsSame = true;
        const names = {};
        for (const [name, { serverName }] of Object.entries(columns)) {
          if (serverName && serverName !== name) {
            allColumnsSame = false;
          }
          if (src === "client") {
            names[name] = serverName ?? name;
          } else {
            names[serverName ?? name] = name;
          }
        }
        return [
          src === "client" ? tableName : serverTableName ?? tableName,
          {
            tableName: src === "client" ? serverTableName ?? tableName : tableName,
            columns: names,
            allColumnsSame
          }
        ];
      }
    )
  );
  return new NameMapper(mapping);
}
var NameMapper = class {
  #tables = /* @__PURE__ */ new Map();
  constructor(tables) {
    this.#tables = tables;
  }
  #getTable(src, ctx) {
    const table2 = this.#tables.get(src);
    if (!table2) {
      throw new Error(
        `unknown table "${src}" ${!ctx ? "" : `in ${JSON.stringify(ctx)}`}`
      );
    }
    return table2;
  }
  tableName(src, context) {
    return this.#getTable(src, context).tableName;
  }
  columnName(table2, src, ctx) {
    const dst = this.#getTable(table2, ctx).columns[src];
    if (!dst) {
      throw new Error(
        `unknown column "${src}" of "${table2}" table ${!ctx ? "" : `in ${JSON.stringify(ctx)}`}`
      );
    }
    return dst;
  }
  row(table2, row) {
    const dest = this.#getTable(table2);
    const { allColumnsSame, columns } = dest;
    if (allColumnsSame) {
      return row;
    }
    const clientRow = {};
    for (const col in row) {
      clientRow[columns[col] ?? col] = row[col];
    }
    return clientRow;
  }
  columns(table2, cols) {
    const dest = this.#getTable(table2);
    const { allColumnsSame, columns } = dest;
    return cols === void 0 || allColumnsSame ? cols : cols.map((col) => columns[col] ?? col);
  }
};

// ../zero-schema/src/permissions.ts
var ANYONE_CAN = [
  (_, eb) => eb.and()
];
var ANYONE_CAN_DO_ANYTHING = {
  row: {
    select: ANYONE_CAN,
    insert: ANYONE_CAN,
    update: {
      preMutation: ANYONE_CAN,
      postMutation: ANYONE_CAN
    },
    delete: ANYONE_CAN
  }
};
var NOBODY_CAN = [];
async function definePermissions(schema, definer) {
  const expressionBuilders = {};
  for (const name of Object.keys(schema.tables)) {
    expressionBuilders[name] = new StaticQuery(
      schema,
      name,
      { table: name },
      defaultFormat
    ).expressionBuilder();
  }
  const config = await definer();
  return compilePermissions(schema, config, expressionBuilders);
}
function compilePermissions(schema, authz, expressionBuilders) {
  if (!authz) {
    return void 0;
  }
  const nameMapper = clientToServer(schema.tables);
  const ret = { tables: {} };
  for (const [tableName, tableConfig] of Object.entries(authz)) {
    const serverName = schema.tables[tableName].serverName ?? tableName;
    ret.tables[serverName] = {
      row: compileRowConfig(
        nameMapper,
        tableName,
        tableConfig.row,
        expressionBuilders[tableName]
      ),
      cell: compileCellConfig(
        nameMapper,
        tableName,
        tableConfig.cell,
        expressionBuilders[tableName]
      )
    };
  }
  return ret;
}
function compileRowConfig(clientToServer2, tableName, rowRules, expressionBuilder) {
  if (!rowRules) {
    return void 0;
  }
  return {
    select: compileRules(
      clientToServer2,
      tableName,
      rowRules.select,
      expressionBuilder
    ),
    insert: compileRules(
      clientToServer2,
      tableName,
      rowRules.insert,
      expressionBuilder
    ),
    update: {
      preMutation: compileRules(
        clientToServer2,
        tableName,
        rowRules.update?.preMutation,
        expressionBuilder
      ),
      postMutation: compileRules(
        clientToServer2,
        tableName,
        rowRules.update?.postMutation,
        expressionBuilder
      )
    },
    delete: compileRules(
      clientToServer2,
      tableName,
      rowRules.delete,
      expressionBuilder
    )
  };
}
function compileRules(clientToServer2, tableName, rules, expressionBuilder) {
  if (!rules) {
    return void 0;
  }
  return rules.map((rule) => {
    const cond = rule(authDataRef, expressionBuilder);
    return ["allow", mapCondition(cond, tableName, clientToServer2)];
  });
}
function compileCellConfig(clientToServer2, tableName, cellRules, expressionBuilder) {
  if (!cellRules) {
    return void 0;
  }
  const ret = {};
  for (const [columnName, rules] of Object.entries(cellRules)) {
    ret[columnName] = {
      select: compileRules(
        clientToServer2,
        tableName,
        rules.select,
        expressionBuilder
      ),
      insert: compileRules(
        clientToServer2,
        tableName,
        rules.insert,
        expressionBuilder
      ),
      update: {
        preMutation: compileRules(
          clientToServer2,
          tableName,
          rules.update?.preMutation,
          expressionBuilder
        ),
        postMutation: compileRules(
          clientToServer2,
          tableName,
          rules.update?.postMutation,
          expressionBuilder
        )
      },
      delete: compileRules(
        clientToServer2,
        tableName,
        rules.delete,
        expressionBuilder
      )
    };
  }
  return ret;
}
var CallTracker = class _CallTracker {
  #anchor;
  #path;
  constructor(anchor, path) {
    this.#anchor = anchor;
    this.#path = path;
  }
  get(target, prop) {
    if (prop === toStaticParam) {
      return target[toStaticParam];
    }
    assert(typeof prop === "string");
    const path = [...this.#path, prop];
    return new Proxy(
      {
        [toStaticParam]: () => staticParam(this.#anchor, path)
      },
      new _CallTracker(this.#anchor, path)
    );
  }
};
function baseTracker(anchor) {
  return new Proxy(
    {
      [toStaticParam]: () => {
        throw new Error("no JWT field specified");
      }
    },
    new CallTracker(anchor, [])
  );
}
var authDataRef = baseTracker("authData");
var preMutationRowRef = baseTracker("preMutationRow");

// ../zql/src/query/escape-like.ts
function escapeLike(val) {
  return val.replace(/[%_]/g, "\\$&");
}

// ../zero-client/src/client/zero.ts
import { LogContext as LogContext4 } from "@rocicorp/logger";
import { resolver as resolver8 } from "@rocicorp/resolver";

// ../zero-protocol/src/delete-clients.ts
var deleteClientsBodySchema = valita_exports.union(
  readonlyObject({
    clientIDs: readonlyArray(valita_exports.string()).optional(),
    clientGroupIDs: readonlyArray(valita_exports.string()).optional()
  })
);
var deleteClientsMessageSchema = valita_exports.tuple([
  valita_exports.literal("deleteClients"),
  deleteClientsBodySchema
]);

// ../zero-protocol/src/queries-patch.ts
var putOpSchema = valita_exports.object({
  op: valita_exports.literal("put"),
  hash: valita_exports.string(),
  ast: astSchema,
  ttl: valita_exports.number().optional()
});
var delOpSchema = valita_exports.object({
  op: valita_exports.literal("del"),
  hash: valita_exports.string()
});
var clearOpSchema = valita_exports.object({
  op: valita_exports.literal("clear")
});
var patchOpSchema = valita_exports.union(putOpSchema, delOpSchema, clearOpSchema);
var queriesPatchSchema = valita_exports.array(patchOpSchema);

// ../zero-protocol/src/connect.ts
var connectedBodySchema = valita_exports.object({
  wsid: valita_exports.string(),
  timestamp: valita_exports.number().optional()
});
var connectedMessageSchema = valita_exports.tuple([
  valita_exports.literal("connected"),
  connectedBodySchema
]);
var initConnectionBodySchema = valita_exports.object({
  desiredQueriesPatch: queriesPatchSchema,
  clientSchema: clientSchemaSchema.optional(),
  deleted: deleteClientsBodySchema.optional()
});
var initConnectionMessageSchema = valita_exports.tuple([
  valita_exports.literal("initConnection"),
  initConnectionBodySchema
]);
function encodeSecProtocols(initConnectionMessage, authToken) {
  const protocols = {
    initConnectionMessage,
    authToken
  };
  return encodeURIComponent(btoa(JSON.stringify(protocols)));
}

// ../zero-protocol/src/error-kind-enum.ts
var AuthInvalidated = "AuthInvalidated";
var ClientNotFound = "ClientNotFound";
var InvalidConnectionRequest = "InvalidConnectionRequest";
var InvalidConnectionRequestBaseCookie = "InvalidConnectionRequestBaseCookie";
var InvalidConnectionRequestLastMutationID = "InvalidConnectionRequestLastMutationID";
var InvalidConnectionRequestClientDeleted = "InvalidConnectionRequestClientDeleted";
var InvalidMessage = "InvalidMessage";
var InvalidPush = "InvalidPush";
var MutationFailed = "MutationFailed";
var MutationRateLimited = "MutationRateLimited";
var Rebalance = "Rebalance";
var Rehome = "Rehome";
var Unauthorized = "Unauthorized";
var VersionNotSupported = "VersionNotSupported";
var SchemaVersionNotSupported = "SchemaVersionNotSupported";
var ServerOverloaded = "ServerOverloaded";
var Internal = "Internal";

// ../zero-protocol/src/error.ts
var basicErrorKindSchema = valita_exports.union(
  valita_exports.literal(AuthInvalidated),
  valita_exports.literal(ClientNotFound),
  valita_exports.literal(InvalidConnectionRequest),
  valita_exports.literal(InvalidConnectionRequestBaseCookie),
  valita_exports.literal(InvalidConnectionRequestLastMutationID),
  valita_exports.literal(InvalidConnectionRequestClientDeleted),
  valita_exports.literal(InvalidMessage),
  valita_exports.literal(InvalidPush),
  valita_exports.literal(MutationRateLimited),
  valita_exports.literal(MutationFailed),
  valita_exports.literal(Unauthorized),
  valita_exports.literal(VersionNotSupported),
  valita_exports.literal(SchemaVersionNotSupported),
  valita_exports.literal(Internal)
);
var basicErrorBodySchema = valita_exports.object({
  kind: basicErrorKindSchema,
  message: valita_exports.string()
});
var backoffErrorKindSchema = valita_exports.union(
  valita_exports.literal(Rebalance),
  valita_exports.literal(Rehome),
  valita_exports.literal(ServerOverloaded)
);
var backoffBodySchema = valita_exports.object({
  kind: backoffErrorKindSchema,
  message: valita_exports.string(),
  minBackoffMs: valita_exports.number().optional(),
  maxBackoffMs: valita_exports.number().optional(),
  // Query parameters to send in the next reconnect. In the event of
  // a conflict, these will be overridden by the parameters used by
  // the client; it is the responsibility of the server to avoid
  // parameter name conflicts.
  //
  // The parameters will only be added to the immediately following
  // reconnect, and not after that.
  reconnectParams: valita_exports.record(valita_exports.string()).optional()
});
var errorBodySchema = valita_exports.union(basicErrorBodySchema, backoffBodySchema);
var errorMessageSchema = valita_exports.tuple([
  valita_exports.literal("error"),
  errorBodySchema
]);

// ../zero-protocol/src/row-patch.ts
var putOpSchema2 = valita_exports.object({
  op: valita_exports.literal("put"),
  tableName: valita_exports.string(),
  value: rowSchema
});
var updateOpSchema = valita_exports.object({
  op: valita_exports.literal("update"),
  tableName: valita_exports.string(),
  id: primaryKeyValueRecordSchema,
  merge: jsonObjectSchema.optional(),
  constrain: valita_exports.array(valita_exports.string()).optional()
});
var delOpSchema2 = valita_exports.object({
  op: valita_exports.literal("del"),
  tableName: valita_exports.string(),
  id: primaryKeyValueRecordSchema
});
var clearOpSchema2 = valita_exports.object({
  op: valita_exports.literal("clear")
});
var rowPatchOpSchema = valita_exports.union(
  putOpSchema2,
  updateOpSchema,
  delOpSchema2,
  clearOpSchema2
);
var rowsPatchSchema = valita_exports.array(rowPatchOpSchema);

// ../zero-protocol/src/version.ts
var versionSchema = valita_exports.string();
var nullableVersionSchema = valita_exports.union(versionSchema, valita_exports.null());

// ../zero-protocol/src/poke.ts
var pokeStartBodySchema = valita_exports.object({
  pokeID: valita_exports.string(),
  // We always specify a Version as our cookie, but Replicache starts clients
  // with initial cookie `null`, before the first request. So we have to be
  // able to send a base cookie with value `null` to match that state.
  baseCookie: nullableVersionSchema,
  /**
   * This field is always set if the poke contains a `rowsPatch`.
   * It may be absent for patches that only update clients and queries.
   */
  schemaVersions: valita_exports.object({
    minSupportedVersion: valita_exports.number(),
    maxSupportedVersion: valita_exports.number()
  }).optional(),
  timestamp: valita_exports.number().optional()
});
var pokePartBodySchema = valita_exports.object({
  pokeID: valita_exports.string(),
  // Changes to last mutation id by client id.
  lastMutationIDChanges: valita_exports.record(valita_exports.number()).optional(),
  // Patches to the desired query sets by client id.
  desiredQueriesPatches: valita_exports.record(queriesPatchSchema).optional(),
  // Patches to the set of queries for which entities are sync'd in
  // rowsPatch.
  gotQueriesPatch: queriesPatchSchema.optional(),
  // Patches to the rows set.
  rowsPatch: rowsPatchSchema.optional()
});
var pokeEndBodySchema = valita_exports.object({
  pokeID: valita_exports.string(),
  // Note: This should be ignored (and may be empty) if cancel === `true`.
  cookie: versionSchema,
  // If `true`, the poke with id `pokeID` should be discarded without
  // applying it.
  cancel: valita_exports.boolean().optional()
});
var pokeStartMessageSchema = valita_exports.tuple([
  valita_exports.literal("pokeStart"),
  pokeStartBodySchema
]);
var pokePartMessageSchema = valita_exports.tuple([
  valita_exports.literal("pokePart"),
  pokePartBodySchema
]);
var pokeEndMessageSchema = valita_exports.tuple([
  valita_exports.literal("pokeEnd"),
  pokeEndBodySchema
]);

// ../zero-protocol/src/pong.ts
var pongBodySchema = valita_exports.object({});
var pongMessageSchema = valita_exports.tuple([valita_exports.literal("pong"), pongBodySchema]);

// ../zero-protocol/src/pull.ts
var pullRequestBodySchema = valita_exports.object({
  clientGroupID: valita_exports.string(),
  cookie: nullableVersionSchema,
  requestID: valita_exports.string()
});
var pullResponseBodySchema = valita_exports.object({
  cookie: versionSchema,
  // Matches pullRequestBodySchema requestID that initiated this response
  requestID: valita_exports.string(),
  lastMutationIDChanges: valita_exports.record(valita_exports.number())
  // Pull is currently only used for mutation recovery which does not use
  // the patch so we save work by not computing the patch.
});
var pullRequestMessageSchema = valita_exports.tuple([
  valita_exports.literal("pull"),
  pullRequestBodySchema
]);
var pullResponseMessageSchema = valita_exports.tuple([
  valita_exports.literal("pull"),
  pullResponseBodySchema
]);

// ../zero-protocol/src/mutation-type-enum.ts
var CRUD = "crud";
var Custom = "custom";

// ../zero-protocol/src/push.ts
var CRUD_MUTATION_NAME = "_zero_crud";
var insertOpSchema = valita_exports.object({
  op: valita_exports.literal("insert"),
  tableName: valita_exports.string(),
  primaryKey: primaryKeySchema,
  value: rowSchema
});
var upsertOpSchema = valita_exports.object({
  op: valita_exports.literal("upsert"),
  tableName: valita_exports.string(),
  primaryKey: primaryKeySchema,
  value: rowSchema
});
var updateOpSchema2 = valita_exports.object({
  op: valita_exports.literal("update"),
  tableName: valita_exports.string(),
  primaryKey: primaryKeySchema,
  // Partial value with at least the primary key fields
  value: rowSchema
});
var deleteOpSchema = valita_exports.object({
  op: valita_exports.literal("delete"),
  tableName: valita_exports.string(),
  primaryKey: primaryKeySchema,
  // Partial value representing the primary key
  value: primaryKeyValueRecordSchema
});
var crudOpSchema = valita_exports.union(
  insertOpSchema,
  upsertOpSchema,
  updateOpSchema2,
  deleteOpSchema
);
var crudArgSchema = valita_exports.object({
  ops: valita_exports.array(crudOpSchema)
});
var crudArgsSchema = valita_exports.tuple([crudArgSchema]);
var crudMutationSchema = valita_exports.object({
  type: valita_exports.literal(CRUD),
  id: valita_exports.number(),
  clientID: valita_exports.string(),
  name: valita_exports.literal(CRUD_MUTATION_NAME),
  args: crudArgsSchema,
  timestamp: valita_exports.number()
});
var customMutationSchema = valita_exports.object({
  type: valita_exports.literal(Custom),
  id: valita_exports.number(),
  clientID: valita_exports.string(),
  name: valita_exports.string(),
  args: valita_exports.array(jsonSchema),
  timestamp: valita_exports.number()
});
var mutationSchema = valita_exports.union(crudMutationSchema, customMutationSchema);
var pushBodySchema = valita_exports.object({
  clientGroupID: valita_exports.string(),
  mutations: valita_exports.array(mutationSchema),
  pushVersion: valita_exports.number(),
  // For legacy (CRUD) mutations, the schema is tied to the client group /
  // sync connection. For custom mutations, schema versioning is delegated
  // to the custom protocol / api-server.
  schemaVersion: valita_exports.number().optional(),
  timestamp: valita_exports.number(),
  requestID: valita_exports.string()
});
var pushMessageSchema = valita_exports.tuple([valita_exports.literal("push"), pushBodySchema]);
var mutationIDSchema = valita_exports.object({
  id: valita_exports.number(),
  clientID: valita_exports.string()
});
var appErrorSchema = valita_exports.object({
  error: valita_exports.literal("app"),
  // The user can return any additional data here
  details: jsonSchema.optional()
});
var zeroErrorSchema = valita_exports.object({
  error: valita_exports.literal("oooMutation"),
  details: jsonSchema.optional()
});
var mutationOkSchema = valita_exports.object({
  // The user can return any additional data here
  data: jsonSchema.optional()
});
var mutationErrorSchema = valita_exports.union(appErrorSchema, zeroErrorSchema);
var mutationResultSchema = valita_exports.union(mutationOkSchema, mutationErrorSchema);
var mutationResponseSchema = valita_exports.object({
  id: mutationIDSchema,
  result: mutationResultSchema
});
var pushOkSchema = valita_exports.object({
  mutations: valita_exports.array(mutationResponseSchema)
});
var unsupportedPushVersionSchema = valita_exports.object({
  error: valita_exports.literal("unsupportedPushVersion"),
  // optional for backwards compatibility
  // This field is included so the client knows which mutations
  // were not processed by the server.
  mutationIDs: valita_exports.array(mutationIDSchema).optional()
});
var unsupportedSchemaVersionSchema = valita_exports.object({
  error: valita_exports.literal("unsupportedSchemaVersion"),
  // optional for backwards compatibility
  // This field is included so the client knows which mutations
  // were not processed by the server.
  mutationIDs: valita_exports.array(mutationIDSchema).optional()
});
var httpErrorSchema = valita_exports.object({
  error: valita_exports.literal("http"),
  status: valita_exports.number(),
  details: valita_exports.string(),
  mutationIDs: valita_exports.array(mutationIDSchema).optional()
});
var zeroPusherErrorSchema = valita_exports.object({
  error: valita_exports.literal("zeroPusher"),
  details: valita_exports.string(),
  mutationIDs: valita_exports.array(mutationIDSchema).optional()
});
var pushErrorSchema = valita_exports.union(
  unsupportedPushVersionSchema,
  unsupportedSchemaVersionSchema,
  httpErrorSchema,
  zeroPusherErrorSchema
);
var pushResponseSchema = valita_exports.union(pushOkSchema, pushErrorSchema);
var pushResponseMessageSchema = valita_exports.tuple([
  valita_exports.literal("pushResponse"),
  pushResponseSchema
]);
var pushParamsSchema = valita_exports.object({
  schema: valita_exports.string(),
  appID: valita_exports.string()
});
function mapCRUD(arg, map) {
  return {
    ops: arg.ops.map(
      ({ op, tableName, primaryKey, value }) => ({
        op,
        tableName: map.tableName(tableName),
        primaryKey: map.columns(tableName, primaryKey),
        value: map.row(tableName, value)
        // The cast is necessary because ts objects to the `value` field
        // for "delete" ops being different.
      })
    )
  };
}

// ../zero-protocol/src/down.ts
var downstreamSchema = valita_exports.union(
  connectedMessageSchema,
  errorMessageSchema,
  pongMessageSchema,
  pokeStartMessageSchema,
  pokePartMessageSchema,
  pokeEndMessageSchema,
  pullResponseMessageSchema,
  deleteClientsMessageSchema,
  pushResponseMessageSchema,
  inspectDownMessageSchema
);

// ../zero-protocol/src/protocol-version.ts
var PROTOCOL_VERSION = 14;
var MIN_SERVER_SUPPORTED_SYNC_PROTOCOL = 6;
assert(MIN_SERVER_SUPPORTED_SYNC_PROTOCOL < PROTOCOL_VERSION);

// ../zql/src/mutate/custom.ts
function customMutatorKey(namespace, name) {
  assert(!namespace.includes("|"), "mutator namespaces must not include a |");
  assert(!name.includes("|"), "mutator names must not include a |");
  return `${namespace}|${name}`;
}

// ../zero-client/src/util/socket.ts
function send(ws, data) {
  ws.send(JSON.stringify(data));
}

// ../zero-client/src/client/connection-state-enum.ts
var Disconnected = 0;
var Connecting = 1;
var Connected = 2;

// ../zql/src/ivm/memory-storage.ts
import { compareUTF8 as compareUTF82 } from "compare-utf8";

// ../shared/src/btree-set.ts
var MAX_NODE_SIZE = 32;
var BTreeSet = class _BTreeSet {
  #root = emptyLeaf;
  size = 0;
  comparator;
  constructor(comparator2, entries) {
    this.comparator = comparator2;
    if (entries) {
      for (const key of entries) {
        this.add(key);
      }
    }
  }
  /** Releases the tree so that its size is 0. */
  clear() {
    this.#root = emptyLeaf;
    this.size = 0;
  }
  clone() {
    this.#root.isShared = true;
    const ret = new _BTreeSet(this.comparator);
    ret.#root = this.#root;
    ret.size = this.size;
    return ret;
  }
  get(key) {
    return this.#root.get(key, this);
  }
  add(key) {
    if (this.#root.isShared) this.#root = this.#root.clone();
    const result = this.#root.set(key, this);
    if (result === null) return this;
    this.#root = new BNodeInternal([this.#root, result]);
    return this;
  }
  /**
   * Returns true if the key exists in the B+ tree, false if not.
   * Use get() for best performance; use has() if you need to
   * distinguish between "undefined value" and "key not present".
   * @param key Key to detect
   * @description Computational complexity: O(log size)
   */
  has(key) {
    return this.#root.has(key, this);
  }
  /**
   * Removes a single key-value pair from the B+ tree.
   * @param key Key to find
   * @returns true if a pair was found and removed, false otherwise.
   * @description Computational complexity: O(log size)
   */
  delete(key) {
    return this.#delete(key);
  }
  #delete(key) {
    let root = this.#root;
    if (root.isShared) {
      this.#root = root = root.clone();
    }
    try {
      return root.delete(key, this);
    } finally {
      let isShared;
      while (root.keys.length <= 1 && root.isInternal()) {
        isShared ||= root.isShared;
        this.#root = root = root.keys.length === 0 ? emptyLeaf : root.children[0];
      }
      if (isShared) {
        root.isShared = true;
      }
    }
  }
  keys() {
    return valuesFrom(this.#root, this.comparator, void 0, true);
  }
  values() {
    return valuesFrom(this.#root, this.comparator, void 0, true);
  }
  valuesFrom(lowestKey, inclusive = true) {
    return valuesFrom(this.#root, this.comparator, lowestKey, inclusive);
  }
  valuesReversed() {
    return valuesFromReversed(
      this.#maxKey(),
      this.#root,
      this.comparator,
      void 0,
      true
    );
  }
  valuesFromReversed(highestKey, inclusive = true) {
    return valuesFromReversed(
      this.#maxKey(),
      this.#root,
      this.comparator,
      highestKey,
      inclusive
    );
  }
  /** Gets the highest key in the tree. Complexity: O(1) */
  #maxKey() {
    return this.#root.maxKey();
  }
  [Symbol.iterator]() {
    return this.keys();
  }
};
function valuesFrom(root, comparator2, lowestKey, inclusive) {
  const info = findPath(lowestKey, root, comparator2);
  if (info === void 0) {
    return iterator(() => ({ done: true, value: void 0 }));
  }
  let [nodeQueue, nodeIndex, leaf] = info;
  let i = lowestKey === void 0 ? -1 : indexOf(lowestKey, leaf.keys, 0, comparator2) - 1;
  if (!inclusive && i < leaf.keys.length && // +1 because we did -1 above.
  comparator2(leaf.keys[i + 1], lowestKey) === 0) {
    i++;
  }
  return iterator(() => {
    for (; ; ) {
      if (++i < leaf.keys.length) {
        return { done: false, value: leaf.keys[i] };
      }
      let level = -1;
      for (; ; ) {
        if (++level >= nodeQueue.length) {
          return { done: true, value: void 0 };
        }
        if (++nodeIndex[level] < nodeQueue[level].length) {
          break;
        }
      }
      for (; level > 0; level--) {
        nodeQueue[level - 1] = nodeQueue[level][nodeIndex[level]].children;
        nodeIndex[level - 1] = 0;
      }
      leaf = nodeQueue[0][nodeIndex[0]];
      i = -1;
    }
  });
}
function valuesFromReversed(maxKey, root, comparator2, highestKey, inclusive) {
  if (highestKey === void 0) {
    highestKey = maxKey;
    if (highestKey === void 0)
      return iterator(() => ({ done: true, value: void 0 }));
  }
  let [nodeQueue, nodeIndex, leaf] = findPath(highestKey, root, comparator2) || findPath(maxKey, root, comparator2);
  assert(!nodeQueue[0] || leaf === nodeQueue[0][nodeIndex[0]]);
  let i = indexOf(highestKey, leaf.keys, 0, comparator2);
  if (inclusive && i < leaf.keys.length && comparator2(leaf.keys[i], highestKey) <= 0) {
    i++;
  }
  return iterator(() => {
    for (; ; ) {
      if (--i >= 0) {
        return { done: false, value: leaf.keys[i] };
      }
      let level;
      for (level = -1; ; ) {
        if (++level >= nodeQueue.length) {
          return { done: true, value: void 0 };
        }
        if (--nodeIndex[level] >= 0) {
          break;
        }
      }
      for (; level > 0; level--) {
        nodeQueue[level - 1] = nodeQueue[level][nodeIndex[level]].children;
        nodeIndex[level - 1] = nodeQueue[level - 1].length - 1;
      }
      leaf = nodeQueue[0][nodeIndex[0]];
      i = leaf.keys.length;
    }
  });
}
function findPath(key, root, comparator2) {
  let nextNode = root;
  const nodeQueue = [];
  const nodeIndex = [];
  if (nextNode.isInternal()) {
    for (let d = 0; nextNode.isInternal(); d++) {
      nodeQueue[d] = nextNode.children;
      nodeIndex[d] = key === void 0 ? 0 : indexOf(key, nextNode.keys, 0, comparator2);
      if (nodeIndex[d] >= nodeQueue[d].length) return;
      nextNode = nodeQueue[d][nodeIndex[d]];
    }
    nodeQueue.reverse();
    nodeIndex.reverse();
  }
  return [nodeQueue, nodeIndex, nextNode];
}
function iterator(next) {
  return {
    next,
    [Symbol.iterator]() {
      return this;
    }
  };
}
var BNode = class _BNode {
  // If this is an internal node, _keys[i] is the highest key in children[i].
  keys;
  // True if this node might be within multiple `BTree`s (or have multiple parents).
  // If so, it must be cloned before being mutated to avoid changing an unrelated tree.
  // This is transitive: if it's true, children are also shared even if `isShared!=true`
  // in those children. (Certain operations will propagate isShared=true to children.)
  isShared;
  constructor(keys) {
    this.keys = keys;
    this.isShared = void 0;
  }
  isInternal() {
    return false;
  }
  maxKey() {
    return this.keys[this.keys.length - 1];
  }
  minKey() {
    return this.keys[0];
  }
  clone() {
    return new _BNode(this.keys.slice(0));
  }
  get(key, tree) {
    const i = indexOf(key, this.keys, -1, tree.comparator);
    return i < 0 ? void 0 : this.keys[i];
  }
  has(key, tree) {
    const i = indexOf(key, this.keys, -1, tree.comparator);
    return i >= 0 && i < this.keys.length;
  }
  set(key, tree) {
    let i = indexOf(key, this.keys, -1, tree.comparator);
    if (i < 0) {
      i = ~i;
      tree.size++;
      if (this.keys.length < MAX_NODE_SIZE) {
        this.keys.splice(i, 0, key);
        return null;
      }
      const newRightSibling = this.splitOffRightSide();
      let target = this;
      if (i > this.keys.length) {
        i -= this.keys.length;
        target = newRightSibling;
      }
      target.keys.splice(i, 0, key);
      return newRightSibling;
    }
    this.keys[i] = key;
    return null;
  }
  takeFromRight(rhs) {
    this.keys.push(rhs.keys.shift());
  }
  takeFromLeft(lhs) {
    this.keys.unshift(lhs.keys.pop());
  }
  splitOffRightSide() {
    const half = this.keys.length >> 1;
    const keys = this.keys.splice(half);
    return new _BNode(keys);
  }
  delete(key, tree) {
    const cmp = tree.comparator;
    const iLow = indexOf(key, this.keys, -1, cmp);
    const iHigh = iLow + 1;
    if (iLow < 0) {
      return false;
    }
    const { keys } = this;
    for (let i = iLow; i < iHigh; i++) {
      const key2 = keys[i];
      if (key2 !== keys[i] || this.isShared === true) {
        throw new Error("BTree illegally changed or cloned in delete");
      }
      this.keys.splice(i, 1);
      tree.size--;
      return true;
    }
    return false;
  }
  mergeSibling(rhs, _) {
    this.keys.push(...rhs.keys);
  }
};
var BNodeInternal = class _BNodeInternal extends BNode {
  // Note: conventionally B+ trees have one fewer key than the number of
  // children, but I find it easier to keep the array lengths equal: each
  // keys[i] caches the value of children[i].maxKey().
  children;
  /**
   * This does not mark `children` as shared, so it is the responsibility of the caller
   * to ensure children are either marked shared, or aren't included in another tree.
   */
  constructor(children, keys) {
    if (!keys) {
      keys = [];
      for (let i = 0; i < children.length; i++) {
        keys[i] = children[i].maxKey();
      }
    }
    super(keys);
    this.children = children;
  }
  isInternal() {
    return true;
  }
  clone() {
    const children = this.children.slice(0);
    for (let i = 0; i < children.length; i++) {
      children[i].isShared = true;
    }
    return new _BNodeInternal(children, this.keys.slice(0));
  }
  minKey() {
    return this.children[0].minKey();
  }
  get(key, tree) {
    const i = indexOf(key, this.keys, 0, tree.comparator);
    const { children } = this;
    return i < children.length ? children[i].get(key, tree) : void 0;
  }
  has(key, tree) {
    const i = indexOf(key, this.keys, 0, tree.comparator);
    const { children } = this;
    return i < children.length ? children[i].has(key, tree) : false;
  }
  set(key, tree) {
    const c = this.children;
    const cmp = tree.comparator;
    let i = Math.min(indexOf(key, this.keys, 0, cmp), c.length - 1);
    let child = c[i];
    if (child.isShared) {
      c[i] = child = child.clone();
    }
    if (child.keys.length >= MAX_NODE_SIZE) {
      let other;
      if (i > 0 && (other = c[i - 1]).keys.length < MAX_NODE_SIZE && cmp(child.keys[0], key) < 0) {
        if (other.isShared) {
          c[i - 1] = other = other.clone();
        }
        other.takeFromRight(child);
        this.keys[i - 1] = other.maxKey();
      } else if ((other = c[i + 1]) !== void 0 && other.keys.length < MAX_NODE_SIZE && cmp(child.maxKey(), key) < 0) {
        if (other.isShared) c[i + 1] = other = other.clone();
        other.takeFromLeft(child);
        this.keys[i] = c[i].maxKey();
      }
    }
    const result = child.set(key, tree);
    this.keys[i] = child.maxKey();
    if (result === null) return null;
    if (this.keys.length < MAX_NODE_SIZE) {
      this.insert(i + 1, result);
      return null;
    }
    const newRightSibling = this.splitOffRightSide();
    let target = this;
    if (cmp(result.maxKey(), this.maxKey()) > 0) {
      target = newRightSibling;
      i -= this.keys.length;
    }
    target.insert(i + 1, result);
    return newRightSibling;
  }
  /**
   * Inserts `child` at index `i`.
   * This does not mark `child` as shared, so it is the responsibility of the caller
   * to ensure that either child is marked shared, or it is not included in another tree.
   */
  insert(i, child) {
    this.children.splice(i, 0, child);
    this.keys.splice(i, 0, child.maxKey());
  }
  /**
   * Split this node.
   * Modifies this to remove the second half of the items, returning a separate node containing them.
   */
  splitOffRightSide() {
    const half = this.children.length >> 1;
    return new _BNodeInternal(
      this.children.splice(half),
      this.keys.splice(half)
    );
  }
  takeFromRight(rhs) {
    this.keys.push(rhs.keys.shift());
    this.children.push(rhs.children.shift());
  }
  takeFromLeft(lhs) {
    this.keys.unshift(lhs.keys.pop());
    this.children.unshift(lhs.children.pop());
  }
  delete(key, tree) {
    const cmp = tree.comparator;
    const { keys } = this;
    const { children } = this;
    let iLow = indexOf(key, this.keys, 0, cmp);
    let i = iLow;
    const iHigh = Math.min(iLow, keys.length - 1);
    if (i <= iHigh) {
      try {
        if (children[i].isShared) {
          children[i] = children[i].clone();
        }
        const result = children[i].delete(key, tree);
        keys[i] = children[i].maxKey();
        return result;
      } finally {
        const half = MAX_NODE_SIZE >> 1;
        if (iLow > 0) iLow--;
        for (i = iHigh; i >= iLow; i--) {
          if (children[i].keys.length <= half) {
            if (children[i].keys.length !== 0) {
              this.tryMerge(i, MAX_NODE_SIZE);
            } else {
              keys.splice(i, 1);
              children.splice(i, 1);
            }
          }
        }
      }
    }
    return false;
  }
  /** Merges child i with child i+1 if their combined size is not too large */
  tryMerge(i, maxSize) {
    const { children } = this;
    if (i >= 0 && i + 1 < children.length) {
      if (children[i].keys.length + children[i + 1].keys.length <= maxSize) {
        if (children[i].isShared)
          children[i] = children[i].clone();
        children[i].mergeSibling(children[i + 1], maxSize);
        children.splice(i + 1, 1);
        this.keys.splice(i + 1, 1);
        this.keys[i] = children[i].maxKey();
        return true;
      }
    }
    return false;
  }
  /**
   * Move children from `rhs` into this.
   * `rhs` must be part of this tree, and be removed from it after this call
   * (otherwise isShared for its children could be incorrect).
   */
  mergeSibling(rhs, maxNodeSize) {
    const oldLength = this.keys.length;
    this.keys.push(...rhs.keys);
    const rhsChildren = rhs.children;
    this.children.push(...rhsChildren);
    if (rhs.isShared && !this.isShared) {
      for (let i = 0; i < rhsChildren.length; i++) {
        rhsChildren[i].isShared = true;
      }
    }
    this.tryMerge(oldLength - 1, maxNodeSize);
  }
};
function indexOf(key, keys, failXor, comparator2) {
  let lo = 0;
  let hi = keys.length;
  let mid = hi >> 1;
  while (lo < hi) {
    const c = comparator2(keys[mid], key);
    if (c < 0) {
      lo = mid + 1;
    } else if (c > 0) {
      hi = mid;
    } else if (c === 0) {
      return mid;
    } else {
      if (key === key) {
        return keys.length;
      }
      throw new Error("NaN was used as a key");
    }
    mid = lo + hi >> 1;
  }
  return mid ^ failXor;
}
var emptyLeaf = new BNode([]);
emptyLeaf.isShared = true;

// ../zql/src/ivm/memory-storage.ts
function comparator(a, b) {
  return compareUTF82(a[0], b[0]);
}
var MemoryStorage = class {
  #data = new BTreeSet(comparator);
  set(key, value) {
    this.#data.add([key, value]);
  }
  get(key, def) {
    const r = this.#data.get([key, null]);
    if (r !== void 0) {
      return r[1];
    }
    return def;
  }
  del(key) {
    this.#data.delete([key, null]);
  }
  *scan(options) {
    for (const entry of this.#data.valuesFrom(
      options && [options.prefix, null]
    )) {
      if (options && !entry[0].startsWith(options.prefix)) {
        return;
      }
      yield entry;
    }
  }
  cloneData() {
    return structuredClone(Object.fromEntries(this.#data.values()));
  }
};

// ../zql/src/ivm/constraint.ts
function constraintMatchesRow(constraint, row) {
  for (const key in constraint) {
    if (!valuesEqual(row[key], constraint[key])) {
      return false;
    }
  }
  return true;
}
function constraintMatchesPrimaryKey(constraint, primary) {
  const constraintKeys = Object.keys(constraint);
  if (constraintKeys.length !== primary.length) {
    return false;
  }
  constraintKeys.sort(stringCompare);
  for (let i = 0; i < constraintKeys.length; i++) {
    if (constraintKeys[i][0] !== primary[i]) {
      return false;
    }
  }
  return true;
}

// ../zql/src/ivm/memory-source.ts
var MemorySource = class _MemorySource {
  #tableName;
  #columns;
  #primaryKey;
  #primaryIndexSort;
  #indexes = /* @__PURE__ */ new Map();
  #connections = [];
  #overlay;
  #splitEditOverlay;
  constructor(tableName, columns, primaryKey, primaryIndexData) {
    this.#tableName = tableName;
    this.#columns = columns;
    this.#primaryKey = primaryKey;
    this.#primaryIndexSort = primaryKey.map((k) => [k, "asc"]);
    const comparator2 = makeBoundComparator(this.#primaryIndexSort);
    this.#indexes.set(JSON.stringify(this.#primaryIndexSort), {
      comparator: comparator2,
      data: primaryIndexData ?? new BTreeSet(comparator2),
      usedBy: /* @__PURE__ */ new Set()
    });
    assertOrderingIncludesPK(this.#primaryIndexSort, this.#primaryKey);
  }
  // Mainly for tests.
  getSchemaInfo() {
    return {
      tableName: this.#tableName,
      columns: this.#columns,
      primaryKey: this.#primaryKey
    };
  }
  fork() {
    const primaryIndex = this.#getPrimaryIndex();
    return new _MemorySource(
      this.#tableName,
      this.#columns,
      this.#primaryKey,
      primaryIndex.data.clone()
    );
  }
  #getSchema(connection) {
    return {
      tableName: this.#tableName,
      columns: this.#columns,
      primaryKey: this.#primaryKey,
      sort: connection.sort,
      system: "client",
      relationships: {},
      isHidden: false,
      compareRows: connection.compareRows
    };
  }
  connect(sort, filters, splitEditKeys) {
    const transformedFilters = transformFilters(filters);
    const input = {
      getSchema: () => schema,
      fetch: (req) => this.#fetch(req, connection),
      cleanup: (req) => this.#cleanup(req, connection),
      setOutput: (output) => {
        connection.output = output;
      },
      destroy: () => {
        this.#disconnect(input);
      },
      fullyAppliedFilters: !transformedFilters.conditionsRemoved
    };
    const connection = {
      input,
      output: void 0,
      sort,
      splitEditKeys,
      compareRows: makeComparator(sort),
      filters: transformedFilters.filters ? {
        condition: transformedFilters.filters,
        predicate: createPredicate(transformedFilters.filters)
      } : void 0
    };
    const schema = this.#getSchema(connection);
    assertOrderingIncludesPK(sort, this.#primaryKey);
    this.#connections.push(connection);
    return input;
  }
  #disconnect(input) {
    const idx = this.#connections.findIndex((c) => c.input === input);
    assert(idx !== -1, "Connection not found");
    const connection = this.#connections[idx];
    this.#connections.splice(idx, 1);
    const primaryIndexKey = JSON.stringify(this.#primaryIndexSort);
    for (const [key, index] of this.#indexes) {
      if (key === primaryIndexKey) {
        continue;
      }
      index.usedBy.delete(connection);
      if (index.usedBy.size === 0) {
        this.#indexes.delete(key);
      }
    }
  }
  #getPrimaryIndex() {
    const index = this.#indexes.get(JSON.stringify(this.#primaryIndexSort));
    assert(index, "Primary index not found");
    return index;
  }
  #getOrCreateIndex(sort, usedBy) {
    const key = JSON.stringify(sort);
    const index = this.#indexes.get(key);
    if (index) {
      index.usedBy.add(usedBy);
      return index;
    }
    const comparator2 = makeBoundComparator(sort);
    const data = new BTreeSet(comparator2);
    for (const row of this.#getPrimaryIndex().data) {
      data.add(row);
    }
    const newIndex = { comparator: comparator2, data, usedBy: /* @__PURE__ */ new Set([usedBy]) };
    this.#indexes.set(key, newIndex);
    return newIndex;
  }
  // For unit testing that we correctly clean up indexes.
  getIndexKeys() {
    return [...this.#indexes.keys()];
  }
  *#fetch(req, from) {
    const callingConnectionIndex = this.#connections.indexOf(from);
    assert(callingConnectionIndex !== -1, "Output not found");
    const conn = this.#connections[callingConnectionIndex];
    const { sort: requestedSort } = conn;
    const indexSort = [];
    if (req.constraint) {
      for (const key of Object.keys(req.constraint)) {
        indexSort.push([key, "asc"]);
      }
    }
    if (this.#primaryKey.length > 1 || !req.constraint || !constraintMatchesPrimaryKey(req.constraint, this.#primaryKey)) {
      indexSort.push(...requestedSort);
    }
    const index = this.#getOrCreateIndex(indexSort, from);
    const { data, comparator: compare } = index;
    const comparator2 = (r1, r2) => compare(r1, r2) * (req.reverse ? -1 : 1);
    const startAt = req.start?.row;
    let scanStart;
    if (req.constraint) {
      scanStart = {};
      for (const [key, dir] of indexSort) {
        if (hasOwn(req.constraint, key)) {
          scanStart[key] = req.constraint[key];
        } else {
          if (req.reverse) {
            scanStart[key] = dir === "asc" ? maxValue : minValue;
          } else {
            scanStart[key] = dir === "asc" ? minValue : maxValue;
          }
        }
      }
    } else {
      scanStart = startAt;
    }
    const withOverlay = generateWithOverlay(
      startAt,
      generateRows(data, scanStart, req.reverse),
      req.constraint,
      this.#overlay,
      this.#splitEditOverlay,
      callingConnectionIndex,
      comparator2,
      conn.filters?.predicate
    );
    const withConstraint = generateWithConstraint(
      generateWithStart(withOverlay, req.start, comparator2),
      req.constraint
    );
    yield* conn.filters ? generateWithFilter(withConstraint, conn.filters.predicate) : withConstraint;
  }
  #cleanup(req, connection) {
    return this.#fetch(req, connection);
  }
  push(change) {
    for (const _ of this.genPush(change)) {
    }
  }
  *genPush(change) {
    const primaryIndex = this.#getPrimaryIndex();
    const { data } = primaryIndex;
    const exists = (row) => data.has(row);
    const setOverlay = (o) => this.#overlay = o;
    const setSplitEditOverlay = (o) => this.#splitEditOverlay = o;
    if (change.type === "set") {
      const existing = data.get(change.row);
      if (existing !== void 0) {
        change = {
          type: "edit",
          row: change.row,
          oldRow: existing
        };
      } else {
        change = {
          type: "add",
          row: change.row
        };
      }
    }
    for (const x of genPush(
      change,
      exists,
      this.#connections.entries(),
      setOverlay,
      setSplitEditOverlay
    )) {
      yield x;
    }
    for (const { data: data2 } of this.#indexes.values()) {
      switch (change.type) {
        case "add": {
          const added = data2.add(change.row);
          assert(added);
          break;
        }
        case "remove": {
          const removed = data2.delete(change.row);
          assert(removed);
          break;
        }
        case "edit": {
          const removed = data2.delete(change.oldRow);
          assert(removed);
          data2.add(change.row);
          break;
        }
        default:
          unreachable(change);
      }
    }
  }
};
function* generateWithConstraint(it, constraint) {
  for (const node of it) {
    if (constraint && !constraintMatchesRow(constraint, node.row)) {
      break;
    }
    yield node;
  }
}
function* generateWithFilter(it, filter) {
  for (const node of it) {
    if (filter(node.row)) {
      yield node;
    }
  }
}
function* genPush(change, exists, connections, setOverlay, setSplitEditOverlay) {
  switch (change.type) {
    case "add":
      assert(
        !exists(change.row),
        () => `Row already exists ${stringify(change)}`
      );
      break;
    case "remove":
      assert(exists(change.row), () => `Row not found ${stringify(change)}`);
      break;
    case "edit":
      assert(exists(change.oldRow), () => `Row not found ${stringify(change)}`);
      break;
    default:
      unreachable(change);
  }
  for (const [outputIndex, { output, splitEditKeys, filters }] of connections) {
    if (output) {
      let splitEdit = false;
      if (change.type === "edit" && splitEditKeys) {
        for (const key of splitEditKeys) {
          if (!valuesEqual(change.row[key], change.oldRow[key])) {
            splitEdit = true;
            break;
          }
        }
      }
      if (splitEdit) {
        assert(change.type === "edit");
        setSplitEditOverlay({
          outputIndex,
          change: {
            type: "remove",
            row: change.oldRow
          }
        });
        const outputRemove = {
          type: "remove",
          node: {
            row: change.oldRow,
            relationships: {}
          }
        };
        filterPush(outputRemove, output, filters?.predicate);
        yield;
        setSplitEditOverlay(void 0);
        setOverlay({ outputIndex, change });
        const outputAdd = {
          type: "add",
          node: {
            row: change.row,
            relationships: {}
          }
        };
        filterPush(outputAdd, output, filters?.predicate);
        yield;
      } else {
        setOverlay({ outputIndex, change });
        const outputChange = change.type === "edit" ? {
          type: change.type,
          oldNode: {
            row: change.oldRow,
            relationships: {}
          },
          node: {
            row: change.row,
            relationships: {}
          }
        } : {
          type: change.type,
          node: {
            row: change.row,
            relationships: {}
          }
        };
        filterPush(outputChange, output, filters?.predicate);
        yield;
      }
    }
  }
  setOverlay(void 0);
}
function* generateWithStart(nodes, start, compare) {
  if (!start) {
    yield* nodes;
    return;
  }
  let started = false;
  for (const node of nodes) {
    if (!started) {
      if (start.basis === "at") {
        if (compare(node.row, start.row) >= 0) {
          started = true;
        }
      } else if (start.basis === "after") {
        if (compare(node.row, start.row) > 0) {
          started = true;
        }
      }
    }
    if (started) {
      yield node;
    }
  }
}
function* generateWithOverlay(startAt, rows, constraint, overlay, splitEditOverlay, connectionIndex, compare, filterPredicate) {
  let overlayToApply = void 0;
  if (splitEditOverlay && splitEditOverlay.outputIndex === connectionIndex) {
    overlayToApply = splitEditOverlay;
  } else if (overlay && connectionIndex <= overlay.outputIndex) {
    overlayToApply = overlay;
  }
  const overlays = computeOverlays(
    startAt,
    constraint,
    overlayToApply,
    compare,
    filterPredicate
  );
  yield* generateWithOverlayInner(rows, overlays, compare);
}
function computeOverlays(startAt, constraint, overlay, compare, filterPredicate) {
  let overlays = {
    add: void 0,
    remove: void 0
  };
  switch (overlay?.change.type) {
    case "add":
      overlays = {
        add: overlay.change.row,
        remove: void 0
      };
      break;
    case "remove":
      overlays = {
        add: void 0,
        remove: overlay.change.row
      };
      break;
    case "edit":
      overlays = {
        add: overlay.change.row,
        remove: overlay.change.oldRow
      };
      break;
  }
  if (startAt) {
    overlays = overlaysForStartAt(overlays, startAt, compare);
  }
  if (constraint) {
    overlays = overlaysForConstraint(overlays, constraint);
  }
  if (filterPredicate) {
    overlays = overlaysForFilterPredicate(overlays, filterPredicate);
  }
  return overlays;
}
function overlaysForStartAt({ add, remove }, startAt, compare) {
  const undefinedIfBeforeStartAt = (row) => row === void 0 || compare(row, startAt) < 0 ? void 0 : row;
  return {
    add: undefinedIfBeforeStartAt(add),
    remove: undefinedIfBeforeStartAt(remove)
  };
}
function overlaysForConstraint({ add, remove }, constraint) {
  const undefinedIfDoesntMatchConstraint = (row) => row === void 0 || !constraintMatchesRow(constraint, row) ? void 0 : row;
  return {
    add: undefinedIfDoesntMatchConstraint(add),
    remove: undefinedIfDoesntMatchConstraint(remove)
  };
}
function overlaysForFilterPredicate({ add, remove }, filterPredicate) {
  const undefinedIfDoesntMatchFilter = (row) => row === void 0 || !filterPredicate(row) ? void 0 : row;
  return {
    add: undefinedIfDoesntMatchFilter(add),
    remove: undefinedIfDoesntMatchFilter(remove)
  };
}
function* generateWithOverlayInner(rowIterator, overlays, compare) {
  let addOverlayYielded = false;
  let removeOverlaySkipped = false;
  for (const row of rowIterator) {
    if (!addOverlayYielded && overlays.add) {
      const cmp = compare(overlays.add, row);
      if (cmp < 0) {
        addOverlayYielded = true;
        yield { row: overlays.add, relationships: {} };
      }
    }
    if (!removeOverlaySkipped && overlays.remove) {
      const cmp = compare(overlays.remove, row);
      if (cmp === 0) {
        removeOverlaySkipped = true;
        continue;
      }
    }
    yield { row, relationships: {} };
  }
  if (!addOverlayYielded && overlays.add) {
    yield { row: overlays.add, relationships: {} };
  }
}
var minValue = Symbol("min-value");
var maxValue = Symbol("max-value");
function makeBoundComparator(sort) {
  return (a, b) => {
    for (const entry of sort) {
      const key = entry[0];
      const cmp = compareBounds(a[key], b[key]);
      if (cmp !== 0) {
        return entry[1] === "asc" ? cmp : -cmp;
      }
    }
    return 0;
  };
}
function compareBounds(a, b) {
  if (a === b) {
    return 0;
  }
  if (a === minValue) {
    return -1;
  }
  if (b === minValue) {
    return 1;
  }
  if (a === maxValue) {
    return 1;
  }
  if (b === maxValue) {
    return -1;
  }
  return compareValues(a, b);
}
function* generateRows(data, scanStart, reverse) {
  yield* data[reverse ? "valuesFromReversed" : "valuesFrom"](
    scanStart
  );
}
function stringify(change) {
  return JSON.stringify(
    change,
    (_, v) => typeof v === "bigint" ? v.toString() : v
  );
}

// ../zero-client/src/client/ivm-branch.ts
var IVMSourceBranch = class _IVMSourceBranch {
  #sources;
  #tables;
  hash;
  constructor(tables, hash, sources = /* @__PURE__ */ new Map()) {
    this.#tables = tables;
    this.#sources = sources;
    this.hash = hash;
  }
  getSource(name) {
    if (this.#sources.has(name)) {
      return this.#sources.get(name);
    }
    const schema = this.#tables[name];
    const source = schema ? new MemorySource(name, schema.columns, schema.primaryKey) : void 0;
    this.#sources.set(name, source);
    return source;
  }
  clear() {
    this.#sources.clear();
  }
  /**
   * Mutates the current branch, advancing it to the new head
   * by applying the given diffs.
   */
  advance(expectedHead, newHead, diffs) {
    assert(
      this.hash === expectedHead,
      () => `Expected head must match the main head. Got: ${this.hash}, expected: ${expectedHead}`
    );
    applyDiffs(diffs, this);
    this.hash = newHead;
  }
  /**
   * Fork the branch and patch it up to match the desired head.
   */
  async forkToHead(store, desiredHead, readOptions) {
    const fork = this.fork();
    if (fork.hash === desiredHead) {
      return fork;
    }
    await patchBranch(desiredHead, store, fork, readOptions);
    fork.hash = desiredHead;
    return fork;
  }
  /**
   * Creates a new IVMSourceBranch that is a copy of the current one.
   * This is a cheap operation since the b-trees are shared until a write is performed
   * and then only the modified nodes are copied.
   *
   * IVM branches are forked when we need to rebase mutations.
   * The mutations modify the fork rather than original branch.
   */
  fork() {
    return new _IVMSourceBranch(
      this.#tables,
      this.hash,
      new Map(
        wrapIterable(this.#sources.entries()).map(([name, source]) => [
          name,
          source?.fork()
        ])
      )
    );
  }
};
async function patchBranch(desiredHead, store, fork, readOptions) {
  const diffs = await computeDiffs(
    must(fork.hash),
    desiredHead,
    store,
    readOptions
  );
  if (!diffs) {
    return;
  }
  applyDiffs(diffs, fork);
}
async function computeDiffs(startHash, endHash, store, readOptions) {
  const readFn = (dagRead) => diff2(
    startHash,
    endHash,
    dagRead,
    {
      shouldComputeDiffs: () => true,
      shouldComputeDiffsForIndex(_name) {
        return false;
      }
    },
    Latest
  );
  let diffs;
  if (readOptions?.openLazySourceRead) {
    diffs = await using(store.read(readOptions.openLazySourceRead), readFn);
  } else if (readOptions?.openLazyRead) {
    diffs = await readFn(readOptions.openLazyRead);
  } else {
    diffs = await withRead(store, readFn);
  }
  return diffs.get("");
}
function applyDiffs(diffs, branch) {
  for (let i = diffBinarySearch(diffs, ENTITIES_KEY_PREFIX, (diff3) => diff3.key); i < diffs.length; i++) {
    const diff3 = diffs[i];
    const { key } = diff3;
    if (!key.startsWith(ENTITIES_KEY_PREFIX)) {
      break;
    }
    const name = sourceNameFromKey(key);
    const source = must(branch.getSource(name));
    switch (diff3.op) {
      case "del":
        source.push({
          type: "remove",
          row: diff3.oldValue
        });
        break;
      case "add":
        source.push({
          type: "add",
          row: diff3.newValue
        });
        break;
      case "change":
        source.push({
          type: "edit",
          row: diff3.newValue,
          oldRow: diff3.oldValue
        });
        break;
    }
  }
}

// ../zero-client/src/client/context.ts
var ZeroContext = class {
  // It is a bummer to have to maintain separate MemorySources here and copy the
  // data in from the Replicache db. But we want the data to be accessible via
  // pipelines *synchronously* and the core Replicache infra is all async. So
  // that needs to be fixed.
  #mainSources;
  #addQuery;
  #updateQuery;
  #batchViewUpdates;
  #commitListeners = /* @__PURE__ */ new Set();
  slowMaterializeThreshold;
  lc;
  staticQueryParameters = void 0;
  constructor(lc, mainSources, addQuery, updateQuery, batchViewUpdates, slowMaterializeThreshold) {
    this.#mainSources = mainSources;
    this.#addQuery = addQuery;
    this.#updateQuery = updateQuery;
    this.#batchViewUpdates = batchViewUpdates;
    this.lc = lc;
    this.slowMaterializeThreshold = slowMaterializeThreshold;
  }
  getSource(name) {
    return this.#mainSources.getSource(name);
  }
  addServerQuery(ast, ttl, gotCallback) {
    return this.#addQuery(ast, ttl, gotCallback);
  }
  updateServerQuery(ast, ttl) {
    this.#updateQuery(ast, ttl);
  }
  onQueryMaterialized(hash, ast, duration) {
    if (this.slowMaterializeThreshold !== void 0 && duration > this.slowMaterializeThreshold) {
      this.lc.warn?.(
        "Slow query materialization (including server/network)",
        hash,
        ast,
        duration
      );
    } else {
      this.lc.debug?.(
        "Materialized query (including server/network)",
        hash,
        ast,
        duration
      );
    }
  }
  mapAst(ast) {
    return ast;
  }
  createStorage() {
    return new MemoryStorage();
  }
  decorateInput(input) {
    return input;
  }
  onTransactionCommit(cb) {
    this.#commitListeners.add(cb);
    return () => {
      this.#commitListeners.delete(cb);
    };
  }
  batchViewUpdates(applyViewUpdates) {
    let result;
    let viewChangesPerformed = false;
    this.#batchViewUpdates(() => {
      result = applyViewUpdates();
      viewChangesPerformed = true;
    });
    assert(
      viewChangesPerformed,
      "batchViewUpdates must call applyViewUpdates synchronously."
    );
    return result;
  }
  processChanges(expectedHead, newHead, changes) {
    this.batchViewUpdates(() => {
      try {
        this.#mainSources.advance(expectedHead, newHead, changes);
      } finally {
        this.#endTransaction();
      }
    });
  }
  #endTransaction() {
    for (const listener of this.#commitListeners) {
      try {
        listener();
      } catch (e) {
        this.lc.error?.("Failed notifying a commit listener of IVM updates", e);
      }
    }
  }
};

// ../zero-client/src/client/crud.ts
function makeCRUDMutate(schema, repMutate) {
  const { [CRUD_MUTATION_NAME]: zeroCRUD } = repMutate;
  const mutateBatch = async (body) => {
    const ops = [];
    const m = {};
    for (const name of Object.keys(schema.tables)) {
      m[name] = makeBatchCRUDMutate(name, schema, ops);
    }
    const rv = await body(m);
    await zeroCRUD({ ops });
    return rv;
  };
  const mutate = {};
  for (const [name, tableSchema] of Object.entries(schema.tables)) {
    mutate[name] = makeEntityCRUDMutate(name, tableSchema.primaryKey, zeroCRUD);
  }
  return {
    mutate,
    mutateBatch
  };
}
function makeEntityCRUDMutate(tableName, primaryKey, zeroCRUD) {
  return {
    insert: (value) => {
      const op = {
        op: "insert",
        tableName,
        primaryKey,
        value
      };
      return zeroCRUD({ ops: [op] });
    },
    upsert: (value) => {
      const op = {
        op: "upsert",
        tableName,
        primaryKey,
        value
      };
      return zeroCRUD({ ops: [op] });
    },
    update: (value) => {
      const op = {
        op: "update",
        tableName,
        primaryKey,
        value
      };
      return zeroCRUD({ ops: [op] });
    },
    delete: (id) => {
      const op = {
        op: "delete",
        tableName,
        primaryKey,
        value: id
      };
      return zeroCRUD({ ops: [op] });
    }
  };
}
function makeBatchCRUDMutate(tableName, schema, ops) {
  const { primaryKey } = schema.tables[tableName];
  return {
    insert: (value) => {
      const op = {
        op: "insert",
        tableName,
        primaryKey,
        value
      };
      ops.push(op);
      return promiseVoid;
    },
    upsert: (value) => {
      const op = {
        op: "upsert",
        tableName,
        primaryKey,
        value
      };
      ops.push(op);
      return promiseVoid;
    },
    update: (value) => {
      const op = {
        op: "update",
        tableName,
        primaryKey,
        value
      };
      ops.push(op);
      return promiseVoid;
    },
    delete: (id) => {
      const op = {
        op: "delete",
        tableName,
        primaryKey,
        value: id
      };
      ops.push(op);
      return promiseVoid;
    }
  };
}
function makeCRUDMutator(schema) {
  return async function zeroCRUDMutator(tx, crudArg) {
    for (const op of crudArg.ops) {
      switch (op.op) {
        case "insert":
          await insertImpl(tx, op, schema, void 0);
          break;
        case "upsert":
          await upsertImpl(tx, op, schema, void 0);
          break;
        case "update":
          await updateImpl(tx, op, schema, void 0);
          break;
        case "delete":
          await deleteImpl(tx, op, schema, void 0);
          break;
      }
    }
  };
}
function defaultOptionalFieldsToNull(schema, value) {
  let rv = value;
  for (const name in schema.columns) {
    if (rv[name] === void 0) {
      rv = { ...rv, [name]: null };
    }
  }
  return rv;
}
async function insertImpl(tx, arg, schema, ivmBranch) {
  const key = toPrimaryKeyString(
    arg.tableName,
    schema.tables[arg.tableName].primaryKey,
    arg.value
  );
  if (!await tx.has(key)) {
    const val = defaultOptionalFieldsToNull(
      schema.tables[arg.tableName],
      arg.value
    );
    await tx.set(key, val);
    if (ivmBranch) {
      must(ivmBranch.getSource(arg.tableName)).push({
        type: "add",
        row: arg.value
      });
    }
  }
}
async function upsertImpl(tx, arg, schema, ivmBranch) {
  const key = toPrimaryKeyString(
    arg.tableName,
    schema.tables[arg.tableName].primaryKey,
    arg.value
  );
  const val = defaultOptionalFieldsToNull(
    schema.tables[arg.tableName],
    arg.value
  );
  await tx.set(key, val);
  if (ivmBranch) {
    must(ivmBranch.getSource(arg.tableName)).push({
      type: "set",
      row: arg.value
    });
  }
}
async function updateImpl(tx, arg, schema, ivmBranch) {
  const key = toPrimaryKeyString(
    arg.tableName,
    schema.tables[arg.tableName].primaryKey,
    arg.value
  );
  const prev = await tx.get(key);
  if (prev === void 0) {
    return;
  }
  const update = arg.value;
  const next = { ...prev };
  for (const k in update) {
    if (update[k] !== void 0) {
      next[k] = update[k];
    }
  }
  await tx.set(key, next);
  if (ivmBranch) {
    must(ivmBranch.getSource(arg.tableName)).push({
      type: "edit",
      oldRow: prev,
      row: next
    });
  }
}
async function deleteImpl(tx, arg, schema, ivmBranch) {
  const key = toPrimaryKeyString(
    arg.tableName,
    schema.tables[arg.tableName].primaryKey,
    arg.value
  );
  const prev = await tx.get(key);
  if (prev === void 0) {
    return;
  }
  await tx.del(key);
  if (ivmBranch) {
    must(ivmBranch.getSource(arg.tableName)).push({
      type: "remove",
      row: prev
    });
  }
}

// ../shared/src/sentinels.ts
function emptyFunction() {
}
var emptyObject = Object.freeze({});

// ../zero-client/src/client/custom.ts
var TransactionImpl = class {
  constructor(lc, repTx, schema, slowMaterializeThreshold) {
    const castedRepTx = repTx;
    must(repTx.reason === "initial" || repTx.reason === "rebase");
    this.clientID = repTx.clientID;
    this.mutationID = repTx.mutationID;
    this.reason = repTx.reason === "initial" ? "optimistic" : "rebase";
    const txData = must(
      castedRepTx[zeroData],
      "zero was not set on replicache internal options!"
    );
    this.mutate = makeSchemaCRUD(
      schema,
      repTx,
      txData.ivmSources
    );
    this.query = makeSchemaQuery(
      lc,
      schema,
      txData.ivmSources,
      slowMaterializeThreshold
    );
    this.token = txData.token;
  }
  clientID;
  mutationID;
  reason;
  location = "client";
  mutate;
  query;
  token;
};
function makeReplicacheMutator(lc, mutator, schema, slowMaterializeThreshold) {
  return async (repTx, args) => {
    const tx = new TransactionImpl(lc, repTx, schema, slowMaterializeThreshold);
    await mutator(tx, args);
  };
}
function makeSchemaCRUD(schema, tx, ivmBranch) {
  return new Proxy(
    {},
    {
      get(target, prop) {
        if (prop in target) {
          return target[prop];
        }
        target[prop] = makeTableCRUD(schema, prop, tx, ivmBranch);
        return target[prop];
      }
    }
  );
}
function makeSchemaQuery(lc, schema, ivmBranch, slowMaterializeThreshold) {
  const context = new ZeroContext(
    lc,
    ivmBranch,
    () => emptyFunction,
    () => {
    },
    (applyViewUpdates) => applyViewUpdates(),
    slowMaterializeThreshold
  );
  return new Proxy(
    {},
    {
      get(target, prop) {
        if (prop in target) {
          return target[prop];
        }
        target[prop] = newQuery(context, schema, prop);
        return target[prop];
      }
    }
  );
}
function makeTableCRUD(schema, tableName, tx, ivmBranch) {
  const table2 = must(schema.tables[tableName]);
  const { primaryKey } = table2;
  return {
    insert: (value) => insertImpl(
      tx,
      { op: "insert", tableName, primaryKey, value },
      schema,
      ivmBranch
    ),
    upsert: (value) => upsertImpl(
      tx,
      { op: "upsert", tableName, primaryKey, value },
      schema,
      ivmBranch
    ),
    update: (value) => updateImpl(
      tx,
      { op: "update", tableName, primaryKey, value },
      schema,
      ivmBranch
    ),
    delete: (id) => deleteImpl(
      tx,
      { op: "delete", tableName, primaryKey, value: id },
      schema,
      ivmBranch
    )
  };
}

// ../zero-client/src/client/delete-clients-manager.ts
var DeleteClientsManager = class {
  #send;
  #lc;
  #dagStore;
  constructor(send2, dagStore, lc) {
    this.#send = send2;
    this.#dagStore = dagStore;
    this.#lc = lc;
  }
  /**
   * This gets called by Replicache when it deletes clients from the persistent
   * storage.
   */
  onClientsDeleted(clientIDs, clientGroupIDs) {
    this.#lc.debug?.("DeletedClientsManager, send:", clientIDs);
    this.#send(["deleteClients", { clientIDs, clientGroupIDs }]);
  }
  /**
   * Zero calls this after it connects to ensure that the server knows about all
   * the clients that might have been deleted locally since the last connection.
   */
  async sendDeletedClientsToServer() {
    const deleted = await withRead(
      this.#dagStore,
      (dagRead) => getDeletedClients(dagRead)
    );
    if (deleted.clientIDs.length > 0 || deleted.clientGroupIDs.length > 0) {
      this.#send(["deleteClients", deleted]);
      this.#lc.debug?.("DeletedClientsManager, send:", deleted);
    }
  }
  /**
   * This is called as a response to the server telling us which clients it
   * actually deleted.
   */
  clientsDeletedOnServer(deletedClients) {
    const { clientIDs = [], clientGroupIDs = [] } = deletedClients;
    if (clientIDs.length > 0 || clientGroupIDs.length > 0) {
      return withWrite(this.#dagStore, async (dagWrite) => {
        this.#lc.debug?.("clientsDeletedOnServer:", clientIDs, clientGroupIDs);
        await removeDeletedClients(dagWrite, clientIDs, clientGroupIDs);
      });
    }
    return promiseVoid;
  }
  getDeletedClients() {
    return withRead(this.#dagStore, getDeletedClients);
  }
};

// ../zero-client/src/client/enable-analytics.ts
var IPV4_ADDRESS_REGEX = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
var IPV6_ADDRESS_HOSTNAME_REGEX = /^\[[a-fA-F0-9:]*:[a-fA-F0-9:]*\]$/;
var IP_ADDRESS_HOSTNAME_REGEX = new RegExp(
  `(${IPV4_ADDRESS_REGEX.source}|${IPV6_ADDRESS_HOSTNAME_REGEX.source})`
);
function shouldEnableAnalytics(server, enableAnalytics = true) {
  if (!enableAnalytics) {
    return false;
  }
  const serverURL = server === null ? null : new URL(server);
  const socketHostname = serverURL?.hostname;
  return server !== null && socketHostname !== void 0 && socketHostname !== "localhost" && !IP_ADDRESS_HOSTNAME_REGEX.test(socketHostname);
}

// ../zero-client/src/client/http-string.ts
function toWSString(url) {
  return "ws" + url.slice(4);
}
function appendPath(url, toAppend) {
  return url + (url.endsWith("/") ? toAppend.substring(1) : toAppend);
}

// ../zero-client/src/client/log-options.ts
import {
  TeeLogSink as TeeLogSink2,
  consoleLogSink as consoleLogSink3
} from "@rocicorp/logger";

// ../datadog/src/datadog-log-sink.ts
import { Lock as Lock2 } from "@rocicorp/lock";
var DD_BASE_URL = new URL(
  "https://http-intake.logs.datadoghq.com/api/v2/logs"
);
var MAX_LOG_ENTRIES_PER_FLUSH = 1e3;
var FORCE_FLUSH_THRESHOLD = 250;
var MAX_ENTRY_BYTES = 5 * 1024 * 1024;
var MAX_MESSAGE_RETRIES = 2;
var MAX_ENTRY_CHARS = MAX_ENTRY_BYTES / 4;
var DatadogLogSink = class {
  #messages = [];
  #apiKey;
  #source;
  #service;
  #host;
  #version;
  #interval;
  #baseURL;
  #timerID = 0;
  #flushLock = new Lock2();
  constructor(options) {
    const {
      apiKey,
      source,
      service,
      host,
      version: version3,
      interval = 5e3,
      baseURL: baseUrl = DD_BASE_URL
    } = options;
    this.#apiKey = apiKey;
    this.#source = source;
    this.#service = service;
    this.#host = host;
    this.#version = version3;
    this.#interval = interval;
    this.#baseURL = baseUrl.toString();
  }
  log(level, context, ...args) {
    this.#messages.push(makeMessage(args, context, level));
    if (level === "error" || this.#messages.length === FORCE_FLUSH_THRESHOLD) {
      void this.flush();
    } else {
      this.#startTimer();
    }
  }
  #startTimer() {
    if (this.#timerID) {
      return;
    }
    this.#timerID = setTimeout(() => {
      this.#timerID = 0;
      void this.flush();
    }, this.#interval);
  }
  flush() {
    return this.#flushLock.withLock(async () => {
      const { length } = this.#messages;
      if (length === 0) {
        return;
      }
      do {
        const flushTime = Date.now();
        const stringified = [];
        let totalBytes = 0;
        for (const m of this.#messages) {
          m.flushDelayMs = flushTime - m.date;
          let str = JSON.stringify(m);
          if (str.length > MAX_ENTRY_CHARS) {
            m.message = `[Dropped message of length ${str.length}]`;
            str = JSON.stringify(m);
          }
          if (str.length + totalBytes + stringified.length > MAX_ENTRY_CHARS) {
            break;
          }
          totalBytes += str.length;
          stringified.push(str);
          if (stringified.length === MAX_LOG_ENTRIES_PER_FLUSH) {
            break;
          }
        }
        const body = stringified.join("\n");
        const url = new URL(this.#baseURL);
        if (this.#apiKey !== void 0) {
          url.searchParams.set("dd-api-key", this.#apiKey);
        }
        if (this.#source) {
          url.searchParams.set("ddsource", this.#source);
          url.searchParams.set("dd-evp-origin", this.#source);
        }
        if (this.#service) {
          url.searchParams.set("service", this.#service);
        }
        if (this.#host) {
          url.searchParams.set("host", this.#host);
        }
        if (this.#version) {
          url.searchParams.set("ddtags", `version:${this.#version}`);
        }
        let ok = false;
        try {
          const response = await fetch(url.toString(), {
            method: "POST",
            body,
            keepalive: true
          });
          ok = response.ok;
          if (!ok) {
            console.error(
              "response",
              response.status,
              response.statusText,
              await response.text
            );
          }
        } catch (e) {
          console.error("Log flush to datadog failed", e);
        }
        if (ok) {
          this.#messages.splice(0, stringified.length);
        } else {
          let numWithTooManyRetries = 0;
          for (let i = 0; i < stringified.length; i++) {
            const m = this.#messages[i];
            m.flushRetryCount = (m.flushRetryCount ?? 0) + 1;
            if (m.flushRetryCount > MAX_MESSAGE_RETRIES) {
              numWithTooManyRetries++;
            }
          }
          if (numWithTooManyRetries > 0) {
            console.error(
              `Dropping ${numWithTooManyRetries} datadog log messages which failed to send ${MAX_MESSAGE_RETRIES + 1} times.`
            );
            this.#messages.splice(0, numWithTooManyRetries);
          }
        }
      } while (this.#messages.length >= FORCE_FLUSH_THRESHOLD);
      if (this.#messages.length) {
        this.#startTimer();
      }
    });
  }
};
function flattenMessage(message) {
  if (Array.isArray(message) && message.length === 1) {
    return flattenMessage(message[0]);
  }
  return message;
}
function convertError(e) {
  return {
    name: e.name,
    message: e.message,
    stack: e.stack
  };
}
function convertErrors(message) {
  if (message instanceof Error) {
    return convertError(message);
  }
  if (message instanceof Array) {
    const convertedMessage = [];
    for (const item of message) {
      if (item instanceof Error) {
        convertedMessage.push(convertError(item));
      } else {
        convertedMessage.push(item);
      }
    }
    return convertedMessage;
  }
  return message;
}
var LOG_SINK_FLUSH_RETRY_COUNT = "flushRetryCount";
var LOG_SINK_FLUSH_DELAY_ATTRIBUTE = "flushDelayMs";
var RESERVED_KEY_PREFIX = "@DATADOG_RESERVED_";
var RESERVED_KEYS = [
  "host",
  "source",
  "status",
  "service",
  "version",
  "trace_id",
  "message",
  "msg",
  // alias for message
  "date",
  // The following are attributes reserved by the DataDogLogSink
  // itself (as opposed to DataDog), to report on its own behavior.
  LOG_SINK_FLUSH_DELAY_ATTRIBUTE,
  LOG_SINK_FLUSH_RETRY_COUNT
];
function makeMessage(message, context, logLevel) {
  let safeContext = void 0;
  if (context !== void 0) {
    for (const reservedKey of RESERVED_KEYS) {
      if (Object.hasOwn(context, reservedKey)) {
        if (safeContext === void 0) {
          safeContext = { ...context };
        }
        safeContext[RESERVED_KEY_PREFIX + reservedKey] = safeContext[reservedKey];
        delete safeContext[reservedKey];
      }
    }
  }
  const msg = {
    ...safeContext ?? context,
    date: Date.now(),
    message: convertErrors(flattenMessage(message)),
    status: logLevel
  };
  if (logLevel === "error") {
    msg.error = { origin: "logger" };
  }
  return msg;
}

// ../zero-client/src/client/version.ts
var version2 = "0.18.2025040300";

// ../zero-client/src/client/log-options.ts
var LevelFilterLogSink = class {
  #wrappedLogSink;
  #level;
  constructor(wrappedLogSink, level) {
    this.#wrappedLogSink = wrappedLogSink;
    this.#level = level;
  }
  log(level, context, ...args) {
    if (this.#level === "error" && level !== "error") {
      return;
    }
    if (this.#level === "info" && level === "debug") {
      return;
    }
    this.#wrappedLogSink.log(level, context, ...args);
  }
  async flush() {
    await consoleLogSink3.flush?.();
  }
};
var DATADOG_LOG_LEVEL = "info";
var ZERO_SASS_DOMAIN = ".reflect-server.net";
function createLogOptions(options, createDatadogLogSink = (options2) => new DatadogLogSink(options2)) {
  const { consoleLogLevel, server, enableAnalytics } = options;
  if (!enableAnalytics || server === null) {
    return {
      logLevel: consoleLogLevel,
      logSink: consoleLogSink3
    };
  }
  const serverURL = new URL(server);
  const { hostname } = serverURL;
  const datadogServiceLabel = hostname.endsWith(ZERO_SASS_DOMAIN) ? hostname.substring(0, hostname.length - ZERO_SASS_DOMAIN.length).toLowerCase() : hostname;
  const baseURL = new URL(appendPath(server, "/logs/v0/log"));
  const logLevel = consoleLogLevel === "debug" ? "debug" : "info";
  const logSink = new TeeLogSink2([
    new LevelFilterLogSink(consoleLogSink3, consoleLogLevel),
    new LevelFilterLogSink(
      createDatadogLogSink({
        service: datadogServiceLabel,
        host: location.host,
        version: version2,
        baseURL
      }),
      DATADOG_LOG_LEVEL
    )
  ]);
  return {
    logLevel,
    logSink
  };
}

// ../zero-client/src/client/metric-name-enum.ts
var TimeToConnectMs = "time_to_connect_ms";
var LastConnectError = "last_connect_error";
var TimeToConnectMsV2 = "time_to_connect_ms_v2";
var LastConnectErrorV2 = "last_connect_error_v2";
var TotalTimeToConnectMs = "total_time_to_connect_ms";
var NotConnected = "not_connected";

// ../zero-client/src/client/metrics.ts
var DID_NOT_CONNECT_VALUE = 100 * 1e3;
var REPORT_INTERVAL_MS = 5e3;
function getLastConnectErrorValue(reason) {
  if ("server" in reason) {
    return `server_${camelToSnake(reason.server)}`;
  }
  return `client_${camelToSnake(reason.client)}`;
}
function camelToSnake(s) {
  return s.split(/\.?(?=[A-Z])/).join("_").toLowerCase();
}
var MetricManager = class {
  #reportIntervalMs;
  #host;
  #reporter;
  #lc;
  #timerID;
  constructor(opts) {
    this.#reportIntervalMs = opts.reportIntervalMs;
    this.#host = opts.host;
    this.#reporter = opts.reporter;
    this.#lc = opts.lc;
    this.tags.push(`source:${opts.source}`);
    this.timeToConnectMs.set(DID_NOT_CONNECT_VALUE);
    this.#setNotConnectedReason("init");
    this.#timerID = setInterval(() => {
      void this.flush();
    }, this.#reportIntervalMs);
  }
  #metrics = [];
  // timeToConnectMs measures the time from the call to connect() to receiving
  // the 'connected' ws message. We record the DID_NOT_CONNECT_VALUE if the previous
  // connection attempt failed for any reason.
  //
  // We set the gauge using #connectStart as follows:
  // - #connectStart is undefined if we are disconnected or connected; it is
  //   defined only in the Connecting state, as a number representing the timestamp
  //   at which we started connecting.
  // - #connectStart is set to the current time when connect() is called.
  // - When we receive the 'connected' message we record the time to connect and
  //   set #connectStart to undefined.
  // - If disconnect() is called with a defined #connectStart then we record
  //   DID_NOT_CONNECT_VALUE and set #connectStart to undefined.
  //
  // TODO It's clear after playing with the connection code we should encapsulate
  // the ConnectionState along with its state transitions and possibly behavior.
  // In that world the metric gauge(s) and bookkeeping like #connectStart would
  // be encapsulated with the ConnectionState. This will probably happen as part
  // of https://github.com/rocicorp/reflect-server/issues/255.
  timeToConnectMs = this.#register(
    new Gauge(TimeToConnectMs)
  );
  // lastConnectError records the last error that occurred when connecting,
  // if any. It is cleared when connecting successfully or when reported, so this
  // state only gets reported if there was a failure during the reporting period and
  // we are still not connected.
  lastConnectError = this.#register(
    new State(
      LastConnectError,
      true
      // clearOnFlush
    )
  );
  // notConnected records the reason why the client is not currently connected.
  // It is cleared when the client successfully connects.
  #notConnected = this.#register(new State(NotConnected));
  // The time from the call to connect() to receiving the 'connected' ws message
  // for the current connection.  Cleared when the client is not connected.
  // TODO: Not actually currently cleared on disconnect untill there is a
  // connect error, or client reports disconnected and waiting for visible.
  // Should have a value iff _notConnected has no value.
  #timeToConnectMsV2 = this.#register(
    new Gauge(TimeToConnectMsV2)
  );
  // lastConnectErrorV2 records the last error that occurred when connecting,
  // if any. It is cleared when the client successfully connects or
  // stops trying to connect due to being hidden.
  // Should have a value iff notConnected state is NotConnectedReason.Error.
  #lastConnectErrorV2 = this.#register(
    new State(LastConnectErrorV2)
  );
  // The total time it took to connect across retries for the current
  // connection.  Cleared when the client is not connected.
  // TODO: Not actually currently cleared on disconnect until there is a
  // connect error, or client reports disconnected and waiting for visible.
  // See Zero.#totalToConnectStart for details of how this total is computed.
  // Should have a value iff _notConnected has no value.
  #totalTimeToConnectMs = this.#register(
    new Gauge(TotalTimeToConnectMs)
  );
  #setNotConnectedReason(reason) {
    this.#notConnected.set(reason);
  }
  setConnected(timeToConnectMs, totalTimeToConnectMs) {
    this.#notConnected.clear();
    this.#lastConnectErrorV2.clear();
    this.#timeToConnectMsV2.set(timeToConnectMs);
    this.#totalTimeToConnectMs.set(totalTimeToConnectMs);
  }
  setDisconnectedWaitingForVisible() {
    this.#timeToConnectMsV2.clear();
    this.#totalTimeToConnectMs.clear();
    this.#lastConnectErrorV2.clear();
    let notConnectedReason;
    switch (this.#notConnected.get()) {
      case "init":
        notConnectedReason = "hidden_was_init";
        break;
      case "error":
        notConnectedReason = "hidden_was_error";
        break;
      default:
        notConnectedReason = "hidden";
        break;
    }
    this.#setNotConnectedReason(notConnectedReason);
  }
  setConnectError(reason) {
    this.#timeToConnectMsV2.clear();
    this.#totalTimeToConnectMs.clear();
    this.#setNotConnectedReason("error");
    this.#lastConnectErrorV2.set(getLastConnectErrorValue(reason));
  }
  /**
   * Tags to include in all metrics.
   */
  tags = [];
  // Flushes all metrics to an array of time series (plural), one Series
  // per metric.
  async flush() {
    const lc = this.#lc;
    if (this.#timerID === null) {
      lc.error?.("MetricManager.flush() called but already stopped");
      return;
    }
    const allSeries = [];
    for (const metric of this.#metrics) {
      const series = metric.flush();
      if (series !== void 0) {
        allSeries.push({
          ...series,
          host: this.#host,
          tags: this.tags
        });
      }
    }
    if (allSeries.length === 0) {
      lc?.debug?.("No metrics to report");
      return;
    }
    try {
      await this.#reporter(allSeries);
    } catch (e) {
      lc?.error?.(`Error reporting metrics: ${e}`);
    }
  }
  stop() {
    if (this.#timerID === null) {
      this.#lc.error?.("MetricManager.stop() called but already stopped");
      return;
    }
    clearInterval(this.#timerID);
    this.#timerID = null;
  }
  #register(metric) {
    this.#metrics.push(metric);
    return metric;
  }
};
function makePoint(ts, value) {
  return [ts, [value]];
}
var Gauge = class {
  #name;
  #value = void 0;
  constructor(name) {
    this.#name = name;
  }
  set(value) {
    this.#value = value;
  }
  get() {
    return this.#value;
  }
  clear() {
    this.#value = void 0;
  }
  flush() {
    if (this.#value === void 0) {
      return void 0;
    }
    const points = [makePoint(t(), this.#value)];
    return { metric: this.#name, points };
  }
};
function t() {
  return Math.round(Date.now() / 1e3);
}
var State = class {
  #prefix;
  #clearOnFlush;
  #current = void 0;
  constructor(prefix, clearOnFlush = false) {
    this.#prefix = prefix;
    this.#clearOnFlush = clearOnFlush;
  }
  set(state) {
    this.#current = state;
  }
  get() {
    return this.#current;
  }
  clear() {
    this.#current = void 0;
  }
  flush() {
    if (this.#current === void 0) {
      return void 0;
    }
    const gauge = new Gauge([this.#prefix, this.#current].join("_"));
    gauge.set(1);
    const series = gauge.flush();
    if (this.#clearOnFlush) {
      this.clear();
    }
    return series;
  }
};

// ../zero-client/src/client/mutation-tracker.ts
import { resolver as resolver7 } from "@rocicorp/resolver";
var transientPushErrorTypes = [
  "zeroPusher",
  "http",
  // These should never actually be received as they cause the websocket
  // connection to be closed.
  "unsupportedPushVersion",
  "unsupportedSchemaVersion"
];
var currentEphemeralID = 0;
function nextEphemeralID() {
  return ++currentEphemeralID;
}
var MutationTracker = class {
  #outstandingMutations;
  #ephemeralIDsByMutationID;
  #allMutationsConfirmedListeners;
  #lc;
  #clientID;
  constructor(lc) {
    this.#lc = lc.withContext("MutationTracker");
    this.#outstandingMutations = /* @__PURE__ */ new Map();
    this.#ephemeralIDsByMutationID = /* @__PURE__ */ new Map();
    this.#allMutationsConfirmedListeners = /* @__PURE__ */ new Set();
  }
  set clientID(clientID) {
    this.#clientID = clientID;
  }
  trackMutation() {
    const id = nextEphemeralID();
    const mutationResolver = resolver7();
    this.#outstandingMutations.set(id, {
      resolver: mutationResolver
    });
    return { ephemeralID: id, serverPromise: mutationResolver.promise };
  }
  mutationIDAssigned(id, mutationID) {
    const entry = this.#outstandingMutations.get(id);
    if (entry) {
      entry.mutationID = mutationID;
      this.#ephemeralIDsByMutationID.set(mutationID, id);
    }
  }
  rejectMutation(id, e) {
    const entry = this.#outstandingMutations.get(id);
    if (entry) {
      this.#settleMutation(id, entry, "reject", {
        error: "app",
        details: e instanceof Error ? e.message : String(e)
      });
    }
  }
  processPushResponse(response) {
    if ("error" in response) {
      this.#lc.error?.(
        "Received an error response when pushing mutations",
        response
      );
      this.#processPushError(response);
    } else {
      this.#processPushOk(response);
    }
  }
  /**
   * When we reconnect to zero-cache, we resolve all outstanding mutations
   * whose ID is less than or equal to the lastMutationID.
   *
   * The reason is that any responses the API server sent
   * to those mutations have been lost.
   *
   * An example case: the API server responds while the connection
   * is down. Those responses are dropped.
   *
   * Mutations whose LMID is > the lastMutationID are not resolved
   * since they will be retried by the client, giving us another chance
   * at getting a response.
   *
   * The only way to ensure that all API server responses are
   * received would be to have the API server write them
   * to the DB while writing the LMID.
   *
   * This would have the downside of not being able to provide responses to a
   * mutation with data gathered after the transaction.
   */
  onConnected(lastMutationID) {
    for (const [id, entry] of this.#outstandingMutations) {
      if (!entry.mutationID) {
        continue;
      }
      if (entry.mutationID <= lastMutationID) {
        this.#settleMutation(id, entry, "resolve", emptyObject);
      } else {
        break;
      }
    }
  }
  get size() {
    return this.#outstandingMutations.size;
  }
  #processPushError(error) {
    if (transientPushErrorTypes.includes(error.error)) {
      return;
    }
    const mids = error.mutationIDs;
    if (!mids) {
      return;
    }
    for (const mid of mids) {
      this.#processMutationError(mid, error);
    }
  }
  #processPushOk(ok) {
    for (const mutation of ok.mutations) {
      if ("error" in mutation.result) {
        this.#processMutationError(mutation.id, mutation.result);
      } else {
        this.#processMutationOk(mutation.result, mutation.id);
      }
    }
  }
  #processMutationError(mid, error) {
    assert(
      mid.clientID === this.#clientID,
      "received mutation for the wrong client"
    );
    this.#lc.error?.(`Mutation ${mid.id} returned an error`, error);
    const ephemeralID = this.#ephemeralIDsByMutationID.get(mid.id);
    assert(
      ephemeralID,
      () => "invalid state. An ephemeral id was never assigned to mutation " + mid.id
    );
    const entry = this.#outstandingMutations.get(ephemeralID);
    assert(entry && entry.mutationID === mid.id);
    this.#settleMutation(ephemeralID, entry, "reject", error);
  }
  #processMutationOk(result, mid) {
    assert(
      mid.clientID === this.#clientID,
      "received mutation for the wrong client"
    );
    const ephemeralID = this.#ephemeralIDsByMutationID.get(mid.id);
    assert(
      ephemeralID,
      () => "invalid state. An ephemeral id was never assigned to mutation " + mid.id
    );
    const entry = this.#outstandingMutations.get(ephemeralID);
    assert(entry && entry.mutationID === mid.id);
    this.#settleMutation(ephemeralID, entry, "resolve", result);
  }
  #settleMutation(ephemeralID, entry, type, result) {
    switch (type) {
      case "resolve":
        assert(!("error" in result));
        entry.resolver.resolve(result);
        break;
      case "reject":
        assert("error" in result);
        entry.resolver.reject(result);
        break;
    }
    const removed = this.#outstandingMutations.delete(ephemeralID);
    if (entry.mutationID) {
      this.#ephemeralIDsByMutationID.delete(entry.mutationID);
    }
    if (removed && this.#outstandingMutations.size === 0) {
      this.#notifyAllMutationsConfirmedListeners();
    }
  }
  onAllMutationsConfirmed(listener) {
    this.#allMutationsConfirmedListeners.add(listener);
  }
  #notifyAllMutationsConfirmedListeners() {
    for (const listener of this.#allMutationsConfirmedListeners) {
      listener();
    }
  }
};

// ../zero-client/src/client/ping-result-enum.ts
var TimedOut = 0;
var Success = 1;

// ../zero-client/src/client/query-manager.ts
var QueryManager = class {
  #clientID;
  #clientToServer;
  #send;
  #queries = /* @__PURE__ */ new Map();
  #recentQueriesMaxSize;
  #recentQueries = /* @__PURE__ */ new Set();
  #gotQueries = /* @__PURE__ */ new Set();
  #mutationTracker;
  #pendingRemovals = [];
  constructor(mutationTracker, clientID, tables, send2, experimentalWatch, recentQueriesMaxSize) {
    this.#clientID = clientID;
    this.#clientToServer = clientToServer(tables);
    this.#recentQueriesMaxSize = recentQueriesMaxSize;
    this.#send = send2;
    this.#mutationTracker = mutationTracker;
    this.#mutationTracker.onAllMutationsConfirmed(() => {
      if (this.#pendingRemovals.length === 0) {
        return;
      }
      const pendingRemovals = this.#pendingRemovals;
      this.#pendingRemovals = [];
      for (const removal of pendingRemovals) {
        removal();
      }
    });
    experimentalWatch(
      (diff3) => {
        for (const diffOp of diff3) {
          const queryHash = diffOp.key.substring(GOT_QUERIES_KEY_PREFIX.length);
          switch (diffOp.op) {
            case "add":
              this.#gotQueries.add(queryHash);
              this.#fireGotCallbacks(queryHash, true);
              break;
            case "del":
              this.#gotQueries.delete(queryHash);
              this.#fireGotCallbacks(queryHash, false);
              break;
          }
        }
      },
      {
        prefix: GOT_QUERIES_KEY_PREFIX,
        initialValuesInFirstDiff: true
      }
    );
  }
  #fireGotCallbacks(queryHash, got) {
    const gotCallbacks = this.#queries.get(queryHash)?.gotCallbacks ?? [];
    for (const gotCallback of gotCallbacks) {
      gotCallback(got);
    }
  }
  /**
   * Get the queries that need to be registered with the server.
   *
   * An optional `lastPatch` can be provided. This is the last patch that was
   * sent to the server and may not yet have been acked. If `lastPatch` is provided,
   * this method will return a patch that does not include any events sent in `lastPatch`.
   *
   * This diffing of last patch and current patch is needed since we send
   * a set of queries to the server when we first connect inside of the `sec-protocol` as
   * the `initConnectionMessage`.
   *
   * While we're waiting for the `connected` response to come back from the server,
   * the client may have registered more queries. We need to diff the `initConnectionMessage`
   * queries with the current set of queries to understand what those were.
   */
  async getQueriesPatch(tx, lastPatch) {
    const existingQueryHashes = /* @__PURE__ */ new Set();
    const prefix = desiredQueriesPrefixForClient(this.#clientID);
    for await (const key of tx.scan({ prefix }).keys()) {
      existingQueryHashes.add(key.substring(prefix.length, key.length));
    }
    const patch = /* @__PURE__ */ new Map();
    for (const hash of existingQueryHashes) {
      if (!this.#queries.has(hash)) {
        patch.set(hash, { op: "del", hash });
      }
    }
    for (const [hash, { normalized, ttl }] of this.#queries) {
      if (!existingQueryHashes.has(hash)) {
        patch.set(hash, { op: "put", hash, ast: normalized, ttl: parseTTL(ttl) });
      }
    }
    if (lastPatch) {
      for (const [hash, { op }] of lastPatch) {
        if (op === "put" && !patch.has(hash)) {
          patch.set(hash, { op: "del", hash });
        }
      }
      for (const [hash, { op }] of patch) {
        const lastPatchOp = lastPatch.get(hash);
        if (lastPatchOp && lastPatchOp.op === op) {
          patch.delete(hash);
        }
      }
    }
    return patch;
  }
  add(ast, ttl, gotCallback) {
    const normalized = normalizeAST(ast);
    const astHash = hashOfAST(normalized);
    let entry = this.#queries.get(astHash);
    this.#recentQueries.delete(astHash);
    if (!entry) {
      const serverAST = mapAST(normalized, this.#clientToServer);
      entry = {
        normalized: serverAST,
        count: 1,
        gotCallbacks: gotCallback ? [gotCallback] : [],
        ttl
      };
      this.#queries.set(astHash, entry);
      this.#send([
        "changeDesiredQueries",
        {
          desiredQueriesPatch: [
            { op: "put", hash: astHash, ast: serverAST, ttl: parseTTL(ttl) }
          ]
        }
      ]);
    } else {
      ++entry.count;
      this.#updateEntry(entry, astHash, ttl);
      if (gotCallback) {
        entry.gotCallbacks.push(gotCallback);
      }
    }
    if (gotCallback) {
      gotCallback(this.#gotQueries.has(astHash));
    }
    let removed = false;
    return () => {
      if (removed) {
        return;
      }
      removed = true;
      if (this.#mutationTracker.size > 0) {
        this.#pendingRemovals.push(
          () => this.#remove(entry, astHash, gotCallback)
        );
        return;
      }
      this.#remove(entry, astHash, gotCallback);
    };
  }
  update(ast, ttl) {
    const normalized = normalizeAST(ast);
    const astHash = hashOfAST(normalized);
    const entry = must(this.#queries.get(astHash));
    this.#updateEntry(entry, astHash, ttl);
  }
  #updateEntry(entry, hash, ttl) {
    if (compareTTL(ttl, entry.ttl) > 0) {
      entry.ttl = ttl;
      this.#send([
        "changeDesiredQueries",
        {
          desiredQueriesPatch: [
            {
              op: "put",
              hash,
              ast: entry.normalized,
              ttl: parseTTL(ttl)
            }
          ]
        }
      ]);
    }
  }
  #remove(entry, astHash, gotCallback) {
    if (gotCallback) {
      const index = entry.gotCallbacks.indexOf(gotCallback);
      entry.gotCallbacks.splice(index, 1);
    }
    --entry.count;
    if (entry.count === 0) {
      this.#recentQueries.add(astHash);
      if (this.#recentQueries.size > this.#recentQueriesMaxSize) {
        const lruAstHash = this.#recentQueries.values().next().value;
        assert(lruAstHash);
        this.#queries.delete(lruAstHash);
        this.#recentQueries.delete(lruAstHash);
        this.#send([
          "changeDesiredQueries",
          {
            desiredQueriesPatch: [{ op: "del", hash: lruAstHash }]
          }
        ]);
      }
    }
  }
};

// ../zero-client/src/client/reload-error-handler.ts
import "@rocicorp/logger";
var RELOAD_REASON_STORAGE_KEY = "_zeroReloadReason";
var RELOAD_BACKOFF_STATE_KEY = "_zeroReloadBackoffState";
var backoffStateSchema = valita_exports.object({
  lastReloadTime: valita_exports.number().default(0),
  nextIntervalMs: valita_exports.number().default(0)
});
var MIN_RELOAD_INTERVAL_MS = 500;
var MAX_RELOAD_INTERVAL_MS = 6e4;
var FALLBACK_RELOAD_INTERVAL_MS = 1e4;
var reloadTimer = null;
function reloadWithReason(lc, reload2, reason) {
  if (reloadTimer) {
    lc.warn?.("reload timer already scheduled");
    return;
  }
  const now = Date.now();
  const backoff = nextBackoff(lc, now);
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(RELOAD_BACKOFF_STATE_KEY, JSON.stringify(backoff));
    sessionStorage.setItem(RELOAD_REASON_STORAGE_KEY, reason);
  }
  const delay = backoff.lastReloadTime - now;
  lc.error?.(
    reason,
    "\n",
    "reloading",
    delay > 0 ? `in ${delay / 1e3} seconds` : ""
  );
  reloadTimer = setTimeout(() => {
    reloadTimer = null;
    reload2();
  }, delay);
}
function reportReloadReason(lc) {
  if (typeof sessionStorage !== "undefined") {
    const reason = sessionStorage.getItem(RELOAD_REASON_STORAGE_KEY);
    if (reason) {
      sessionStorage.removeItem(RELOAD_REASON_STORAGE_KEY);
      lc.error?.("Zero reloaded the page.", reason);
    }
  }
}
function reloadScheduled() {
  return reloadTimer !== null;
}
function resetBackoff() {
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.removeItem(RELOAD_BACKOFF_STATE_KEY);
  }
}
function nextBackoff(lc, now) {
  if (typeof sessionStorage === "undefined") {
    lc.warn?.(
      `sessionStorage not supported. backing off in ${FALLBACK_RELOAD_INTERVAL_MS / 1e3} seconds`
    );
    return {
      lastReloadTime: now + FALLBACK_RELOAD_INTERVAL_MS,
      nextIntervalMs: MIN_RELOAD_INTERVAL_MS
    };
  }
  const val = sessionStorage.getItem(RELOAD_BACKOFF_STATE_KEY);
  if (!val) {
    return { lastReloadTime: now, nextIntervalMs: MIN_RELOAD_INTERVAL_MS };
  }
  let parsed;
  try {
    parsed = parse(JSON.parse(val), backoffStateSchema, "passthrough");
  } catch (e) {
    lc.warn?.("ignoring unparsable backoff state", val, e);
    return { lastReloadTime: now, nextIntervalMs: MIN_RELOAD_INTERVAL_MS };
  }
  const { lastReloadTime, nextIntervalMs } = parsed;
  if (now - lastReloadTime > MAX_RELOAD_INTERVAL_MS * 2) {
    return { lastReloadTime: now, nextIntervalMs: MIN_RELOAD_INTERVAL_MS };
  }
  if (now < lastReloadTime) {
    return parsed;
  }
  const nextReloadTime = Math.max(now, lastReloadTime + nextIntervalMs);
  return {
    lastReloadTime: nextReloadTime,
    nextIntervalMs: Math.min(nextIntervalMs * 2, MAX_RELOAD_INTERVAL_MS)
  };
}

// ../zero-client/src/client/server-error.ts
var ServerError = class extends Error {
  name = "ServerError";
  errorBody;
  get kind() {
    return this.errorBody.kind;
  }
  constructor(errorBody) {
    super(errorBody.kind + ": " + errorBody.message);
    this.errorBody = errorBody;
  }
};
function isServerError(ex) {
  return ex instanceof ServerError;
}
function isAuthError(ex) {
  return isServerError(ex) && isAuthErrorKind(ex.kind);
}
function isAuthErrorKind(kind) {
  return kind === AuthInvalidated || kind === Unauthorized;
}
function isBackoffError(ex) {
  if (isServerError(ex)) {
    switch (ex.errorBody.kind) {
      case Rebalance:
      case Rehome:
      case ServerOverloaded:
        return ex.errorBody;
    }
  }
  return void 0;
}

// ../zero-client/src/client/server-option.ts
function validateServerParam(paramName, server) {
  const expectedProtocol = "http";
  const forExample = (path = "") => ` For example: "${expectedProtocol}s://myapp-myteam.zero.ms/${path}".`;
  if (!server.startsWith(`${expectedProtocol}://`) && !server.startsWith(`${expectedProtocol}s://`)) {
    throw new Error(
      `ZeroOptions.${paramName} must use the "${expectedProtocol}" or "${expectedProtocol}s" scheme.`
    );
  }
  let url;
  try {
    url = new URL(server);
  } catch {
    throw new Error(
      `ZeroOptions.${paramName} must be a valid URL.${forExample()}`
    );
  }
  const urlString = url.toString();
  const pathComponents = url.pathname.split("/");
  if (pathComponents[0] === "") {
    pathComponents.shift();
  }
  if (pathComponents[pathComponents.length - 1] === "") {
    pathComponents.pop();
  }
  if (pathComponents.length > 1) {
    throw new Error(
      `ZeroOptions.${paramName} may have at most one path component.${forExample(
        "zero"
      )}`
    );
  }
  for (const [property, invalidEndsWith] of [
    ["search", "?"],
    ["hash", "#"]
  ]) {
    if (url[property] || urlString.endsWith(invalidEndsWith)) {
      throw new Error(
        `ZeroOptions.${paramName} must not contain a ${property} component.${forExample()}`
      );
    }
  }
  return urlString;
}
function getServer(server) {
  const WS = getBrowserGlobal("WebSocket");
  if (!WS) {
    console.warn(
      "Zero started in an unsupported environment, no data will be synced."
    );
    return null;
  }
  if (server === void 0 || server === null) {
    console.warn(
      "Zero starting up with no server URL. This is supported for unit testing and prototyping, but no data will be synced."
    );
    return null;
  }
  return validateServerParam("server", server);
}

// ../zero-client/src/client/zero-poke-handler.ts
import { Lock as Lock3 } from "@rocicorp/lock";
var PokeHandler = class {
  #replicachePoke;
  #onPokeError;
  #clientID;
  #lc;
  #receivingPoke = void 0;
  #pokeBuffer = [];
  #pokePlaybackLoopRunning = false;
  #lastRafPerfTimestamp = 0;
  // Serializes calls to this.#replicachePoke otherwise we can cause out of
  // order poke errors.
  #pokeLock = new Lock3();
  #schema;
  #serverToClient;
  #raf = getBrowserGlobalMethod("requestAnimationFrame") ?? rafFallback;
  constructor(replicachePoke, onPokeError, clientID, schema, lc) {
    this.#replicachePoke = replicachePoke;
    this.#onPokeError = onPokeError;
    this.#clientID = clientID;
    this.#schema = schema;
    this.#serverToClient = serverToClient(schema.tables);
    this.#lc = lc.withContext("PokeHandler");
  }
  handlePokeStart(pokeStart) {
    if (this.#receivingPoke) {
      this.#handlePokeError(
        `pokeStart ${JSON.stringify(
          pokeStart
        )} while still receiving  ${JSON.stringify(
          this.#receivingPoke.pokeStart
        )} `
      );
      return;
    }
    this.#receivingPoke = {
      pokeStart,
      parts: []
    };
  }
  handlePokePart(pokePart) {
    if (pokePart.pokeID !== this.#receivingPoke?.pokeStart.pokeID) {
      this.#handlePokeError(
        `pokePart for ${pokePart.pokeID}, when receiving ${this.#receivingPoke?.pokeStart.pokeID}`
      );
      return;
    }
    this.#receivingPoke.parts.push(pokePart);
    return pokePart.lastMutationIDChanges?.[this.#clientID];
  }
  handlePokeEnd(pokeEnd) {
    if (pokeEnd.pokeID !== this.#receivingPoke?.pokeStart.pokeID) {
      this.#handlePokeError(
        `pokeEnd for ${pokeEnd.pokeID}, when receiving ${this.#receivingPoke?.pokeStart.pokeID}`
      );
      return;
    }
    if (pokeEnd.cancel) {
      this.#receivingPoke = void 0;
      return;
    }
    this.#pokeBuffer.push({ ...this.#receivingPoke, pokeEnd });
    this.#receivingPoke = void 0;
    if (!this.#pokePlaybackLoopRunning) {
      this.#startPlaybackLoop();
    }
  }
  handleDisconnect() {
    this.#lc.debug?.("clearing due to disconnect");
    this.#clear();
  }
  #startPlaybackLoop() {
    this.#lc.debug?.("starting playback loop");
    this.#pokePlaybackLoopRunning = true;
    this.#raf(this.#rafCallback);
  }
  #rafCallback = async () => {
    const rafLC = this.#lc.withContext("rafAt", Math.floor(performance.now()));
    if (this.#pokeBuffer.length === 0) {
      rafLC.debug?.("stopping playback loop");
      this.#pokePlaybackLoopRunning = false;
      return;
    }
    this.#raf(this.#rafCallback);
    const start = performance.now();
    rafLC.debug?.(
      "raf fired, processing pokes.  Since last raf",
      start - this.#lastRafPerfTimestamp
    );
    this.#lastRafPerfTimestamp = start;
    await this.#processPokesForFrame(rafLC);
    rafLC.debug?.("processing pokes took", performance.now() - start);
  };
  #processPokesForFrame(lc) {
    return this.#pokeLock.withLock(async () => {
      const now = Date.now();
      lc.debug?.("got poke lock at", now);
      lc.debug?.("merging", this.#pokeBuffer.length);
      try {
        const merged = mergePokes(
          this.#pokeBuffer,
          this.#schema,
          this.#serverToClient
        );
        this.#pokeBuffer.length = 0;
        if (merged === void 0) {
          lc.debug?.("frame is empty");
          return;
        }
        const start = performance.now();
        lc.debug?.("poking replicache");
        await this.#replicachePoke(merged);
        lc.debug?.("poking replicache took", performance.now() - start);
      } catch (e) {
        this.#handlePokeError(e);
      }
    });
  }
  #handlePokeError(e) {
    if (String(e).includes("unexpected base cookie for poke")) {
      this.#lc.debug?.("clearing due to", e);
    } else {
      this.#lc.error?.("clearing due to unexpected poke error", e);
    }
    this.#clear();
    this.#onPokeError();
  }
  #clear() {
    this.#receivingPoke = void 0;
    this.#pokeBuffer.length = 0;
  }
};
function mergePokes(pokeBuffer, schema, serverToClient2) {
  if (pokeBuffer.length === 0) {
    return void 0;
  }
  const { baseCookie } = pokeBuffer[0].pokeStart;
  const lastPoke = pokeBuffer[pokeBuffer.length - 1];
  const { cookie } = lastPoke.pokeEnd;
  const mergedPatch = [];
  const mergedLastMutationIDChanges = {};
  let prevPokeEnd = void 0;
  for (const pokeAccumulator of pokeBuffer) {
    if (prevPokeEnd && pokeAccumulator.pokeStart.baseCookie && pokeAccumulator.pokeStart.baseCookie > prevPokeEnd.cookie) {
      throw Error(
        `unexpected cookie gap ${JSON.stringify(prevPokeEnd)} ${JSON.stringify(
          pokeAccumulator.pokeStart
        )}`
      );
    }
    prevPokeEnd = pokeAccumulator.pokeEnd;
    for (const pokePart of pokeAccumulator.parts) {
      if (pokePart.lastMutationIDChanges) {
        for (const [clientID, lastMutationID] of Object.entries(
          pokePart.lastMutationIDChanges
        )) {
          mergedLastMutationIDChanges[clientID] = lastMutationID;
        }
      }
      if (pokePart.desiredQueriesPatches) {
        for (const [clientID, queriesPatch] of Object.entries(
          pokePart.desiredQueriesPatches
        )) {
          mergedPatch.push(
            ...queriesPatch.map(
              (op) => queryPatchOpToReplicachePatchOp(
                op,
                (hash) => toDesiredQueriesKey(clientID, hash),
                serverToClient2
              )
            )
          );
        }
      }
      if (pokePart.gotQueriesPatch) {
        mergedPatch.push(
          ...pokePart.gotQueriesPatch.map(
            (op) => queryPatchOpToReplicachePatchOp(
              op,
              toGotQueriesKey,
              serverToClient2
            )
          )
        );
      }
      if (pokePart.rowsPatch) {
        mergedPatch.push(
          ...pokePart.rowsPatch.map(
            (p) => rowsPatchOpToReplicachePatchOp(p, schema, serverToClient2)
          )
        );
      }
    }
  }
  return {
    baseCookie,
    pullResponse: {
      lastMutationIDChanges: mergedLastMutationIDChanges,
      patch: mergedPatch,
      cookie
    }
  };
}
function queryPatchOpToReplicachePatchOp(op, toKey, serverToClient2) {
  switch (op.op) {
    case "clear":
      return op;
    case "del":
      return {
        op: "del",
        key: toKey(op.hash)
      };
    case "put":
    default:
      return {
        op: "put",
        key: toKey(op.hash),
        value: mapAST(op.ast, serverToClient2)
      };
  }
}
function rowsPatchOpToReplicachePatchOp(op, schema, serverToClient2) {
  if (op.op === "clear") {
    return op;
  }
  const tableName = serverToClient2.tableName(op.tableName, op);
  switch (op.op) {
    case "del":
      return {
        op: "del",
        key: toPrimaryKeyString(
          tableName,
          schema.tables[tableName].primaryKey,
          serverToClient2.row(op.tableName, op.id)
        )
      };
    case "put":
      return {
        op: "put",
        key: toPrimaryKeyString(
          tableName,
          schema.tables[tableName].primaryKey,
          serverToClient2.row(op.tableName, op.value)
        ),
        value: serverToClient2.row(op.tableName, op.value)
      };
    case "update":
      return {
        op: "update",
        key: toPrimaryKeyString(
          tableName,
          schema.tables[tableName].primaryKey,
          serverToClient2.row(op.tableName, op.id)
        ),
        merge: op.merge ? serverToClient2.row(op.tableName, op.merge) : void 0,
        constrain: serverToClient2.columns(op.tableName, op.constrain)
      };
    default:
      throw new Error("to be implemented");
  }
}
function rafFallback(callback) {
  setTimeout(callback, 0);
}

// ../zero-client/src/client/zero-rep.ts
var ZeroRep = class {
  #context;
  #ivmMain;
  #customMutatorsEnabled;
  #mutationTracker;
  #store;
  #auth;
  constructor(context, ivmMain, customMutatorsEnabled, mutationTracker) {
    this.#context = context;
    this.#ivmMain = ivmMain;
    this.#customMutatorsEnabled = customMutatorsEnabled;
    this.#mutationTracker = mutationTracker;
  }
  set auth(auth) {
    if (auth === "") {
      this.#auth = void 0;
    } else {
      this.#auth = auth;
    }
  }
  async init(hash, store) {
    const diffs = [];
    await withRead(store, async (dagRead) => {
      const read = await readFromHash(hash, dagRead, Latest);
      for await (const entry of read.map.scan(ENTITIES_KEY_PREFIX)) {
        if (!entry[0].startsWith(ENTITIES_KEY_PREFIX)) {
          break;
        }
        diffs.push({
          op: "add",
          key: entry[0],
          newValue: entry[1]
        });
      }
    });
    this.#store = store;
    this.#context.processChanges(void 0, hash, diffs);
  }
  getTxData = (desiredHead, readOptions) => {
    if (!this.#customMutatorsEnabled) {
      return;
    }
    return this.#ivmMain.forkToHead(must(this.#store), desiredHead, readOptions).then((branch) => ({
      ivmSources: branch,
      token: this.#auth
    }));
  };
  advance = (expectedHash, newHash, diffs) => {
    this.#context.processChanges(expectedHash, newHash, diffs);
  };
  trackMutation() {
    return this.#mutationTracker.trackMutation();
  }
  mutationIDAssigned(ephemeralID, mutationID) {
    this.#mutationTracker.mutationIDAssigned(ephemeralID, mutationID);
  }
  rejectMutation(ephemeralID, ex) {
    this.#mutationTracker.rejectMutation(ephemeralID, ex);
  }
};

// ../zero-client/src/client/zero.ts
var onSetConnectionStateSymbol = Symbol();
var exposedToTestingSymbol = Symbol();
var createLogOptionsSymbol = Symbol();
var RUN_LOOP_INTERVAL_MS = 5e3;
var PING_INTERVAL_MS = 5e3;
var PING_TIMEOUT_MS = 5e3;
var PULL_TIMEOUT_MS = 5e3;
var DEFAULT_DISCONNECT_HIDDEN_DELAY_MS = 5e3;
var CONNECT_TIMEOUT_MS = 1e4;
var CHECK_CONNECTIVITY_ON_ERROR_FREQUENCY = 6;
var NULL_LAST_MUTATION_ID_SENT = { clientID: "", id: -1 };
function convertOnUpdateNeededReason(reason) {
  return { type: reason.type };
}
function updateNeededReloadReason(reason, serverErrMsg) {
  const { type } = reason;
  let reasonMsg = "";
  switch (type) {
    case "NewClientGroup":
      reasonMsg = "This client could not sync with a newer client. This is probably due to another tab loading a newer incompatible version of the app's code.";
      break;
    case "VersionNotSupported":
      reasonMsg = "The server no longer supports this client's protocol version.";
      break;
    case "SchemaVersionNotSupported":
      reasonMsg = "Client and server schemas incompatible.";
      break;
    default:
      unreachable(type);
  }
  if (serverErrMsg) {
    reasonMsg += " " + serverErrMsg;
  }
  return reasonMsg;
}
function serverAheadReloadReason(kind) {
  return `Server reported that client is ahead of server (${kind}). This probably happened because the server is in development mode and restarted. Currently when this happens, the dev server loses its state and on reconnect sees the client as ahead. If you see this in other cases, it may be a bug in Zero.`;
}
function onClientStateNotFoundServerReason(serverErrMsg) {
  return `Server could not find state needed to synchronize this client. ${serverErrMsg}`;
}
var ON_CLIENT_STATE_NOT_FOUND_REASON_CLIENT = "The local persistent state needed to synchronize this client has been garbage collected.";
var CLOSE_CODE_NORMAL = 1e3;
var Zero = class {
  version = version2;
  #rep;
  #server;
  userID;
  storageKey;
  #lc;
  #logOptions;
  #enableAnalytics;
  #schema;
  #clientSchema;
  #pokeHandler;
  #queryManager;
  #ivmMain;
  #clientToServer;
  #deleteClientsManager;
  #mutationTracker;
  /**
   * The queries we sent when inside the sec-protocol header when establishing a connection.
   * More queries could be registered while we're waiting for the 'connected' message
   * to come back from the server. To understand what queries we need to send
   * to the server, we diff the `initConnectionQueries` with the current set of desired queries.
   *
   * If this is set to `undefined` that means no queries were sent inside the `sec-protocol` header
   * and an `initConnection` message must be sent to the server after receiving the `connected` message.
   */
  #initConnectionQueries;
  /**
   * We try to send the deleted clients and (client groups) as part of the
   * sec-protocol header. If we can't because the header would get too large we
   * keep track of the deleted clients and send them after the connection is
   * established.
   */
  #deletedClients;
  #lastMutationIDSent = NULL_LAST_MUTATION_ID_SENT;
  #onPong = () => void 0;
  #online = false;
  #onOnlineChange;
  #onUpdateNeeded;
  #onClientStateNotFound;
  // Last cookie used to initiate a connection
  #connectCookie = null;
  // Total number of sockets successfully connected by this client
  #connectedCount = 0;
  // Number of messages received over currently connected socket.  Reset
  // on disconnect.
  #messageCount = 0;
  #connectedAt = 0;
  // Reset on successful connection.
  #connectErrorCount = 0;
  #abortPingTimeout = () => {
  };
  #zeroContext;
  #connectResolver = resolver8();
  #pendingPullsByRequestID = /* @__PURE__ */ new Map();
  #lastMutationIDReceived = 0;
  #socket = void 0;
  #socketResolver = resolver8();
  #connectionStateChangeResolver = resolver8();
  /**
   * This resolver is only used for rejections. It is awaited in the connected
   * state (including when waiting for a pong). It is rejected when we get an
   * invalid message or an 'error' message.
   */
  #rejectMessageError = void 0;
  #closeAbortController = new AbortController();
  #visibilityWatcher;
  // We use an accessor pair to allow the subclass to override the setter.
  #connectionState = Disconnected;
  #setConnectionState(state) {
    if (state === this.#connectionState) {
      return;
    }
    this.#connectionState = state;
    this.#connectionStateChangeResolver.resolve(state);
    this.#connectionStateChangeResolver = resolver8();
    if (false) {
      asTestZero(this)[onSetConnectionStateSymbol]?.(state);
    }
  }
  #connectStart = void 0;
  // Set on connect attempt if currently undefined.
  // Reset to undefined when
  // 1. client stops trying to connect because it is hidden
  // 2. client encounters a connect error and canary request indicates
  //    the client is offline
  // 2. client successfully connects
  #totalToConnectStart = void 0;
  #options;
  query;
  // TODO: Metrics needs to be rethought entirely as we're not going to
  // send metrics to customer server.
  #metrics;
  // Store as field to allow test subclass to override. Web API doesn't allow
  // overwriting location fields for security reasons.
  #reload = () => getBrowserGlobal("location")?.reload();
  /**
   * Constructs a new Zero client.
   */
  constructor(options) {
    const {
      userID,
      storageKey,
      onOnlineChange,
      onUpdateNeeded,
      onClientStateNotFound,
      hiddenTabDisconnectDelay = DEFAULT_DISCONNECT_HIDDEN_DELAY_MS,
      schema,
      batchViewUpdates = (applyViewUpdates) => applyViewUpdates(),
      maxRecentQueries = 0,
      slowMaterializeThreshold = 5e3
    } = options;
    if (!userID) {
      throw new Error("ZeroOptions.userID must not be empty.");
    }
    const server = getServer(options.server);
    this.#enableAnalytics = shouldEnableAnalytics(
      server,
      false
      // Reenable analytics
    );
    let { kvStore = "idb" } = options;
    if (kvStore === "idb") {
      if (!getBrowserGlobal("indexedDB")) {
        console.warn(
          "IndexedDB is not supported in this environment. Falling back to memory storage."
        );
        kvStore = "mem";
      }
    }
    if (hiddenTabDisconnectDelay < 0) {
      throw new Error(
        "ZeroOptions.hiddenTabDisconnectDelay must not be negative."
      );
    }
    this.#onOnlineChange = onOnlineChange;
    this.#options = options;
    this.#logOptions = this.#createLogOptions({
      consoleLogLevel: options.logLevel ?? "error",
      server: null,
      //server, // Reenable remote logging
      enableAnalytics: this.#enableAnalytics
    });
    const logOptions = this.#logOptions;
    const replicacheMutators = {
      [CRUD_MUTATION_NAME]: makeCRUDMutator(schema)
    };
    this.#ivmMain = new IVMSourceBranch(schema.tables);
    function assertUnique(key) {
      assert(
        replicacheMutators[key] === void 0,
        `A mutator, or mutator namespace, has already been defined for ${key}`
      );
    }
    const lc = new LogContext4(logOptions.logLevel, {}, logOptions.logSink);
    this.#mutationTracker = new MutationTracker(lc);
    if (options.mutators) {
      for (const [namespaceOrKey, mutatorOrMutators] of Object.entries(
        options.mutators
      )) {
        if (typeof mutatorOrMutators === "function") {
          const key = namespaceOrKey;
          assertUnique(key);
          replicacheMutators[key] = makeReplicacheMutator(
            lc,
            mutatorOrMutators,
            schema,
            slowMaterializeThreshold
            // Replicache expects mutators to only be able to return JSON
            // but Zero wraps the return with: `{server?: Promise<MutationResult>, client?: T}`
          );
          continue;
        }
        if (typeof mutatorOrMutators === "object") {
          for (const [name, mutator] of Object.entries(mutatorOrMutators)) {
            const key = customMutatorKey(
              namespaceOrKey,
              name
            );
            assertUnique(key);
            replicacheMutators[key] = makeReplicacheMutator(
              lc,
              mutator,
              schema,
              slowMaterializeThreshold
            );
          }
          continue;
        }
        unreachable(mutatorOrMutators);
      }
    }
    this.storageKey = storageKey ?? "";
    this.#schema = schema;
    const { clientSchema, hash } = clientSchemaFrom(schema);
    this.#clientSchema = clientSchema;
    const replicacheOptions = {
      // The schema stored in IDB is dependent upon both the ClientSchema
      // and the AST schema (i.e. PROTOCOL_VERSION).
      schemaVersion: `${PROTOCOL_VERSION}.${hash}`,
      logLevel: logOptions.logLevel,
      logSinks: [logOptions.logSink],
      mutators: replicacheMutators,
      name: `zero-${userID}-${this.storageKey}`,
      pusher: (req, reqID) => this.#pusher(req, reqID),
      puller: (req, reqID) => this.#puller(req, reqID),
      pushDelay: 0,
      requestOptions: {
        maxDelayMs: 0,
        minDelayMs: 0
      },
      licenseKey: "zero-client-static-key",
      kvStore
    };
    this.#zeroContext = new ZeroContext(
      lc,
      this.#ivmMain,
      (ast, ttl, gotCallback) => this.#queryManager.add(ast, ttl, gotCallback),
      (ast, ttl) => this.#queryManager.update(ast, ttl),
      batchViewUpdates,
      slowMaterializeThreshold
    );
    const replicacheImplOptions = {
      enableClientGroupForking: false,
      enableMutationRecovery: false,
      enablePullAndPushInOpen: false,
      // Zero calls push in its connection management code
      onClientsDeleted: (clientIDs, clientGroupIDs) => this.#deleteClientsManager.onClientsDeleted(clientIDs, clientGroupIDs),
      zero: new ZeroRep(
        this.#zeroContext,
        this.#ivmMain,
        options.mutators !== void 0,
        this.#mutationTracker
      )
    };
    const rep = new ReplicacheImpl(replicacheOptions, replicacheImplOptions);
    this.#rep = rep;
    if (false) {
      internalReplicacheImplMap.set(this, rep);
    }
    this.#server = server;
    this.userID = userID;
    this.#lc = lc.withContext("clientID", rep.clientID);
    this.#mutationTracker.clientID = rep.clientID;
    const onUpdateNeededCallback = onUpdateNeeded ?? ((reason, serverErrorMsg) => {
      reloadWithReason(
        this.#lc,
        this.#reload,
        updateNeededReloadReason(reason, serverErrorMsg)
      );
    });
    this.#onUpdateNeeded = onUpdateNeededCallback;
    this.#rep.onUpdateNeeded = (reason) => {
      onUpdateNeededCallback(convertOnUpdateNeededReason(reason));
    };
    const onClientStateNotFoundCallback = onClientStateNotFound ?? ((reason) => {
      reloadWithReason(
        this.#lc,
        this.#reload,
        reason ?? ON_CLIENT_STATE_NOT_FOUND_REASON_CLIENT
      );
    });
    this.#onClientStateNotFound = onClientStateNotFoundCallback;
    this.#rep.onClientStateNotFound = onClientStateNotFoundCallback;
    const { mutate, mutateBatch } = makeCRUDMutate(schema, rep.mutate);
    if (options.mutators) {
      for (const [namespaceOrKey, mutatorsOrMutator] of Object.entries(
        options.mutators
      )) {
        if (typeof mutatorsOrMutator === "function") {
          mutate[namespaceOrKey] = must(rep.mutate[namespaceOrKey]);
          continue;
        }
        let existing = mutate[namespaceOrKey];
        if (existing === void 0) {
          existing = {};
          mutate[namespaceOrKey] = existing;
        }
        for (const name of Object.keys(mutatorsOrMutator)) {
          existing[name] = must(
            rep.mutate[customMutatorKey(namespaceOrKey, name)]
          );
        }
      }
    }
    this.mutate = mutate;
    this.mutateBatch = mutateBatch;
    this.#queryManager = new QueryManager(
      this.#mutationTracker,
      rep.clientID,
      schema.tables,
      (msg) => this.#sendChangeDesiredQueries(msg),
      rep.experimentalWatch.bind(rep),
      maxRecentQueries
    );
    this.#clientToServer = clientToServer(schema.tables);
    this.#deleteClientsManager = new DeleteClientsManager(
      (msg) => this.#send(msg),
      rep.perdag,
      this.#lc
    );
    this.query = this.#registerQueries(schema);
    reportReloadReason(this.#lc);
    this.#metrics = new MetricManager({
      reportIntervalMs: REPORT_INTERVAL_MS,
      host: getBrowserGlobal("location")?.host ?? "",
      source: "client",
      reporter: this.#enableAnalytics ? (allSeries) => this.#reportMetrics(allSeries) : () => Promise.resolve(),
      lc: this.#lc
    });
    this.#metrics.tags.push(`version:${this.version}`);
    this.#pokeHandler = new PokeHandler(
      (poke) => this.#rep.poke(poke),
      () => this.#onPokeError(),
      rep.clientID,
      schema,
      this.#lc
    );
    this.#visibilityWatcher = getDocumentVisibilityWatcher(
      getBrowserGlobal("document"),
      hiddenTabDisconnectDelay,
      this.#closeAbortController.signal
    );
    void this.#runLoop();
    if (false) {
      asTestZero(this)[exposedToTestingSymbol] = {
        puller: this.#puller,
        pusher: this.#pusher,
        setReload: (r) => {
          this.#reload = r;
        },
        logOptions: this.#logOptions,
        connectStart: () => this.#connectStart,
        socketResolver: () => this.#socketResolver,
        connectionState: () => this.#connectionState
      };
    }
  }
  #sendChangeDesiredQueries(msg) {
    this.#send(msg);
  }
  #send(msg) {
    if (this.#socket && this.#connectionState === Connected) {
      send(this.#socket, msg);
    }
  }
  #createLogOptions(options) {
    if (false) {
      const testZero = asTestZero(this);
      if (testZero[createLogOptionsSymbol]) {
        return testZero[createLogOptionsSymbol](options);
      }
    }
    return createLogOptions(options);
  }
  /**
   * The server URL that this Zero instance is configured with.
   */
  get server() {
    return this.#server;
  }
  /**
   * The name of the IndexedDB database in which the data of this
   * instance of Zero is stored.
   */
  get idbName() {
    return this.#rep.idbName;
  }
  /**
   * The schema version of the data understood by this application.
   * See [[ZeroOptions.schemaVersion]].
   */
  get schemaVersion() {
    return this.#rep.schemaVersion;
  }
  /**
   * The client ID for this instance of Zero. Each instance
   * gets a unique client ID.
   */
  get clientID() {
    return this.#rep.clientID;
  }
  get clientGroupID() {
    return this.#rep.clientGroupID;
  }
  /**
   * Provides simple "CRUD" mutations for the tables in the schema.
   *
   * Each table has `create`, `set`, `update`, and `delete` methods.
   *
   * ```ts
   * await zero.mutate.issue.create({id: '1', title: 'First issue', priority: 'high'});
   * await zero.mutate.comment.create({id: '1', text: 'First comment', issueID: '1'});
   * ```
   *
   * The `update` methods support partials. Unspecified or `undefined` fields
   * are left unchanged:
   *
   * ```ts
   * // Priority left unchanged.
   * await zero.mutate.issue.update({id: '1', title: 'Updated title'});
   * ```
   */
  mutate;
  /**
   * Provides a way to batch multiple CRUD mutations together:
   *
   * ```ts
   * await zero.mutateBatch(m => {
   *   await m.issue.create({id: '1', title: 'First issue'});
   *   await m.comment.create({id: '1', text: 'First comment', issueID: '1'});
   * });
   * ```
   *
   * Batch sends all mutations in a single transaction. If one fails, all are
   * rolled back together. Batch can also be more efficient than making many
   * individual mutations.
   *
   * `mutateBatch` is not allowed inside another `mutateBatch` call. Doing so
   * will throw an error.
   */
  mutateBatch;
  /**
   * Whether this Zero instance has been closed.
   *
   * Once a Zero instance has been closed it no longer syncs, you can no
   * longer query or mutate data with it, and its query views stop updating.
   */
  get closed() {
    return this.#rep.closed;
  }
  /**
   * Closes this Zero instance.
   *
   * Once a Zero instance has been closed it no longer syncs, you can no
   * longer query or mutate data with it, and its query views stop updating.
   */
  close() {
    const lc = this.#lc.withContext("close");
    lc.debug?.("Closing Zero instance. Stack:", new Error().stack);
    if (this.#connectionState !== Disconnected) {
      let closeReason = JSON.stringify([
        "closeConnection",
        []
      ]);
      if (closeReason.length > 123) {
        lc.warn?.("Close reason is too long. Removing it.");
        closeReason = "";
      }
      this.#disconnect(
        lc,
        {
          client: "ClientClosed"
        },
        CLOSE_CODE_NORMAL,
        closeReason
      );
    }
    lc.debug?.("Aborting closeAbortController due to close()");
    this.#closeAbortController.abort();
    this.#metrics.stop();
    return this.#rep.close();
  }
  #onPageHide = (e) => {
    if (e.persisted) {
      this.#lc.debug?.("Ignoring pagehide event because it was persisted");
    } else {
      this.#lc.debug?.("Closing client because we got a clean close");
      this.close().catch(() => {
      });
    }
  };
  #onMessage = (e) => {
    const lc = this.#lc;
    lc.debug?.("received message", e.data);
    if (this.closed) {
      lc.debug?.("ignoring message because already closed");
      return;
    }
    const rejectInvalidMessage = (e2) => this.#rejectMessageError?.reject(
      new Error(
        `Invalid message received from server: ${e2 instanceof Error ? e2.message + ". " : ""}${data}`
      )
    );
    let downMessage;
    const { data } = e;
    try {
      downMessage = parse(JSON.parse(data), downstreamSchema);
    } catch (e2) {
      rejectInvalidMessage(e2);
      return;
    }
    this.#messageCount++;
    const msgType = downMessage[0];
    switch (msgType) {
      case "connected":
        return this.#handleConnectedMessage(lc, downMessage);
      case "error":
        return this.#handleErrorMessage(lc, downMessage);
      case "pong":
        resetBackoff();
        return this.#onPong();
      case "pokeStart":
        return this.#handlePokeStart(lc, downMessage);
      case "pokePart":
        if (downMessage[1].rowsPatch) {
          resetBackoff();
        }
        return this.#handlePokePart(lc, downMessage);
      case "pokeEnd":
        return this.#handlePokeEnd(lc, downMessage);
      case "pull":
        return this.#handlePullResponse(lc, downMessage);
      case "deleteClients":
        return this.#deleteClientsManager.clientsDeletedOnServer(
          downMessage[1]
        );
      case "pushResponse":
        return this.#mutationTracker.processPushResponse(downMessage[1]);
      case "inspect":
        break;
      default:
        msgType;
        rejectInvalidMessage();
    }
  };
  #onOpen = () => {
    const l = addWebSocketIDFromSocketToLogContext(this.#socket, this.#lc);
    if (this.#connectStart === void 0) {
      l.error?.(
        "Got open event but connect start time is undefined. This should not happen."
      );
    } else {
      const now = Date.now();
      const timeToOpenMs = now - this.#connectStart;
      l.info?.("Got socket open event", {
        navigatorOnline: localNavigator?.onLine,
        timeToOpenMs
      });
    }
  };
  #onClose = (e) => {
    const l = addWebSocketIDFromSocketToLogContext(this.#socket, this.#lc);
    const { code, reason, wasClean } = e;
    const log = code <= 1001 ? "info" : "error";
    l[log]?.("Got socket close event", { code, reason, wasClean });
    const closeKind = wasClean ? "CleanClose" : "AbruptClose";
    this.#connectResolver.reject(new CloseError(closeKind));
    this.#disconnect(l, { client: closeKind });
  };
  // An error on the connection is fatal for the connection.
  async #handleErrorMessage(lc, downMessage) {
    const [, { kind, message }] = downMessage;
    if (kind === MutationRateLimited) {
      this.#lastMutationIDSent = NULL_LAST_MUTATION_ID_SENT;
      lc.error?.("Mutation rate limited", { message });
      return;
    }
    lc.info?.(`${kind}: ${message}}`);
    const error = new ServerError(downMessage[1]);
    this.#rejectMessageError?.reject(error);
    lc.debug?.("Rejecting connect resolver due to error", error);
    this.#connectResolver.reject(error);
    this.#disconnect(lc, { server: kind });
    if (kind === VersionNotSupported) {
      this.#onUpdateNeeded?.({ type: kind }, message);
    } else if (kind === SchemaVersionNotSupported) {
      await this.#rep.disableClientGroup();
      this.#onUpdateNeeded?.({ type: "SchemaVersionNotSupported" }, message);
    } else if (kind === ClientNotFound) {
      await this.#rep.disableClientGroup();
      this.#onClientStateNotFound?.(onClientStateNotFoundServerReason(message));
    } else if (kind === InvalidConnectionRequestLastMutationID || kind === InvalidConnectionRequestBaseCookie) {
      await dropDatabase(this.#rep.idbName);
      reloadWithReason(lc, this.#reload, serverAheadReloadReason(kind));
    }
  }
  async #handleConnectedMessage(lc, connectedMessage) {
    const now = Date.now();
    const [, connectBody] = connectedMessage;
    lc = addWebSocketIDToLogContext(connectBody.wsid, lc);
    if (this.#connectedCount === 0) {
      this.#checkConnectivity("firstConnect");
    } else if (this.#connectErrorCount > 0) {
      this.#checkConnectivity("connectAfterError");
    }
    this.#connectedCount++;
    this.#connectedAt = now;
    this.#metrics.lastConnectError.clear();
    const proceedingConnectErrorCount = this.#connectErrorCount;
    this.#connectErrorCount = 0;
    let timeToConnectMs;
    let connectMsgLatencyMs;
    if (this.#connectStart === void 0) {
      lc.error?.(
        "Got connected message but connect start time is undefined. This should not happen."
      );
    } else {
      timeToConnectMs = now - this.#connectStart;
      this.#metrics.timeToConnectMs.set(timeToConnectMs);
      connectMsgLatencyMs = connectBody.timestamp !== void 0 ? now - connectBody.timestamp : void 0;
      this.#connectStart = void 0;
    }
    let totalTimeToConnectMs;
    if (this.#totalToConnectStart === void 0) {
      lc.error?.(
        "Got connected message but total to connect start time is undefined. This should not happen."
      );
    } else {
      totalTimeToConnectMs = now - this.#totalToConnectStart;
      this.#totalToConnectStart = void 0;
    }
    this.#metrics.setConnected(timeToConnectMs ?? 0, totalTimeToConnectMs ?? 0);
    lc.info?.("Connected", {
      navigatorOnline: localNavigator?.onLine,
      timeToConnectMs,
      totalTimeToConnectMs,
      connectMsgLatencyMs,
      connectedCount: this.#connectedCount,
      proceedingConnectErrorCount
    });
    this.#lastMutationIDSent = NULL_LAST_MUTATION_ID_SENT;
    lc.debug?.("Resolving connect resolver");
    const socket = must(this.#socket);
    const queriesPatch = await this.#rep.query(
      (tx) => this.#queryManager.getQueriesPatch(tx, this.#initConnectionQueries)
    );
    const hasDeletedClients = () => skipEmptyArray(this.#deletedClients?.clientIDs) || skipEmptyArray(this.#deletedClients?.clientGroupIDs);
    const maybeSendDeletedClients = () => {
      if (hasDeletedClients()) {
        send(socket, ["deleteClients", this.#deletedClients]);
        this.#deletedClients = void 0;
      }
    };
    if (queriesPatch.size > 0 && this.#initConnectionQueries !== void 0) {
      maybeSendDeletedClients();
      send(socket, [
        "changeDesiredQueries",
        {
          desiredQueriesPatch: [...queriesPatch.values()]
        }
      ]);
    } else if (this.#initConnectionQueries === void 0) {
      const clientSchema = this.#clientSchema;
      send(socket, [
        "initConnection",
        {
          desiredQueriesPatch: [...queriesPatch.values()],
          deleted: skipEmptyDeletedClients(this.#deletedClients),
          // The clientSchema only needs to be sent for the very first request.
          // Henceforth it is stored with the CVR and verified automatically.
          ...this.#connectCookie === null ? { clientSchema } : {}
        }
      ]);
      this.#deletedClients = void 0;
    }
    this.#initConnectionQueries = void 0;
    maybeSendDeletedClients();
    this.#setConnectionState(Connected);
    this.#connectResolver.resolve();
  }
  /**
   * Starts a new connection. This will create the WebSocket that does the HTTP
   * request to the server.
   *
   * {@link #connect} will throw an assertion error if the
   * {@link #connectionState} is not {@link ConnectionState.Disconnected}.
   * Callers MUST check the connection state before calling this method and log
   * an error as needed.
   *
   * The function will resolve once the socket is connected. If you need to know
   * when a connection has been established, as in we have received the
   * {@link ConnectedMessage}, you should await the {@link #connectResolver}
   * promise. The {@link #connectResolver} promise rejects if an error message
   * is received before the connected message is received or if the connection
   * attempt times out.
   */
  async #connect(l, additionalConnectParams) {
    assert(this.#server);
    assert(this.#connectionState === Disconnected);
    const wsid = nanoid();
    l = addWebSocketIDToLogContext(wsid, l);
    l.info?.("Connecting...", { navigatorOnline: localNavigator?.onLine });
    this.#setConnectionState(Connecting);
    assert(this.#connectStart === void 0);
    const now = Date.now();
    this.#connectStart = now;
    if (this.#totalToConnectStart === void 0) {
      this.#totalToConnectStart = now;
    }
    if (this.closed) {
      return;
    }
    this.#connectCookie = parse(
      await this.#rep.cookie,
      nullableVersionSchema
    );
    if (this.closed) {
      return;
    }
    const timeoutID = setTimeout(() => {
      l.debug?.("Rejecting connect resolver due to timeout");
      this.#connectResolver.reject(new TimedOutError("Connect"));
      this.#disconnect(l, {
        client: "ConnectTimeout"
      });
    }, CONNECT_TIMEOUT_MS);
    const abortHandler = () => {
      clearTimeout(timeoutID);
    };
    this.#closeAbortController.signal.addEventListener("abort", abortHandler);
    const [ws, initConnectionQueries, deletedClients] = await createSocket(
      this.#rep,
      this.#queryManager,
      this.#deleteClientsManager,
      toWSString(this.#server),
      this.#connectCookie,
      this.clientID,
      await this.clientGroupID,
      this.#clientSchema,
      this.userID,
      this.#rep.auth,
      this.#lastMutationIDReceived,
      wsid,
      this.#options.logLevel === "debug",
      l,
      this.#options.maxHeaderLength,
      additionalConnectParams
    );
    if (this.closed) {
      return;
    }
    this.#initConnectionQueries = initConnectionQueries;
    this.#deletedClients = deletedClients;
    ws.addEventListener("message", this.#onMessage);
    ws.addEventListener("open", this.#onOpen);
    ws.addEventListener("close", this.#onClose);
    this.#socket = ws;
    this.#socketResolver.resolve(ws);
    getBrowserGlobal("window")?.addEventListener?.(
      "pagehide",
      this.#onPageHide
    );
    try {
      l.debug?.("Waiting for connection to be acknowledged");
      await this.#connectResolver.promise;
      this.#mutationTracker.onConnected(this.#lastMutationIDReceived);
      this.#rep.push().catch(() => {
      });
    } finally {
      clearTimeout(timeoutID);
      this.#closeAbortController.signal.removeEventListener(
        "abort",
        abortHandler
      );
    }
  }
  #disconnect(l, reason, closeCode, closeReason) {
    if (this.#connectionState === Connecting) {
      this.#connectErrorCount++;
    }
    l.info?.("disconnecting", {
      navigatorOnline: localNavigator?.onLine,
      reason,
      connectStart: this.#connectStart,
      totalToConnectStart: this.#totalToConnectStart,
      connectedAt: this.#connectedAt,
      connectionDuration: this.#connectedAt ? Date.now() - this.#connectedAt : 0,
      messageCount: this.#messageCount,
      connectionState: this.#connectionState,
      connectErrorCount: this.#connectErrorCount
    });
    switch (this.#connectionState) {
      case Connected: {
        if (this.#connectStart !== void 0) {
          l.error?.(
            "disconnect() called while connected but connect start time is defined. This should not happen."
          );
        }
        break;
      }
      case Connecting: {
        this.#metrics.lastConnectError.set(getLastConnectErrorValue(reason));
        this.#metrics.timeToConnectMs.set(DID_NOT_CONNECT_VALUE);
        this.#metrics.setConnectError(reason);
        if (this.#connectErrorCount % CHECK_CONNECTIVITY_ON_ERROR_FREQUENCY === 1) {
          this.#checkConnectivity(
            `connectErrorCount=${this.#connectErrorCount}`
          );
        }
        if (this.#connectStart === void 0) {
          l.error?.(
            "disconnect() called while connecting but connect start time is undefined. This should not happen."
          );
        }
        break;
      }
      case Disconnected:
        l.error?.("disconnect() called while disconnected");
        break;
    }
    this.#socketResolver = resolver8();
    l.debug?.("Creating new connect resolver");
    this.#connectResolver = resolver8();
    this.#setConnectionState(Disconnected);
    this.#messageCount = 0;
    this.#connectStart = void 0;
    this.#connectedAt = 0;
    this.#socket?.removeEventListener("message", this.#onMessage);
    this.#socket?.removeEventListener("open", this.#onOpen);
    this.#socket?.removeEventListener("close", this.#onClose);
    this.#socket?.close(closeCode, closeReason);
    this.#socket = void 0;
    this.#lastMutationIDSent = NULL_LAST_MUTATION_ID_SENT;
    this.#pokeHandler.handleDisconnect();
    getBrowserGlobal("window")?.removeEventListener?.(
      "pagehide",
      this.#onPageHide
    );
  }
  #handlePokeStart(_lc, pokeMessage) {
    this.#abortPingTimeout();
    this.#pokeHandler.handlePokeStart(pokeMessage[1]);
  }
  #handlePokePart(_lc, pokeMessage) {
    this.#abortPingTimeout();
    const lastMutationIDChangeForSelf = this.#pokeHandler.handlePokePart(
      pokeMessage[1]
    );
    if (lastMutationIDChangeForSelf !== void 0) {
      this.#lastMutationIDReceived = lastMutationIDChangeForSelf;
    }
  }
  #handlePokeEnd(_lc, pokeMessage) {
    this.#abortPingTimeout();
    this.#pokeHandler.handlePokeEnd(pokeMessage[1]);
  }
  #onPokeError() {
    const lc = this.#lc;
    lc.info?.(
      "poke error, disconnecting?",
      this.#connectionState !== Disconnected
    );
    if (this.#connectionState !== Disconnected) {
      this.#disconnect(lc, {
        client: "UnexpectedBaseCookie"
      });
    }
  }
  #handlePullResponse(lc, pullResponseMessage) {
    this.#abortPingTimeout();
    const body = pullResponseMessage[1];
    lc = lc.withContext("requestID", body.requestID);
    lc.debug?.("Handling pull response", body);
    const resolver9 = this.#pendingPullsByRequestID.get(body.requestID);
    if (!resolver9) {
      lc.debug?.("No resolver found");
      return;
    }
    resolver9.resolve(pullResponseMessage[1]);
  }
  async #pusher(req, requestID) {
    assert(req.pushVersion === 1);
    await this.#connectResolver.promise;
    const lc = this.#lc.withContext("requestID", requestID);
    lc.debug?.(`pushing ${req.mutations.length} mutations`);
    const socket = this.#socket;
    assert(socket);
    const isMutationRecoveryPush = req.clientGroupID !== await this.clientGroupID;
    const start = isMutationRecoveryPush ? 0 : req.mutations.findIndex(
      (m) => m.clientID === this.#lastMutationIDSent.clientID && m.id === this.#lastMutationIDSent.id
    ) + 1;
    lc.debug?.(
      isMutationRecoveryPush ? "pushing for recovery" : "pushing",
      req.mutations.length - start,
      "mutations of",
      req.mutations.length,
      "mutations."
    );
    const now = Date.now();
    for (let i = start; i < req.mutations.length; i++) {
      const m = req.mutations[i];
      const timestamp = now - Math.round(performance.now() - m.timestamp);
      const zeroM = m.name === CRUD_MUTATION_NAME ? {
        type: CRUD,
        timestamp,
        id: m.id,
        clientID: m.clientID,
        name: m.name,
        args: [mapCRUD(m.args, this.#clientToServer)]
      } : {
        type: Custom,
        timestamp,
        id: m.id,
        clientID: m.clientID,
        name: m.name,
        args: [m.args]
      };
      const msg = [
        "push",
        {
          timestamp: now,
          clientGroupID: req.clientGroupID,
          mutations: [zeroM],
          pushVersion: req.pushVersion,
          requestID
        }
      ];
      send(socket, msg);
      if (!isMutationRecoveryPush) {
        this.#lastMutationIDSent = { clientID: m.clientID, id: m.id };
      }
    }
    return {
      httpRequestInfo: {
        errorMessage: "",
        httpStatusCode: 200
      }
    };
  }
  async #updateAuthToken(lc, error) {
    const { auth: authOption } = this.#options;
    const auth = await (typeof authOption === "function" ? authOption(error) : authOption);
    if (auth) {
      lc.debug?.("Got auth token");
      this.#rep.auth = auth;
    }
  }
  async #runLoop() {
    this.#lc.info?.(`Starting Zero version: ${this.version}`);
    if (this.#server === null) {
      this.#lc.info?.("No socket origin provided, not starting connect loop.");
      return;
    }
    let runLoopCounter = 0;
    const bareLogContext = this.#lc;
    const getLogContext = () => {
      let lc = bareLogContext;
      if (this.#socket) {
        lc = addWebSocketIDFromSocketToLogContext(this.#socket, lc);
      }
      return lc.withContext("runLoopCounter", runLoopCounter);
    };
    await this.#updateAuthToken(bareLogContext);
    let needsReauth = false;
    let gotError = false;
    let backoffMs = RUN_LOOP_INTERVAL_MS;
    let additionalConnectParams;
    while (!this.closed) {
      runLoopCounter++;
      let lc = getLogContext();
      backoffMs = RUN_LOOP_INTERVAL_MS;
      try {
        switch (this.#connectionState) {
          case Disconnected: {
            if (this.#visibilityWatcher.visibilityState === "hidden") {
              this.#metrics.setDisconnectedWaitingForVisible();
              this.#totalToConnectStart = void 0;
            }
            await this.#visibilityWatcher.waitForVisible();
            if (needsReauth) {
              await this.#updateAuthToken(lc, "invalid-token");
            }
            if (reloadScheduled()) {
              break;
            }
            await this.#connect(lc, additionalConnectParams);
            additionalConnectParams = void 0;
            if (this.closed) {
              break;
            }
            assert(this.#socket);
            lc = getLogContext();
            lc.debug?.("Connected successfully");
            gotError = false;
            needsReauth = false;
            this.#setOnline(true);
            break;
          }
          case Connecting:
            lc.error?.("unreachable");
            gotError = true;
            break;
          case Connected: {
            const controller = new AbortController();
            this.#abortPingTimeout = () => controller.abort();
            const [pingTimeoutPromise, pingTimeoutAborted] = sleepWithAbort(
              PING_INTERVAL_MS,
              controller.signal
            );
            this.#rejectMessageError = resolver8();
            const PING = 0;
            const HIDDEN = 2;
            const raceResult = await promiseRace([
              pingTimeoutPromise,
              pingTimeoutAborted,
              this.#visibilityWatcher.waitForHidden(),
              this.#connectionStateChangeResolver.promise,
              this.#rejectMessageError.promise
            ]);
            if (this.closed) {
              this.#rejectMessageError = void 0;
              break;
            }
            switch (raceResult) {
              case PING: {
                const pingResult = await this.#ping(
                  lc,
                  this.#rejectMessageError.promise
                );
                if (pingResult === TimedOut) {
                  gotError = true;
                }
                break;
              }
              case HIDDEN:
                this.#disconnect(lc, {
                  client: "Hidden"
                });
                this.#setOnline(false);
                break;
            }
            this.#rejectMessageError = void 0;
          }
        }
      } catch (ex) {
        if (this.#connectionState !== Connected) {
          lc.error?.("Failed to connect", ex, {
            lmid: this.#lastMutationIDReceived,
            baseCookie: this.#connectCookie
          });
        }
        lc.debug?.(
          "Got an exception in the run loop",
          "state:",
          this.#connectionState,
          "exception:",
          ex
        );
        if (isAuthError(ex)) {
          if (!needsReauth) {
            needsReauth = true;
            continue;
          }
          needsReauth = true;
        }
        if (isServerError(ex) || ex instanceof TimedOutError || ex instanceof CloseError) {
          gotError = true;
        }
        const backoffError = isBackoffError(ex);
        if (backoffError) {
          if (backoffError.minBackoffMs !== void 0) {
            backoffMs = Math.max(backoffMs, backoffError.minBackoffMs);
          }
          if (backoffError.maxBackoffMs !== void 0) {
            backoffMs = Math.min(backoffMs, backoffError.maxBackoffMs);
          }
          additionalConnectParams = backoffError.reconnectParams;
        }
      }
      if (gotError) {
        this.#setOnline(false);
        lc.debug?.(
          "Sleeping",
          backoffMs,
          "ms before reconnecting due to error, state:",
          this.#connectionState
        );
        await sleep(backoffMs);
      }
    }
  }
  async #puller(req, requestID) {
    assert(req.pullVersion === 1);
    const lc = this.#lc.withContext("requestID", requestID);
    lc.debug?.("Pull", req);
    if (req.clientGroupID === await this.clientGroupID) {
      return {
        httpRequestInfo: {
          errorMessage: "",
          httpStatusCode: 200
        }
      };
    }
    await this.#connectResolver.promise;
    const socket = this.#socket;
    assert(socket);
    lc.debug?.("Pull is for mutation recovery");
    const cookie = parse(req.cookie, nullableVersionSchema);
    const pullRequestMessage = [
      "pull",
      {
        clientGroupID: req.clientGroupID,
        cookie,
        requestID
      }
    ];
    send(socket, pullRequestMessage);
    const pullResponseResolver = resolver8();
    this.#pendingPullsByRequestID.set(requestID, pullResponseResolver);
    try {
      const TIMEOUT = 0;
      const RESPONSE = 1;
      const raceResult = await promiseRace([
        sleep(PULL_TIMEOUT_MS),
        pullResponseResolver.promise
      ]);
      switch (raceResult) {
        case TIMEOUT:
          lc.debug?.("Mutation recovery pull timed out");
          throw new Error("Pull timed out");
        case RESPONSE: {
          lc.debug?.("Returning mutation recovery pull response");
          const response = await pullResponseResolver.promise;
          return {
            response: {
              cookie: response.cookie,
              lastMutationIDChanges: response.lastMutationIDChanges,
              patch: []
            },
            httpRequestInfo: {
              errorMessage: "",
              httpStatusCode: 200
            }
          };
        }
        default:
          assert(false, "unreachable");
      }
    } finally {
      pullResponseResolver.reject("timed out");
      this.#pendingPullsByRequestID.delete(requestID);
    }
  }
  #setOnline(online) {
    if (this.#online === online) {
      return;
    }
    this.#online = online;
    this.#onOnlineChange?.(online);
  }
  /**
   * A rough heuristic for whether the client is currently online and
   * authenticated.
   */
  get online() {
    return this.#online;
  }
  /**
   * Starts a ping and waits for a pong.
   *
   * If it takes too long to get a pong we disconnect and this returns
   * {@code PingResult.TimedOut}.
   */
  async #ping(l, messageErrorRejectionPromise) {
    l.debug?.("pinging");
    const { promise, resolve } = resolver8();
    this.#onPong = resolve;
    const pingMessage = ["ping", {}];
    const t0 = performance.now();
    assert(this.#socket);
    send(this.#socket, pingMessage);
    const connected = await promiseRace([
      promise,
      sleep(PING_TIMEOUT_MS),
      messageErrorRejectionPromise
    ]) === 0;
    const delta = performance.now() - t0;
    if (!connected) {
      l.info?.("ping failed in", delta, "ms - disconnecting");
      this.#disconnect(l, {
        client: "PingTimeout"
      });
      return TimedOut;
    }
    l.debug?.("ping succeeded in", delta, "ms");
    return Success;
  }
  // Sends a set of metrics to the server. Throws unless the server
  // returns 200.
  // TODO: Reenable metrics reporting
  async #reportMetrics(_allSeries) {
  }
  #checkConnectivity(reason) {
    void this.#checkConnectivityAsync(reason);
  }
  #checkConnectivityAsync(_reason) {
  }
  #registerQueries(schema) {
    const rv = {};
    const context = this.#zeroContext;
    for (const name of Object.keys(schema.tables)) {
      rv[name] = newQuery(context, schema, name);
    }
    return rv;
  }
  /**
   * `inspect` returns an object that can be used to inspect the state of the
   * queries a Zero instance uses. It is intended for debugging purposes.
   */
  async inspect() {
    BUNDLE_SIZE: {
      const m = await import("./inspector-C2BDA4K7.js");
      return m.newInspector(this.#rep, this.#schema, () => this.#socket);
    }
  }
};
async function createSocket(rep, queryManager, deleteClientsManager, socketOrigin, baseCookie, clientID, clientGroupID, clientSchema, userID, auth, lmid, wsid, debugPerf, lc, maxHeaderLength = 1024 * 8, additionalConnectParams) {
  const url = new URL(
    appendPath(socketOrigin, `/sync/v${PROTOCOL_VERSION}/connect`)
  );
  const { searchParams } = url;
  searchParams.set("clientID", clientID);
  searchParams.set("clientGroupID", clientGroupID);
  searchParams.set("userID", userID);
  searchParams.set("baseCookie", baseCookie === null ? "" : String(baseCookie));
  searchParams.set("ts", String(performance.now()));
  searchParams.set("lmid", String(lmid));
  searchParams.set("wsid", wsid);
  if (debugPerf) {
    searchParams.set("debugPerf", true.toString());
  }
  if (additionalConnectParams) {
    for (const k in additionalConnectParams) {
      if (searchParams.has(k)) {
        lc.warn?.(`skipping conflicting parameter ${k}`);
      } else {
        searchParams.set(k, additionalConnectParams[k]);
      }
    }
  }
  lc.info?.("Connecting to", url.toString());
  const WS = mustGetBrowserGlobal("WebSocket");
  const queriesPatchP = rep.query((tx) => queryManager.getQueriesPatch(tx));
  let deletedClients = await deleteClientsManager.getDeletedClients();
  let queriesPatch = await queriesPatchP;
  let secProtocol = encodeSecProtocols(
    [
      "initConnection",
      {
        desiredQueriesPatch: [...queriesPatch.values()],
        deleted: skipEmptyDeletedClients(deletedClients),
        // The clientSchema only needs to be sent for the very first request.
        // Henceforth it is stored with the CVR and verified automatically.
        ...baseCookie === null ? { clientSchema } : {}
      }
    ],
    auth
  );
  if (secProtocol.length > maxHeaderLength) {
    secProtocol = encodeSecProtocols(void 0, auth);
    queriesPatch = void 0;
  } else {
    deletedClients = void 0;
  }
  return [
    new WS(
      // toString() required for RN URL polyfill.
      url.toString(),
      secProtocol
    ),
    queriesPatch,
    skipEmptyDeletedClients(deletedClients)
  ];
}
function skipEmptyArray(arr) {
  return arr && arr.length > 0 ? arr : void 0;
}
function skipEmptyDeletedClients(deletedClients) {
  if (!deletedClients) {
    return void 0;
  }
  const { clientIDs, clientGroupIDs } = deletedClients;
  if ((!clientIDs || clientIDs.length === 0) && (!clientGroupIDs || clientGroupIDs.length === 0)) {
    return void 0;
  }
  const data = {};
  data.clientIDs = skipEmptyArray(clientIDs);
  data.clientGroupIDs = skipEmptyArray(clientGroupIDs);
  return data;
}
function addWebSocketIDFromSocketToLogContext({ url }, lc) {
  const wsid = new URL(url).searchParams.get("wsid") ?? nanoid();
  return addWebSocketIDToLogContext(wsid, lc);
}
function addWebSocketIDToLogContext(wsid, lc) {
  return lc.withContext("wsid", wsid);
}
function promiseRace(ps) {
  return Promise.race(ps.map((p, i) => p.then(() => i)));
}
var TimedOutError = class extends Error {
  constructor(m) {
    super(`${m} timed out`);
  }
};
var CloseError = class extends Error {
};

export {
  getDefaultPuller,
  IDBNotFoundError,
  TransactionClosedError,
  makeIDBName,
  dropDatabase,
  dropAllDatabases,
  relationships,
  table,
  string3 as string,
  number2 as number,
  boolean,
  json,
  enumeration,
  createSchema,
  ANYONE_CAN,
  ANYONE_CAN_DO_ANYTHING,
  NOBODY_CAN,
  definePermissions,
  escapeLike,
  Zero
};
//# sourceMappingURL=chunk-ITPVSMKZ.js.map
